/**
 * The single agreement between the API and the web app.
 *
 * Both import from here, so a field that changes shape breaks the build on
 * both sides at once instead of failing at runtime in a form nobody
 * touched for a week.
 */

// ---------------------------------------------------------------------
// Locale and theme
// ---------------------------------------------------------------------

/** The nine languages the product ships in. Spanish is the default, everywhere. */
export const SUPPORTED_LOCALES = ['es', 'en', 'fr', 'it', 'de', 'pt', 'zh-CN', 'ko', 'ja'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: SupportedLocale = 'es';

export const THEMES = ['CLASSIC', 'DARK'] as const;
export type Theme = (typeof THEMES)[number];
export const DEFAULT_THEME: Theme = 'CLASSIC';

// ---------------------------------------------------------------------
// Sex
// ---------------------------------------------------------------------

/**
 * Male or female, as a sporting category — not as an identity.
 *
 * It is here because the categories a community runs are a level crossed
 * with a sex ("A femenino" and "A masculino" are two different draws, with
 * two different fees), so a player with no sex on file cannot be placed in
 * any of them. Two values because that is what the draws are; if a
 * federation ever adds a mixed draw, that is a property of the draw, not a
 * third value here.
 */
/**
 * CÓMO SE BUSCA A UNA PERSONA POR SU NOMBRE.
 *
 * LA REGLA: se busca por lo que alguien ESCRIBE, no por lo que está guardado.
 * Nadie escribe «José Andrés Rodríguez Vargas» con las tildes puestas cuando
 * está apurado armando un torneo; escribe «jose rodriguez» y espera
 * encontrarlo. Por eso las dos puntas —lo guardado y lo tecleado— pasan por
 * esta misma función.
 *
 * VIVE EN EL CONTRATO porque la busca el servidor Y la pantalla. El cuadro
 * tenía su propia copia, parecida pero no igual —no juntaba los espacios de
 * más— y dos normalizaciones distintas para la misma pregunta es cómo un
 * nombre se encuentra en una pantalla y no en la otra.
 */
export function normalizeForSearch(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * LO MISMO, PERO SIN NADA QUE NO SEA LETRA O NÚMERO: ni espacios, ni guiones,
 * ni apóstrofos, ni puntos.
 *
 * PARA COMPARAR EN MEMORIA, no para guardar. Es más agresiva a propósito:
 * quien busca en un cuadro escribe «jeanpierre» o «jean pierre» o «Jean-Pierre»
 * y en los tres casos está buscando a la misma persona. Sacando todo lo que no
 * es letra, los tres dan el mismo texto y la comparación es una sola.
 *
 * NO SE USA PARA EL ÍNDICE GUARDADO, y esa diferencia es deliberada. La
 * columna `searchIndex` se llenó con `normalizeForSearch` y la compara la base
 * de datos; cambiar esa regla dejaría a todas las filas viejas escritas con la
 * anterior, y un apellido con apóstrofo dejaría de aparecer sin que nada
 * fallara. Si algún día se quiere esto también allá, hay que REESCRIBIR el
 * índice de todos los perfiles, y eso es una migración, no un cambio de línea.
 *
 * Se construye SOBRE la otra y no aparte: así las tildes y las mayúsculas se
 * resuelven en un solo lugar, y esta solo agrega lo suyo.
 */
export function searchKey(text: string): string {
  return normalizeForSearch(text).replace(/[^\p{L}\p{N}]/gu, '');
}

export const SEXES = ['MALE', 'FEMALE'] as const;
export type Sex = (typeof SEXES)[number];

// ---------------------------------------------------------------------
// Identity document
// ---------------------------------------------------------------------

/**
 * A Costa Rican cédula, a DIMEX for a foreign resident, or a passport.
 *
 * The TYPE is stored beside the number because the same string means
 * different things depending on it, and because a foreign player must be
 * able to register without owning a cédula — the requirement was
 * explicitly "puede ser extranjero".
 */
export const ID_DOCUMENT_TYPES = ['CEDULA', 'DIMEX', 'PASSPORT'] as const;
export type IdDocumentType = (typeof ID_DOCUMENT_TYPES)[number];

// ---------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------

/**
 * What an account can do platform-wide.
 *
 * Deliberately NOT a permission list. Two roles is what the product actually
 * has today — a player, and whoever at Tennis Up Club approves things — and a role
 * enum that mirrors reality is easier to reason about than a permission
 * system nobody has needed yet. Membership of a community is a different
 * question, answered by a different table.
 */
export const SYSTEM_ROLES = ['PLAYER', 'PLATFORM_ADMIN'] as const;
export type SystemRole = (typeof SYSTEM_ROLES)[number];

/**
 * EN QUE ESTADO ESTA UNA CUENTA.
 *
 * Vive acá porque la pantalla de administración lo muestra; la copia de
 * trabajo del servidor está en `persistence/ports.ts`, con el mismo juego de
 * valores. Solo ACTIVE puede entrar: lo comprueba la guardia en cada
 * petición, no solo al iniciar sesión.
 */
export const USER_STATUSES = ['ACTIVE', 'SUSPENDED', 'DEACTIVATED'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

// ---------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------

export interface RegisterRequest {
  email: string;
  password: string;

  /** How this player appears to everyone else. */
  displayName: string;
  /** Their real, full legal name. Private — never shown to other players. */
  fullName: string;

  idDocumentType: IdDocumentType;
  /** Stored as typed, only trimmed: a cédula, a DIMEX and a passport are formatted differently, and normalising to digits destroys what we cannot rebuild. */
  idDocumentNumber: string;

  /** Date-only, `YYYY-MM-DD`. Never a timestamp: a birth date must not shift a day because of a timezone. */
  dateOfBirth: string;

  /** ISO 3166-1 alpha-2, chosen BY the player — never inferred from their IP, phone prefix or browser language. */
  nationalityCode: string;

  /** Required when registering: the categories a player can be placed in depend on it. See SEXES. */
  sex: Sex;

  /**
   * Costa Rican territorial codes. All three are REQUIRED.
   *
   * They used to be nullable ("all three or none"). They are not any more:
   * this product exists so that someone near you can challenge you, and a
   * player with no district cannot be found that way — an optional field
   * that the core feature depends on is a field that is missing exactly when
   * it matters. The API rejects a registration without them.
   *
   * `addressLine` below stays optional: it is the human detail under the
   * district, not the location itself.
   */
  provinceCode: string;
  cantonCode: string;
  districtCode: string;
  /** Free-text detail below the district — "200m sur de la iglesia". */
  addressLine: string | null;

  /** E.164, e.g. `+50688887777`. Required: it is how opponents actually reach each other. */
  whatsappPhone: string;

  /** Whether this player wants to receive challenges. Their own switch, on from the start. */
  availableForChallenges: boolean;

  /**
   * Whether notifications also arrive by e-mail. On from the start.
   *
   * Asked here and not only in «Más» because it used to be forced to `true`
   * in `auth.service` and someone would discover it existed months later,
   * by getting mail they never agreed to. Asking does not change the
   * default — it changes whether the person KNOWS they have it on.
   *
   * It does NOT silence the bell: the notification is stored and shown
   * inside the app either way. See `PlayerProfile.emailNotifications`.
   */
  emailNotifications: boolean;

  /**
   * El nivel que la persona dice tener. OBLIGATORIO.
   *
   * Fue opcional —«prefiero no decirlo» era una respuesta válida— con este
   * argumento, que sigue siendo cierto y se deja escrito: quien empieza no
   * sabe en qué nivel está, y obligarlo a elegir mete un dato inventado que
   * después nadie revisa.
   *
   * Javier lo pidió obligatorio a sabiendas. La razón del otro lado: sin
   * nivel, una cuenta no aparece en ninguna búsqueda por categoría ni puede
   * entrar a un club sin que alguien se lo asigne a mano, y un campo del que
   * depende encontrar gente es un campo que vacío no sirve.
   *
   * No es la categoría de ningún club: ver `PlayerProfile.declaredLevel`.
   */
  declaredLevel: CategoryLevel;

  /**
   * LA UBICACIÓN DEL APARATO, si la persona la dio. Opcional de verdad.
   *
   * Acá y no solo en `PUT /profile/location` porque quien se acaba de
   * registrar y no la pone NO APARECE en la búsqueda de retos —el producto
   * entero se apoya en la distancia— y no tiene forma de saberlo. Pedirla en
   * el momento en que alguien está creando su cuenta es la única vez que
   * está mirando esta pregunta.
   *
   * SE PIDE DE UN BOTÓN, nunca sola: un cartel del navegador que aparece sin
   * que nadie haya pedido nada se cierra sin leerlo, y después ya no se
   * puede volver a preguntar.
   *
   * Se recorta a tres decimales en el servidor, igual que por el otro
   * camino: lo que se guarda es el barrio, no la puerta de la casa.
   */
  latitude?: number | null;
  longitude?: number | null;

  locale: SupportedLocale;
  theme: Theme;
}

/**
 * `PUT /profile/location` — guardar la ubicación tomada del aparato.
 *
 * Va por su propio camino y no dentro de `UpdateProfileRequest` porque no es
 * algo que la persona escriba: se lo pide al navegador de un botón. Metido en
 * el formulario, guardar el perfil sin tocar el botón tendría que mandar
 * coordenadas viejas —o borrarlas—, y las dos cosas están mal.
 */
export interface SetLocationRequest {
  latitude: number;
  longitude: number;
}

/**
 * Registration returns a session: `AuthResponse`, exactly what login returns.
 *
 * There is no email confirmation step any more. A separate `RegisterResponse`
 * would only describe the same account twice, so registering and signing in
 * answer with the same shape and the web app has one code path for "you are
 * in" instead of two.
 */

// ---------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  locale: SupportedLocale;
  theme: Theme;
  /** What this account is allowed to do platform-wide. See SYSTEM_ROLES. */
  systemRole: SystemRole;
  /**
   * Their profile photo, as a path relative to the API base — or `null` if
   * they have not set one. See `ASSET_PATH_PREFIX` for why it is a path and
   * not an absolute URL.
   */
  avatarPath: string | null;
  /**
   * Masculino o femenino, o `null` si la cuenta es anterior al campo.
   *
   * Viaja en la sesión —y no solo en el perfil completo— porque decide QUÉ
   * CATEGORÍAS se le pueden ofrecer a esta persona, y eso hace falta en
   * pantallas que no cargan el perfil entero: pedir entrar a un club, sin ir
   * más lejos. Pedir el perfil completo para saber una letra sería un viaje
   * de más en cada una.
   */
  sex: Sex | null;
  /**
   * EL CLUB DE ESTA PERSONA: el que abre la pestaña de Torneos.
   *
   * Viaja en la sesión —y no solo en el perfil completo— porque lo usa la
   * barra de abajo, que está en TODAS las pantallas. Pedir el perfil entero
   * para pintar una pestaña sería un viaje de más en cada una.
   *
   * `null` mientras la persona no haya elegido, o si la sacaron de ese club.
   */
  defaultCommunityId: string | null;
  /**
   * El nivel que esta persona DICE tener, o `null`.
   *
   * Viaja en la sesión por lo mismo que `sex`: lo usa la pantalla de pedir
   * entrar a un club, para llegar con la categoría preseleccionada. Pedir el
   * perfil entero para saber una letra sería un viaje de más.
   *
   * NO ES SU CATEGORÍA EN NINGÚN CLUB. Ver `PlayerProfile.declaredLevel`.
   */
  declaredLevel: CategoryLevel | null;
}

export interface AuthResponse {
  user: AuthUser;
  accessToken: string;
  accessTokenExpiresAt: string;
  /**
   * LA LLAVE PARA RENOVAR, cuando el servidor la emite.
   *
   * OPCIONAL A PROPÓSITO, y no por pereza: entre que se despliega el servidor
   * nuevo y que cada teléfono recarga la aplicación pasan horas, y durante
   * esas horas hay webs viejas hablando con el servidor nuevo y al revés. Un
   * campo obligatorio rompería a alguien en ese rato. La web lo usa si viene y
   * sigue andando si no.
   *
   * SE GUARDA COMO EL DE ACCESO Y NO EN UNA COOKIE `httpOnly`, que sería lo
   * ortodoxo: la aplicación es un SPA que habla con la API en otro dominio, y
   * el resto de la sesión ya vive en el almacenamiento del navegador. Ponerlo
   * en una cookie lo protegería de un XSS que, con el de acceso donde está,
   * ya tendría la sesión igual. La rotación con detección de robo es lo que
   * de verdad acota el daño acá; ver `domain/refresh.ts` en el servidor.
   */
  refreshToken?: string;
  /** Cuándo vence la llave de renovación, en ISO. */
  refreshTokenExpiresAt?: string;
}

/**
 * `POST /auth/refresh` — cambiar una llave por una sesión nueva.
 *
 * NO LLEVA EL TOKEN DE ACCESO NI HACE FALTA ESTAR AUTENTICADO: se llama
 * justamente cuando el de acceso ya venció. La llave es la credencial.
 */
export interface RefreshRequest {
  refreshToken: string;
}

/** Always answers 202, whether or not the address exists — see the service note. */
export interface RequestPasswordResetRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

// ---------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------

/**
 * Every failure carries a stable `code`. The web app switches on the code
 * and shows its own translated message — it never prints the server's
 * English `message`, which exists for logs and for developers.
 */
export interface ApiErrorBody {
  statusCode: number;
  code: string;
  message: string;
  /** Field-level problems, keyed by the field name the form uses. */
  fieldErrors?: Record<string, string>;
}

export const AUTH_ERROR_CODES = [
  'EMAIL_ALREADY_REGISTERED',
  'INVALID_CREDENTIALS',
  /** The account exists and the password is right, but the account is suspended or deactivated. */
  'ACCOUNT_NOT_ACTIVE',
  /**
   * The server has no email provider configured, so password reset cannot
   * work. A configuration problem, not a fact about any user — see the note in
   * AuthService.requestPasswordReset for why this one IS safe to report.
   */
  'EMAIL_NOT_CONFIGURED',
  'RESET_TOKEN_INVALID',
  'RESET_TOKEN_EXPIRED',
  /**
   * LA LLAVE DE RENOVACIÓN NO SIRVE: no existe, venció, se revocó, o se
   * volvió a presentar una ya canjeada.
   *
   * ES UN SOLO CÓDIGO PARA LOS CUATRO MOTIVOS, y eso es deliberado. La web
   * hace lo mismo en todos los casos —mandar a la pantalla de entrar— y
   * decirle a quien presenta una llave ajena si «venció» o si «ya se usó» le
   * da información sobre una sesión que no es suya. El servidor sí distingue
   * los cuatro adentro, porque uno de ellos mata la cadena entera; ver
   * `domain/refresh.ts`.
   */
  'REFRESH_TOKEN_INVALID',
  'PASSWORD_TOO_WEAK',
  'VALIDATION_ERROR',
  'TOO_MANY_REQUESTS',
] as const;
export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[number];

// ---------------------------------------------------------------------
// Images (community logos, profile photos)
// ---------------------------------------------------------------------

/**
 * What the server accepts as an upload, sniffed from the file's own bytes —
 * never from its extension or from the `Content-Type` the browser claims.
 *
 * HEIC is not here on purpose. iPhones shoot HEIC, but Safari converts it to
 * JPEG when the file goes through an `<input type="file">`, and the library
 * that resizes on the server only reads HEIC when it was built with an extra
 * codec that the prebuilt binaries usually lack. Advertising a format we
 * might not be able to open is worse than not advertising it.
 */
export const ACCEPTED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type AcceptedImageMimeType = (typeof ACCEPTED_IMAGE_MIME_TYPES)[number];

/**
 * The biggest file the server will even read (12 MB) — a modern phone photo
 * fits comfortably. This is the *upload* limit, not the stored size.
 */
export const IMAGE_MAX_UPLOAD_BYTES = 12 * 1024 * 1024;

/**
 * What actually gets stored: under 1 MB, always. The server resizes and
 * re-encodes until it fits, so this is a guarantee about the file on disk,
 * not a rule the uploader has to satisfy.
 */
export const IMAGE_MAX_STORED_BYTES = 1024 * 1024;

/**
 * Stored images are served from here, and every `*Path` in this contract is
 * relative to the API base URL (the one that already ends in `/api`).
 *
 * A path and not an absolute URL because the API is reached by a different
 * host in dev (localhost), in the phone build (a LAN address or the public
 * domain) and in production. An absolute URL baked into a database row is
 * wrong on two of those three, and it is wrong *silently* — the image just
 * does not load. The client knows its own API base; it is the only one that
 * can build the full URL correctly.
 */
export const ASSET_PATH_PREFIX = '/uploads';

// ---------------------------------------------------------------------
// Communities
// ---------------------------------------------------------------------

/**
 * A community is created by a player and starts PENDING: it exists, its
 * creator can see it, and nobody else can. Tennis Up Club approves it (or rejects
 * it with a reason) and only then does it show up in the public list.
 *
 * REJECTED is kept rather than deleted so the creator can be told why, once,
 * instead of watching their club silently vanish.
 */
export const COMMUNITY_STATUSES = ['PENDING', 'APPROVED', 'REJECTED'] as const;
export type CommunityStatus = (typeof COMMUNITY_STATUSES)[number];

export const COMMUNITY_LIMITS = {
  nameMin: 3,
  nameMax: 80,
  descriptionMin: 10,
  descriptionMax: 1000,
  locationMax: 160,
  whatsappUrlMax: 300,
} as const;

export interface Community {
  id: string;
  name: string;
  /** Derived from the name. Stable once created, even if the name changes. */
  slug: string;
  description: string;
  /** An invite link to the club's WhatsApp group, or `null`. */
  whatsappGroupUrl: string | null;
  /** Free text on purpose: "Cancha del Liceo, Grecia" is a location and no map has it. */
  locationText: string;
  /** Relative to the API base — see ASSET_PATH_PREFIX. `null` while it has no logo. */
  logoPath: string | null;
  /**
   * EL REGLAMENTO DE LA CASA. El que arranca cada torneo nuevo.
   *
   * Vale para todos los torneos que no escribieron uno propio, y esos son
   * casi todos: lo normal es que el club tenga un reglamento y ya. El torneo
   * que se aparta lo hace porque alguien lo editó a propósito —ver
   * `Tournament.rules`—, y corregir este sigue arreglando de una a todos los
   * que no se apartaron.
   */
  tournamentRules: string | null;
  /**
   * LOS PARÁMETROS DE RETOS DEL CLUB.
   *
   * LOS VE CUALQUIERA QUE VEA EL CLUB, no solo quien administra. Son las
   * reglas con las que se juega: quien está por retar a alguien tiene que
   * poder saber cuántos retos le quedan en la tanda y hasta dónde puede
   * retar, y enterarse de eso solo cuando el botón le dice que no es la peor
   * forma de aprender una regla.
   */
  challengeSettings: ChallengeSettings;
  /** Las categorías que corre, ordenadas por nivel y después por sexo. */
  categories: CommunityCategory[];
  /** Cuántas personas tiene. El listado completo se pide aparte. */
  memberCount: number;
  /** El rol de quien pregunta dentro de esta comunidad, o `null` si no es miembro. */
  myRole: CommunityMemberRole | null;
  /**
   * En qué situación está quien pregunta: `null` si nunca pidió entrar,
   * `'PENDING'` si su solicitud está esperando, `'ACTIVE'` si ya es miembro.
   *
   * Va aparte de `myRole` porque son dos preguntas distintas: `myRole` dice
   * QUÉ PUEDE HACER, y esto dice SI YA ESTÁ ADENTRO. Sin separarlas, una
   * solicitud pendiente tendría que fingir un rol que todavía no tiene.
   */
  myMembershipStatus: CommunityMemberStatus | null;
  /**
   * MI CATEGORÍA DENTRO DE ESTE CLUB. `null` si no soy miembro activo.
   *
   * Estaba —y sigue estando— en la lista de jugadores, pero ahí hay que
   * buscarse a uno mismo entre doscientos para averiguar en qué categoría
   * juega. Es el dato que decide en qué torneos se puede inscribir y contra
   * quién compite en el ranking: tiene que estar dicho, no deducido.
   *
   * Sale de la membresía y NO del perfil: la misma persona puede ser B en un
   * club y A en otro. Lo que dice en el perfil es otra cosa —el nivel que
   * declara— y no manda acá.
   */
  myLevel: CategoryLevel | null;
  /**
   * TODAS LAS TABLAS EN LAS QUE COMPITO EN ESTE CLUB, o pedí competir.
   *
   * ES LA LISTA Y `myLevel` ES SU RESUMEN, y por eso conviven en vez de que
   * una reemplace a la otra. `myLevel` contesta «¿en qué categoría estoy?»
   * con un nivel suelto —lo que necesitan los torneos, que todavía inscriben
   * por nivel— y no puede contestar «estoy en B femenino Y en C masculino».
   * Cuando hay varias, `myLevel` es la MÁS FUERTE: es la que manda para
   * inscribirse, porque dejar que alguien entre a un torneo por su tabla más
   * floja sería la forma de bajar de categoría sin pedirlo.
   *
   * VIENEN LAS QUE ESPERAN RESPUESTA TAMBIÉN, marcadas con `status`. La
   * pantalla tiene que poder mostrar «C masculino · esperando» al lado de la
   * que ya juega: sin eso, quien pidió una segunda no ve nada y la pide otra
   * vez.
   *
   * De la más fuerte a la más floja, que es como se leen.
   */
  myRankings: MyRanking[];
  /**
   * La categoría que YO pedí en este club, si estoy esperando respuesta.
   *
   * Va aparte de `myLevel` porque son dos cosas distintas: una es lo que soy
   * hoy y la otra lo que pedí ser. Mostrar solo la pedida haría creer que ya
   * cambió; mostrar solo la actual escondería que hay algo esperando y la
   * persona lo pediría otra vez.
   */
  myRequestedLevel: CategoryLevel | null;
  /** Cuántas solicitudes esperan respuesta. Solo se llena para quien administra. */
  pendingRequests: number;
  /**
   * Cuántos pedidos de CAMBIO DE CATEGORÍA esperan. Solo para quien
   * administra, igual que `pendingRequests`: cuánta gente quiere cambiarse de
   * categoría en un club ajeno no es asunto de nadie más.
   */
  pendingCategoryRequests: number;
  /**
   * ¿Las solicitudes de ingreso entran aprobadas de una?
   *
   * Cambiarlo no toca las que ya están esperando: aplicarlo hacia atrás
   * dejaría entrar de golpe a gente que alguien decidió no aprobar todavía.
   */
  autoApproveMembers: boolean;
  /**
   * Y CUANDO PIDE TRAER PUNTOS DE OTRO RANKING, ¿también entra solo?
   *
   * Cuelga de `autoApproveMembers`: sin auto-aceptar, esto no significa nada
   * —no se acepta solo a nadie, con puntos o sin ellos—. Por eso el
   * interruptor de la pantalla aparece ADENTRO del otro.
   *
   * ENTRAR NO ES RECIBIR LOS PUNTOS. Prendido, la persona queda adentro del
   * club al toque, pero los puntos que pidió siguen esperando tu visto bueno:
   * son dos decisiones distintas y se toman por separado. Si fueran la misma,
   * cualquiera que se anota escribe «3000» y entra al ranking con eso.
   *
   * Arranca en `false`: es el comportamiento que ya había, y prenderlo es una
   * decisión del club.
   */
  autoApproveWithPoints: boolean;
  /**
   * ¿Los jugadores pueden escribirse por WhatsApp desde el cuadro?
   *
   * Encendido de fábrica: es como se acuerda un partido en un club. Apagarlo
   * no esconde un botón, hace que el teléfono NO VIAJE. Ver `MatchContact`.
   */
  allowWhatsappContact: boolean;
  /**
   * ¿Puede cada jugador anotar el marcador del partido que jugó?
   *
   * Apagado de fábrica: anotar es escribir en el ranking del club, y quién
   * puede hacerlo es una decisión de confianza que se toma a propósito.
   * Quien organiza anota siempre, esté como esté esto.
   */
  allowPlayerScores: boolean;
  /**
   * ¿El club felicita a su gente el día del cumpleaños?
   *
   * Apagado de fábrica: un saludo a nombre de un club que no decidió mandarlo
   * es el club hablando sin haber hablado.
   */
  celebrateBirthdays: boolean;
  status: CommunityStatus;
  /**
   * Whether this community may create tournaments. SEPARATE from `status` on
   * purpose: approving a club so it appears in the list, and trusting it to
   * run tournaments, are two different decisions and the second one does not
   * follow from the first.
   */
  canCreateTournaments: boolean;
  /** Why it was rejected, or the note left on approval. Shown to its creator. */
  reviewNote: string | null;
  createdAt: string;
  createdBy: { id: string; displayName: string };
  /** True when the person asking is the one who created it. */
  isMine: boolean;
}

/**
 * QUIÉN ADMINISTRA UN CLUB. `GET /communities/:id/admins`.
 *
 * QUÉ PROBLEMA RESUELVE. Un club aparecía como una entidad sin gente detrás:
 * quien tenía un problema —una categoría mal puesta, una inscripción que no
 * le aprobaron, una queja— no tenía a quién escribirle. Javier: «hay que
 * poner quiénes son los administradores del club que todos puedan ver y
 * contactarlos si es necesario».
 *
 * LO VE CUALQUIERA QUE PUEDA VER EL CLUB, y eso es a propósito: administrar
 * un club es un cargo, no un dato personal. Quien lo acepta responde por lo
 * que ahí pasa, y para eso hay que saber quién es.
 *
 * NO TRAE TELÉFONO NI CORREO. Se llega a la persona por su ficha, que es
 * donde vive la única regla de contacto de la aplicación; publicar acá el
 * número personal de alguien sería una segunda regla, más suelta, decidida
 * en otro lado. La ficha ya sabe a quién mostrárselo.
 */
export interface CommunityAdmin {
  userId: string;
  displayName: string;
  avatarPath: string | null;
  /**
   * `true` para quien creó el club.
   *
   * Va aparte del rol porque son dos cosas: se puede administrar sin haberlo
   * fundado, y quien lo fundó no deja de responder por él aunque otro se
   * ocupe del día a día. Además hay clubes donde el fundador todavía no es
   * miembro de su propio club, y sin esto no aparecería en ninguna lista.
   */
  isFounder: boolean;
}

/**
 * The text half of "create a community". It travels as `multipart/form-data`
 * next to the logo file, not as JSON, so these are form field names — the
 * field for the file is `logo`.
 */
// --- Categorías -------------------------------------------------------

/**
 * The playing levels, from strongest to weakest. The ORDER MATTERS: it is
 * the order they are shown in and the order a ranking sorts by, and it is
 * declared here once so that no screen re-invents it and gets it backwards.
 */
export const CATEGORY_LEVELS = ['OPEN', 'A_PLUS', 'A', 'B', 'C', 'C2'] as const;
export type CategoryLevel = (typeof CATEGORY_LEVELS)[number];

/**
 * En qué categorías se puede inscribir alguien, dada la que tiene asignada.
 *
 * LA REGLA: la suya, y como mucho UNA arriba. Nunca una abajo.
 *
 * Hacia abajo nunca, porque es lo que arruina un torneo: alguien de C
 * anotado en C2 le gana a gente que no tiene con qué, y la categoría entera
 * deja de tener sentido para los que sí son de C2. Hacia arriba se permite un
 * escalón porque medirse con mejores es cómo se progresa —y quien lo hace
 * suele estar listo para subir— pero solo uno: alguien de C metido en A no
 * juega un partido, recibe una paliza y le ocupa el lugar a otro.
 *
 * Vive en el contrato y no en el servidor porque la pantalla tiene que
 * ofrecer EXACTAMENTE lo mismo que la API acepta. Con la regla escrita dos
 * veces, el día que cambie va a cambiar en una sola.
 */
export function eligibleLevels(assigned: CategoryLevel): CategoryLevel[] {
  const index = CATEGORY_LEVELS.indexOf(assigned);
  if (index === -1) return [];
  // El arreglo va de la más fuerte a la más débil, así que «una arriba» es
  // el índice ANTERIOR. Se devuelve en ese mismo orden.
  const arriba = CATEGORY_LEVELS[index - 1];
  return arriba === undefined ? [assigned] : [arriba, assigned];
}

/**
 * QUÉ CATEGORÍAS DE CLUB PUEDEN JUGAR ESTE NIVEL. La vuelta de
 * `eligibleLevels`.
 *
 * `eligibleLevels` contesta «yo soy C, ¿qué puedo jugar?» → C y B. Esta
 * contesta «esto es un cuadro de C, ¿quiénes entran?» → las C y las C2, que
 * suben un escalón. Son la misma regla leída en las dos direcciones, y por eso
 * viven pegadas: separadas, una se actualiza y la otra queda contestando la
 * regla vieja.
 *
 * SIRVE PARA BUSCAR, no para autorizar. Quien inscribe sigue pasando por
 * `enterableCategories`, que además mira el sorteo, el tope de dos y el sexo.
 * Esta es para poder pedirle a la base «traeme a los que podrían» sin bajarse
 * la tabla entera y filtrarla en memoria.
 *
 * Devuelve el nivel pedido primero y el de abajo después, que es el orden en
 * que conviene mostrarlos: los de la categoría, y después los que suben.
 */
export function levelsThatCanPlay(level: CategoryLevel): CategoryLevel[] {
  const index = CATEGORY_LEVELS.indexOf(level);
  if (index === -1) return [];
  // El arreglo va de la más fuerte a la más débil, así que «el de abajo» —el
  // que sube un escalón para jugar acá— es el índice SIGUIENTE.
  const abajo = CATEGORY_LEVELS[index + 1];
  return abajo === undefined ? [level] : [level, abajo];
}

/** `true` si esa persona puede jugar ese nivel. Es `eligibleLevels` mirado al revés. */
export function canPlayLevel(assigned: CategoryLevel, level: CategoryLevel): boolean {
  return eligibleLevels(assigned).includes(level);
}

/**
 * `true` si `level` es MÁS DÉBIL que `assigned`. O sea: jugar para abajo.
 *
 * NUNCA SE PERMITE, y esta función existe para que esa frase esté escrita una
 * sola vez. `CATEGORY_LEVELS` va de la más fuerte a la más débil, así que
 * «más débil» es un índice MAYOR — y esa inversión es exactamente el detalle
 * que alguien va a escribir al revés si la regla se copia.
 *
 * Es aparte de `canPlayLevel` porque contestan cosas distintas: aquella dice
 * «¿está entre las que le tocan?» y esta dice «¿está por debajo?». En un
 * torneo abierto la primera no aplica —se puede jugar cualquier categoría de
 * arriba— pero la segunda sigue valiendo.
 */
export function isBelowLevel(assigned: CategoryLevel, level: CategoryLevel): boolean {
  const mío = CATEGORY_LEVELS.indexOf(assigned);
  const otro = CATEGORY_LEVELS.indexOf(level);
  if (mío === -1 || otro === -1) return false;
  return otro > mío;
}

/**
 * EN QUÉ CATEGORÍAS DE ESTE TORNEO SE PUEDE ANOTAR ESTA PERSONA. Vacío = en
 * ninguna.
 *
 * Vive en el contrato por lo mismo que `eligibleLevels`: la pantalla tiene
 * que ofrecer EXACTAMENTE lo que la API acepta, y ahora son DOS pantallas las
 * que preguntan —el listado de torneos, para decir «podés anotarte», y la
 * ficha, para dibujar los botones—. Con la regla escrita tres veces, el día
 * que cambie va a cambiar en una.
 *
 * Las cinco condiciones son las mismas que aplica `addEntry` en el servidor:
 *
 *  1. ESE CUADRO no se ha tirado. Por CATEGORÍA y no por torneo: cada cuadro
 *     arranca cuando está listo, así que un torneo «en juego» porque el A
 *     masculino ya empezó sigue recibiendo gente en el B femenino.
 *  2. Un torneo terminado no recibe a nadie.
 *  3. La categoría es del sexo de esa persona: es nivel × sexo, y las demás
 *     son cuadros donde no puede jugar.
 *  4. El nivel le corresponde —el suyo o el de justo arriba, ver
 *     `eligibleLevels`—. Sin categoría en el club no hay nivel asignado y
 *     quedan todas las de su sexo: ahí decide quien organiza.
 *  5. No está ya anotada en ella, y no llegó al tope de categorías.
 *
 * EN UN TORNEO ABIERTO, LA CUATRO NO APLICA. Javier: «si el torneo es público
 * debería poder inscribirse a cualquier categoría». Un B puede tomar A o C, y
 * la inscripción que queda fuera de su nivel se le marca a quien organiza,
 * que decide con el botón de aprobar o rechazar que ya existe. Ofrecer todas
 * y controlar después: lo eligió él, preguntado con las tres opciones a la
 * vista.
 *
 * LAS OTRAS CUATRO SIGUEN VALIENDO. Un torneo abierto no es un torneo sin
 * reglas: el sexo, el cuadro ya tirado, el tope de dos categorías y el «no
 * dos veces en la misma» no dependen de quién sea socio de qué.
 */
/**
 * LO QUE HACE FALTA DE UN TORNEO PARA SABER SI ALGUIEN PUEDE ANOTARSE, y nada
 * más.
 *
 * ERA `Pick<Tournament, …>`, QUE PEDÍA DE MÁS. Un `Tournament` armado trae los
 * patrocinadores, las cuentas para depositar, el reglamento y el nombre de
 * cada inscrito; de todo eso, esta regla lee cinco campos. El servidor, que
 * tiene las filas crudas, tenía que inventar objetos completos —con nombres
 * vacíos y un formato de mentira— solo para poder preguntar.
 *
 * Un `Tournament` de verdad lo cumple igual, así que la pantalla no cambia.
 */
export interface EnterableCategory {
  level: CategoryLevel;
  sex: CategorySex;
  discipline: CategoryDiscipline;
  /** `null` = todavía no se sorteó, que es lo único que esta regla mira. */
  startedAt: string | null;
}

export interface EnterableTournament {
  status: TournamentStatus;
  openToEveryone: boolean;
  categories: readonly EnterableCategory[];
  entries: readonly {
    userId: string | null;
    /** Solo el id: quién más ocupa esa silla. Ver `playsIn`. */
    partner: { userId: string | null } | null;
    level: CategoryLevel;
    sex: CategorySex;
    discipline: CategoryDiscipline;
  }[];
}

export function enterableCategories<C extends EnterableCategory>(
  /*
   * GENÉRICO EN LA CATEGORÍA: entra lo que entre, sale lo MISMO. La pantalla
   * le pasa `TournamentCategory` —con su formato— y recibe eso de vuelta, no
   * una versión recortada a la que le falte el campo que iba a usar después.
   */
  tournament: Omit<EnterableTournament, 'categories'> & { categories: readonly C[] },
  /**
   * QUIÉN SE VA A ANOTAR. No necesariamente quien mira.
   *
   * `level` es su categoría EN EL CLUB, y `null` significa «no es del club»,
   * que no es lo mismo que «no tiene nivel»: sin membresía no hay contra qué
   * comparar y quedan todas las de su sexo, porque a un torneo llega gente de
   * otros clubes y decide quien organiza.
   *
   * ESTE ARGUMENTO ERA `viewer` Y EL NIVEL SALÍA DE `tournament.myLevel`, que
   * es el de quien mira. Servía para «¿en qué me puedo anotar yo?» y no para
   * «¿en qué puedo anotar a esta persona?», así que la pantalla donde el club
   * inscribe a otros tuvo que escribir la regla otra vez — y en esa copia se
   * perdieron dos condiciones. Ver el arreglo de «Primavera Mujeres».
   */
  player: { id: string; sex: Sex | null; level: CategoryLevel | null },
): C[] {
  if (player.sex === null) return [];
  if (tournament.status === 'FINISHED') return [];

  // En una constante y no leyendo `player.sex` adentro del filtro: el estrecha-
  // miento del `if` de arriba no cruza a una función anidada.
  const suSexo = player.sex;

  // `!== null` primero, y no por gusto: un invitado no se inscribió a sí
  // mismo —no tiene con qué— así que nunca es «suya», y comparar contra un
  // nulo a secas es la clase de igualdad que un día contesta que sí.
  const suyas = tournament.entries.filter((entry) => playsIn(entry, player.id));
  if (suyas.length >= TOURNAMENT_LIMITS.maxCategoriesPerPlayer) return [];

  /*
   * `null` = TODAS LAS DE SU SEXO. Dos caminos llegan acá:
   *
   *  · no es del club, así que no hay categoría asignada contra qué comparar;
   *  · el torneo está abierto, y ahí Javier decidió que no mande la categoría.
   */
  const niveles =
    tournament.openToEveryone || player.level === null ? null : eligibleLevels(player.level);

  /*
   * HACIA ABAJO NUNCA, NI SIQUIERA EN UN TORNEO ABIERTO.
   *
   * Javier: «no puedo jugar nunca un torneo en una categoría inferior a la
   * que estoy en el club». Un torneo abierto suelta el techo —un B se puede
   * anotar en A o en A+, y quien organiza lo revisa— pero NO el piso: un C
   * anotado en C2 le gana a gente que no tiene con qué, y la categoría entera
   * deja de tener sentido para los que sí son de C2. Eso no es «medirse con
   * mejores», es lo contrario.
   *
   * Solo aplica con categoría asignada: quien no es del club no tiene piso
   * contra qué comparar, y ahí decide quien organiza.
   */
  const piso = player.level;

  return tournament.categories.filter(
    (category) =>
      category.startedAt === null &&
      // `sexFitsCategory` Y NO `===`: el mixto no es igual a ningún sexo de
      // persona, así que comparado por igualdad es un cuadro donde no entra
      // nadie —sin error, sin mensaje, solo vacío—.
      sexFitsCategory(suSexo, category.sex) &&
      (niveles === null || niveles.includes(category.level)) &&
      (piso === null || !isBelowLevel(piso, category.level)) &&
      // Por `categoryKey` y no campo por campo: el singles B y el dobles B
      // tienen el mismo nivel y el mismo sexo, y comparados sin la disciplina
      // anotarse en uno tapa el otro.
      !suyas.some((entry) => categoryKey(entry) === categoryKey(category)),
  );
}

/**
 * ¿ESTA PERSONA JUEGA EN ESTA INSCRIPCIÓN? Las dos sillas cuentan.
 *
 * UNA INSCRIPCIÓN DE DOBLES ES UNA FILA CON DOS PERSONAS ADENTRO, y quien fue
 * anotado COMO PAREJA está tan adentro del cuadro como quien apretó el botón.
 * Preguntando solo por `userId` —que es lo que uno escribe sin pensarlo— la
 * pareja queda libre: se puede anotar otra vez en el mismo cuadro con otra
 * pareja distinta y terminar jugando contra sí misma, y el tope de categorías
 * le vale el doble que a los demás.
 *
 * `id` nulo nunca es nadie: dos invitados sin cuenta no son la misma persona,
 * y comparar nulos por igualdad es cómo un cuadro con invitados deja a
 * cualquiera afuera por «ya está anotado».
 */
export function playsIn(
  entry: { userId: string | null; partner: { userId: string | null } | null },
  userId: string | null,
): boolean {
  if (userId === null) return false;
  return entry.userId === userId || entry.partner?.userId === userId;
}

/**
 * POR QUÉ ESA PERSONA NO SE PUEDE ANOTAR EN NINGUNA CATEGORÍA.
 *
 * `null` cuando SÍ se puede — o sea, exactamente cuando `enterableCategories`
 * devuelve algo. Las dos son la misma decisión mirada de los dos lados, y por
 * eso viven juntas: separadas, la explicación se queda vieja respecto de la
 * regla y la pantalla termina dando un motivo que no es.
 *
 * EL ORDEN IMPORTA, Y VA DE LO GENERAL A LO PARTICULAR: primero lo que vale
 * para TODA la pantalla, después lo de cada persona.
 *
 * Quien lee esto es el club, mirando una LISTA. Con el torneo terminado,
 * contestar por persona reparte tres motivos distintos en tres filas —«le
 * falta el sexo», «ya está en dos», «sus cuadros se sortearon»— cuando el
 * hecho que las explica a todas es uno solo y es del torneo: se acabó. Dicho
 * una vez por fila, al menos dice lo mismo en todas.
 *
 * Después sí, lo de cada quien, de lo que no tiene arreglo a lo que sí: el
 * perfil incompleto, el tope de dos, que el torneo no corra su sexo, que no
 * corra su nivel, y por último el sorteo, que es lo único que un club podría
 * llegar a deshacer.
 */
/**
 * EN ORDEN, Y EL ORDEN ES LA RESPUESTA: se contesta el primero que aplique.
 *
 * Es un arreglo y no solo una unión de textos para que el catálogo de
 * traducciones se pueda comparar CONTRA ESTO. Escrito solo como tipo, agregar
 * un motivo compila en todos lados y sale en pantalla como la llave cruda en
 * los nueve idiomas, que es justo lo que pasó con «sex.MIXED».
 */
export const NOT_ENTERABLE_REASONS = [
  /** El torneo terminó. Vale para todo el mundo, así que se contesta primero. */
  'TOURNAMENT_FINISHED',
  /** Sin sexo en el perfil no hay categoría posible: una categoría es nivel × sexo. */
  'NO_SEX',
  /** Ya está en dos, que es el tope. */
  'TWO_ALREADY',
  /** El torneo no corre NINGUNA categoría de su sexo. */
  'NO_CATEGORY_FOR_SEX',
  /** El torneo corre categorías de su sexo, pero ninguna de su nivel. */
  'LEVEL_NOT_ELIGIBLE',
  /** Las que le corresponden ya se sortearon: un cuadro tirado no recibe a nadie. */
  'ALL_STARTED',
  /** Puede jugar, y ya está anotada en todas las que le tocan. */
  'ALREADY_IN_ALL',
] as const;

export type NotEnterableReason = (typeof NOT_ENTERABLE_REASONS)[number];

export function whyNotEnterable(
  tournament: EnterableTournament,
  player: { id: string; sex: Sex | null; level: CategoryLevel | null },
): NotEnterableReason | null {
  if (enterableCategories(tournament, player).length > 0) return null;

  if (tournament.status === 'FINISHED') return 'TOURNAMENT_FINISHED';
  if (player.sex === null) return 'NO_SEX';

  const suyas = tournament.entries.filter((entry) => playsIn(entry, player.id));
  if (suyas.length >= TOURNAMENT_LIMITS.maxCategoriesPerPlayer) return 'TWO_ALREADY';

  // `sexFitsCategory` Y NO `===`, igual que en `enterableCategories`: el mixto
  // no es igual a ningún sexo de persona, y comparado por igualdad este motivo
  // diría «este torneo no corre ninguna categoría de su sexo» sobre un torneo
  // de mixtos donde sí puede jugar.
  const suSexo = player.sex;
  const deSuSexo = tournament.categories.filter((c) => sexFitsCategory(suSexo, c.sex));
  if (deSuSexo.length === 0) return 'NO_CATEGORY_FOR_SEX';

  // La misma cuenta que en `enterableCategories`, y por la misma razón: en un
  // torneo abierto el nivel asignado no filtra nada.
  const niveles =
    tournament.openToEveryone || player.level === null ? null : eligibleLevels(player.level);
  // Y EL PISO, que también está allá: hacia abajo no se juega ni en un torneo
  // abierto. Sin esta línea, un B que solo tiene el C2 disponible recibiría
  // «ya estás en todas» —que es falso— en vez de «no corre ninguna para vos».
  const piso = player.level;
  const deSuNivel = deSuSexo.filter(
    (c) =>
      (niveles === null || niveles.includes(c.level)) &&
      (piso === null || !isBelowLevel(piso, c.level)),
  );
  if (deSuNivel.length === 0) return 'LEVEL_NOT_ELIGIBLE';

  /*
   * LO QUE QUEDA ES «YA SE SORTEARON» O «YA ESTÁ EN TODAS», y se distinguen
   * mirando las que NO se sortearon: si no queda ninguna abierta, el motivo
   * es el sorteo; si queda alguna abierta, entonces está anotada en ella.
   *
   * Este es el caso que la frase vieja tapaba, y el más común de todos: el
   * cuadro está tirado, no es que a la persona le falte nivel.
   */
  const abiertas = deSuNivel.filter((c) => c.startedAt === null);
  if (abiertas.length === 0) return 'ALL_STARTED';
  return 'ALREADY_IN_ALL';
}

/** `true` si va a jugar por encima de lo suyo. Es lo que dispara el aviso al club. */
export function isPlayingUp(assigned: CategoryLevel, level: CategoryLevel): boolean {
  return canPlayLevel(assigned, level) && level !== assigned;
}

/**
 * A category a community runs: a level crossed with a sex.
 *
 * Crossed and not kept apart because that is how the draws work — "A
 * femenino" has its own bracket, and a club can perfectly well run one and
 * not the other.
 *
 * NO PRICE HERE. Ticking a category only says the club runs it; what it
 * costs to enter is decided per TOURNAMENT, because a big tournament does
 * not charge what the Tuesday one charges.
 */
/**
 * UNA TABLA EN LA QUE COMPITO, dentro de un club.
 *
 * ES UNA CATEGORÍA DEL CLUB MÁS MI SITUACIÓN EN ELLA. La categoría —nivel,
 * sexo, disciplina— es del club y existe aunque yo no esté; `status` y
 * `requestedLevel` son míos y solo significan algo para mí.
 */
export interface MyRanking {
  level: CategoryLevel;
  /**
   * EL SEXO DE LA TABLA, QUE PUEDE NO SER EL MÍO.
   *
   * Es el punto entero de que esto sea una lista. Javier: «si soy B femenino
   * quiero estar en el ranking de C masculino». Leer el sexo del perfil para
   * dibujar la etiqueta —como hacía la pantalla— diría «C femenino», una
   * tabla en la que esa persona no está.
   */
  sex: CategorySex;
  discipline: CategoryDiscipline;
  /**
   * `ACTIVE` es compitiendo; `PENDING` es esperando que el club responda.
   *
   * ES DE LA TABLA Y NO DE LA MEMBRESÍA: se puede llevar un año adentro del
   * club y estar esperando respuesta por una segunda tabla. Esa espera no
   * quita de la primera.
   */
  status: CommunityMemberStatus;
  /**
   * EL CAMBIO DE NIVEL PEDIDO DENTRO DE ESTA TABLA. `null` si no hay ninguno.
   *
   * NO ES LO MISMO QUE PEDIR OTRA TABLA, y es la distinción que sostiene todo
   * el paso 4. Esto es «en esta tabla quiero pasar de C a B», y al aprobarse
   * la de C deja de existir. Pedir otra tabla es una inscripción MÁS: se
   * sigue compitiendo en las dos, con puntos separados.
   */
  requestedLevel: CategoryLevel | null;
}

/**
 * PEDIR ENTRAR A OTRA TABLA DEL CLUB DONDE YA ESTOY.
 *
 * LO APRUEBA SIEMPRE EL CLUB, aunque el club acepte socios nuevos de una. Lo
 * decidió Javier, y la razón es la misma que la de los puntos traídos de otro
 * ranking: auto-aceptar dice «confío en quién entra», no «confío en que cada
 * quien elija bien en qué tabla compite». Un C metiéndose solo en el ranking
 * A le desordena la tabla a todos los demás.
 */
export interface AskForRankingRequest {
  level: CategoryLevel;
  /** Cuál de las dos: el check de masculino / femenino de la pantalla. */
  sex: CategorySex;
}

/**
 * LA RESPUESTA DEL CLUB A UN PEDIDO DE TABLA, y también la inscripción
 * directa: son la misma operación desde el lado de quien administra.
 *
 * SON LA MISMA porque terminan igual —esa persona compitiendo en esa tabla—
 * y porque separarlas obligaría a quien administra a saber si la persona ya
 * pidió o no antes de elegir el botón, que es un dato que no tiene a mano.
 * Con `approve: true` sobre una tabla que nadie pidió, el club la inscribe;
 * sobre una pedida, la aprueba.
 */
export interface ReviewRankingRequest {
  level: CategoryLevel;
  sex: CategorySex;
  approve: boolean;
}

export interface CommunityCategory {
  level: CategoryLevel;
  /** Quién juega en este cuadro. `MIXED` solo en dobles. Ver `CategorySex`. */
  sex: CategorySex;
  /** Singles o dobles. Son dos cuadros distintos del mismo nivel y sexo. */
  discipline: CategoryDiscipline;
}

/* ===== Anotarse en otra tabla del mismo club ===== */

/**
 * ANOTARSE EN OTRA TABLA DEL MISMO CLUB.
 *
 * Javier: «no puedo estar en 2 categorías en el mismo club, si soy B quiero
 * participar en el ranking A, y si soy B femenino quiero estar en el ranking
 * de C masculino». Son dos pedidos y una sola cosa: una persona compite en
 * más de una tabla del mismo club, y la tabla se elige entera —nivel Y
 * sexo—, no solo el nivel.
 *
 * NO ES UN CAMBIO DE CATEGORÍA, y eso es lo que distingue este archivo del
 * camino que ya existía. Cambiar de categoría es `requestedLevel`: la misma
 * inscripción pasa de C a B y la de C deja de existir. Anotarse en otra es
 * una inscripción MÁS: se sigue compitiendo en las dos, con puntos separados
 * —eso es el paso 2— y cada reto cuenta en una sola —el paso 3—.
 *
 * TODO ACÁ ES PURO. La regla se prueba con listas de ternas, sin base de
 * datos y sin montar un club entero, que es lo que hace barato probar los
 * casos raros: pedir la tabla en la que ya se está, pedir una tercera, pedir
 * una que el club no corre.
 */

/** Una tabla del club: nivel, sexo y disciplina. Las tres, siempre. */
export interface Tabla {
  level: CategoryLevel;
  sex: CategorySex;
  discipline: CategoryDiscipline;
}

/**
 * CUÁNTAS TABLAS COMO MÁXIMO, POR PERSONA Y POR CLUB.
 *
 * Dos, y lo eligió Javier. Cubre el caso que pidió —la suya y una más— sin
 * abrir la puerta a que alguien se anote en seis y vuelva ilegible tanto el
 * ranking como su propio perfil.
 *
 * CUENTA LAS QUE ESPERAN RESPUESTA. Si solo contara las aprobadas, se podrían
 * dejar cinco pedidos esperando y que el club los aprobara de a uno sin que
 * nada avisara al pasarse: el límite se rompería en la aprobación, que es
 * donde nadie lo está mirando.
 */
export const MÁXIMO_DE_TABLAS = 2;

function mismaTabla(a: Tabla, b: Tabla): boolean {
  return a.level === b.level && a.sex === b.sex && a.discipline === b.discipline;
}

/**
 * LOS RANKINGS SON DE SINGLES, Y SOLO MASCULINO O FEMENINO.
 *
 * Un club corre más categorías de las que tienen ranking: dobles y mixto
 * existen para los TORNEOS, donde se juega en pareja. Un ranking es de una
 * persona sola, así que ni los dobles ni el mixto tienen tabla donde anotarse
 * — el servidor solo crea inscripciones con `discipline: 'SINGLES'`.
 *
 * SIN ESTE FILTRO LA PANTALLA OFRECÍA LAS DE TORNEO, y se veía feo de dos
 * formas a la vez: «A · Masculino» aparecía DOS veces —una de singles y otra
 * de dobles, indistinguibles porque la etiqueta no dice la disciplina— y el
 * mixto salía como «A · sex.MIXED», el nombre crudo de una clave de
 * traducción que no existe, porque `sex` solo tiene masculino y femenino: es
 * el sexo de una PERSONA, y mixto no lo es.
 *
 * VA ACÁ Y NO EN LA PANTALLA porque es la misma regla que aplica el servidor:
 * pedir una tabla de dobles sería pedir algo que no se puede crear. Escrito
 * en el web, el servidor seguiría aceptándolo por una ruta que nadie mira.
 */
function esTablaDeRanking(tabla: Tabla): boolean {
  return tabla.discipline === 'SINGLES' && (tabla.sex === 'MALE' || tabla.sex === 'FEMALE');
}

/** Por qué no se puede. `null` es que sí se puede. */
export type Estorbo =
  | 'YA_ESTA_EN_ESA'
  | 'DEMASIADAS_TABLAS'
  | 'NO_EXISTE_EN_EL_CLUB'
  | 'NO_ES_DE_RANKING';

/**
 * ¿PUEDE ESTA PERSONA ANOTARSE EN ESTA TABLA DE ESTE CLUB?
 *
 * `suyas` son TODAS las que ya tiene ahí, las aprobadas y las que esperan
 * respuesta. `delClub` son las categorías que el club corre.
 *
 * EL ORDEN DE LAS TRES PREGUNTAS NO ES CASUAL, porque lo que se le dice a la
 * persona es el PRIMER estorbo que aparece. «Ya estás en esa» antes que «ya
 * tenés dos»: quien toca dos veces el mismo botón tiene que leer que ya está
 * anotado, no que llegó al límite, que lo mandaría a borrar una tabla para
 * volver a intentar lo mismo. Y «el club no la corre» al final porque es el
 * único que no depende de la persona.
 */
export function porQuéNoPuede(
  tabla: Tabla,
  suyas: readonly Tabla[],
  delClub: readonly Tabla[],
): Estorbo | null {
  // PRIMERO DE TODO: los dobles y el mixto no tienen ranking. Va antes que
  // las demás porque no depende de la persona ni del club — esa tabla no
  // existe como ranking para nadie, así que las otras preguntas no aplican.
  if (!esTablaDeRanking(tabla)) return 'NO_ES_DE_RANKING';
  if (suyas.some((suya) => mismaTabla(suya, tabla))) return 'YA_ESTA_EN_ESA';
  if (suyas.length >= MÁXIMO_DE_TABLAS) return 'DEMASIADAS_TABLAS';
  if (!delClub.some((delClub) => mismaTabla(delClub, tabla))) return 'NO_EXISTE_EN_EL_CLUB';
  return null;
}

/**
 * LAS QUE TODAVÍA PUEDE PEDIR, para dibujar la lista de la pantalla.
 *
 * SALE DE LA MISMA REGLA que la validación del servidor, y por eso vive acá y
 * no en el web: dos listas calculadas en dos lados se separan, y separadas la
 * pantalla ofrece tablas que el servidor rechaza —o esconde las que sí
 * aceptaría, que es peor porque nadie lo reporta—.
 *
 * DEVUELVE VACÍO CUANDO YA LLEGÓ AL LÍMITE, y la pantalla usa eso para no
 * ofrecer el botón: abrir una lista vacía es peor que no tener el botón.
 */
export function tablasQuePuedePedir(
  suyas: readonly Tabla[],
  delClub: readonly Tabla[],
): Tabla[] {
  return delClub.filter((tabla) => porQuéNoPuede(tabla, suyas, delClub) === null);
}

/**
 * LOS SEXOS QUE EL CLUB CORRE PARA UN NIVEL.
 *
 * Es lo que llena el check de «masculino / femenino» que pidió Javier. Un
 * club puede correr «C masculino» y no «C femenino», y ofrecer los dos
 * siempre sería ofrecer una tabla que no existe.
 *
 * NO MIRA EL SEXO DEL PERFIL, y ese es el punto entero del paso 4: una mujer
 * puede pedir el ranking masculino. Filtrar por el perfil acá volvería a
 * cerrar justo lo que se está abriendo.
 */
export function sexosDelNivel(
  level: CategoryLevel,
  delClub: readonly Tabla[],
): CategorySex[] {
  const sexos = new Set(delClub.filter((t) => t.level === level).map((t) => t.sex));
  // Orden fijo y no el de llegada: una lista que cambia de orden según cómo
  // se crearon las categorías hace que el mismo club se vea distinto cada vez.
  return (['MALE', 'FEMALE'] as const).filter((sexo) => sexos.has(sexo));
}

/**
 * SINGLES O DOBLES.
 *
 * Es un EJE APARTE del nivel y del sexo, y no un valor más metido adentro de
 * alguno de los dos: un club corre «B masculino» de singles Y de dobles al
 * mismo tiempo, son dos cuadros, dos rankings y dos inscripciones distintas.
 * Metido adentro del sexo —un «B dobles masculino» como si fuera un sexo más—
 * sería imposible decir que el mixto es de dobles.
 */
export const CATEGORY_DISCIPLINES = ['SINGLES', 'DOUBLES'] as const;
export type CategoryDiscipline = (typeof CATEGORY_DISCIPLINES)[number];

/**
 * EL SEXO DE UNA CATEGORÍA, QUE NO ES EL SEXO DE UNA PERSONA.
 *
 * Son dos preguntas distintas y por eso son dos tipos. `Sex` contesta «¿qué es
 * esta persona?» y tiene dos respuestas. Esto contesta «¿quién juega en este
 * cuadro?» y tiene tres: el masculino, el femenino, y el MIXTO —una pareja de
 * uno de cada— que no es ni lo uno ni lo otro.
 *
 * TENERLOS SEPARADOS ES LO QUE HACE POSIBLE EL MIXTO. Si `MIXED` fuera un
 * valor más de `Sex`, cada una de las sesenta comparaciones del tipo
 * `categoría.sex === persona.sex` que hay en la aplicación seguiría compilando
 * y contestaría que no para todo el mundo: un cuadro de mixtos donde nadie se
 * puede inscribir, sin un solo error en ningún lado.
 *
 * `MIXED` SOLO EXISTE EN DOBLES. No hay singles mixtos: un partido de singles
 * entre un hombre y una mujer no es una categoría, es un amistoso. Lo cuida
 * `checkCategory`.
 */
export const CATEGORY_SEXES = ['MALE', 'FEMALE', 'MIXED'] as const;
export type CategorySex = (typeof CATEGORY_SEXES)[number];

/**
 * ¿Puede esta persona jugar en un cuadro de este sexo?
 *
 * En el mixto juegan los dos, y por eso no alcanza con comparar: la pareja
 * tiene que ser una de cada, y eso se comprueba sobre la PAREJA y no sobre
 * cada quien por separado. Ver `checkPair`.
 */
export function sexFitsCategory(personSex: Sex, category: CategorySex): boolean {
  return category === 'MIXED' || category === personSex;
}

/**
 * QUÉ SEXO TIENE QUE SER LA PAREJA, para que el par sea legal.
 *
 * Es `checkPair` mirado al revés: aquella contesta «¿este par se puede?» y
 * esta «¿a quién puedo buscar?». Viven pegadas porque son la misma regla, y
 * separadas una queda ofreciendo gente que la otra rechaza.
 *
 * EN EL MIXTO DEPENDE DE QUIÉN PREGUNTA: al hombre le toca buscar una mujer y
 * a la mujer un hombre. Por eso recibe `mine` y no solo la categoría — y por
 * eso NO se puede usar la llave de la categoría como filtro de búsqueda: el
 * sexo del CUADRO mixto no es el sexo de nadie, y buscar por él devuelve cero
 * personas sin que nada falle.
 */
export function partnerSexFor(category: { sex: CategorySex }, mine: Sex): Sex {
  if (category.sex !== 'MIXED') return category.sex;
  return mine === 'MALE' ? 'FEMALE' : 'MALE';
}

/**
 * QUIÉN VA CON QUIÉN. Devuelve el motivo si la pareja no puede jugar ese
 * cuadro, o `null` si está bien.
 *
 * RECIBE LA PAREJA ENTERA Y NO CADA PERSONA POR SEPARADO, y ahí está todo el
 * asunto. `sexFitsCategory` contesta «¿esta persona puede jugar acá?» y en el
 * mixto contesta que sí para cualquiera —porque en el mixto juegan los dos—.
 * Preguntado uno por uno, DOS HOMBRES PASAN un cuadro de mixtos: cada uno
 * cumple la condición y la pareja no. La regla del mixto no es sobre nadie en
 * particular, es sobre el par, y por eso la función toma el par.
 *
 * `null` en `partner` significa «viene solo», que no es lo mismo que «no sé
 * quién es»: en singles es lo correcto y en dobles es el error.
 *
 * SEXO DESCONOCIDO NO ES SEXO EQUIVOCADO. Un invitado sin cuenta no tiene
 * perfil, así que no hay contra qué comparar, y al invitado lo mete el club:
 * ahí decide quien organiza, igual que con el nivel de quien no es socio. Lo
 * que no se puede comprobar no se inventa.
 */
export function checkPair(
  category: { sex: CategorySex; discipline: CategoryDiscipline },
  one: { id?: string | null; sex: Sex | null },
  partner: { id?: string | null; sex: Sex | null } | null,
): string | null {
  if (category.discipline === 'SINGLES') {
    if (partner !== null) return 'En singles se juega solo: ese cuadro no lleva pareja.';
    // Y EL SEXO TAMBIÉN SE REVISA ACÁ. Antes lo cuidaba sin querer la búsqueda
    // de la categoría —se buscaba «la del nivel X y del sexo de esta persona»,
    // así que una mujer en el cuadro masculino no encontraba ninguna—. Ahora
    // la categoría llega dicha entera desde el cliente y esa red ya no existe:
    // sin esta línea, cualquiera se anota en el cuadro del otro sexo.
    if (one.sex !== null && !sexFitsCategory(one.sex, category.sex)) {
      return category.sex === 'MALE'
        ? 'Ese cuadro es masculino.'
        : 'Ese cuadro es femenino.';
    }
    return null;
  }

  if (partner === null) return 'En dobles se juega con pareja: falta decir con quién.';

  // Antes que los sexos: una persona anotada consigo misma cumpliría
  // cualquier regla de sexo del masculino y del femenino, y sería un solo
  // jugador ocupando las dos sillas.
  const unoId = one.id ?? null;
  const otroId = partner.id ?? null;
  if (unoId !== null && unoId === otroId) {
    return 'Nadie es su propia pareja: elegí a otra persona.';
  }

  if (category.sex === 'MIXED') {
    // Uno de cada. Con un sexo sin registrar no se puede saber, y no saber no
    // es motivo para rechazar.
    if (one.sex === null || partner.sex === null) return null;
    return one.sex === partner.sex ? 'El mixto es de uno de cada: la pareja tiene que ser un hombre y una mujer.' : null;
  }

  // Masculino o femenino: los dos tienen que ser de ese sexo.
  for (const quien of [one, partner]) {
    if (quien.sex === null) continue;
    if (!sexFitsCategory(quien.sex, category.sex)) {
      return category.sex === 'MALE'
        ? 'Ese cuadro es masculino: los dos de la pareja tienen que ser hombres.'
        : 'Ese cuadro es femenino: las dos de la pareja tienen que ser mujeres.';
    }
  }
  return null;
}

/**
 * ¿ESTA CATEGORÍA EXISTE? Devuelve el motivo si no, o `null` si está bien.
 *
 * La única combinación imposible es el singles mixto. Vive acá —en el
 * contrato— porque la pantalla no tiene que ofrecerla y el servidor no tiene
 * que aceptarla, y dos versiones de la misma regla es cómo aparece un botón
 * que siempre falla.
 */
export function checkCategory(category: {
  sex: CategorySex;
  discipline: CategoryDiscipline;
}): string | null {
  if (category.sex === 'MIXED' && category.discipline === 'SINGLES') {
    return 'El mixto es de dobles: en singles no hay categoría mixta.';
  }
  return null;
}

/**
 * LA LLAVE DE UNA CATEGORÍA: un texto que la identifica y nada más.
 *
 * QUÉ PROBLEMA RESUELVE. Esta misma idea estaba escrita a mano en TRECE
 * lugares —la pantalla, el servidor, el repositorio, los dobles de prueba— y
 * con TRES separadores distintos: «B/MALE», «B:MALE» y «B-MALE». Mientras cada
 * uno se compare solo consigo mismo no se nota; el día que un valor cruza de un
 * lado al otro, dos categorías que son la misma dejan de serlo y el cuadro
 * aparece vacío sin que nada falle.
 *
 * RECIBE EL OBJETO Y NO LOS CAMPOS SUELTOS, y eso es lo importante: el día que
 * una categoría tenga un campo más —dobles, por ejemplo— quien pase la
 * categoría entera lo hereda solo, y quien arme un objeto a mano deja de
 * compilar. Es la diferencia entre que el compilador encuentre los trece
 * lugares y que los encuentre un jugador mirando un cuadro en blanco.
 *
 * NO SE GUARDA NI VIAJA: es para juntar, comparar y poner de `key` en una
 * lista. Lo que se guarda son los campos.
 */
export function categoryKey(category: {
  level: CategoryLevel;
  sex: CategorySex;
  discipline: CategoryDiscipline;
}): string {
  return `${category.level}/${category.sex}/${category.discipline}`;
}

/**
 * LOS CUADROS QUE PUEDE CORRER UN CLUB: cada disciplina cruzada con cada sexo,
 * menos el que no existe.
 *
 * SE DERIVA, NO SE ESCRIBE. Es el producto de las dos listas pasado por
 * `checkCategory`, así que el singles mixto no está porque la regla dice que no
 * existe —no porque alguien se acordó de no escribirlo—. Una lista a mano es
 * una que se queda vieja el día que se agregue una disciplina.
 *
 * EL ORDEN ES EL QUE SE MUESTRA: primero singles, que es lo que todo club
 * corre, y dobles después, que es el agregado.
 */
export const CATEGORY_GROUPS: ReadonlyArray<{
  discipline: CategoryDiscipline;
  sex: CategorySex;
}> = CATEGORY_DISCIPLINES.flatMap((discipline) =>
  CATEGORY_SEXES.filter((sex) => checkCategory({ sex, discipline }) === null).map((sex) => ({
    discipline,
    sex,
  })),
);

/**
 * CUÁNTAS CATEGORÍAS PUEDE CORRER UN CLUB COMO MÁXIMO: todas las que existen.
 *
 * SE CALCULA, NO SE ESCRIBE, y esa es toda la razón de que exista esta
 * constante. Antes había un `12` escrito a mano en tres validadores, con un
 * comentario que lo explicaba así: «seis niveles por dos sexos son doce
 * categorías posibles, y no existe una decimotercera». Era verdad —hasta que
 * llegaron los dobles y el mixto—. Nadie volvió a ese número, y el club que
 * quiso correr las seis masculinas y las seis femeninas de singles más
 * cualquier cosa de dobles se topaba con «Hay más categorías de las que
 * existen» intentando agregar una que sí existe.
 *
 * Hoy son cinco grupos por seis niveles: treinta. Y el día que se agregue una
 * disciplina o un nivel, este número se mueve solo.
 */
export const MAX_COMMUNITY_CATEGORIES = CATEGORY_GROUPS.length * CATEGORY_LEVELS.length;

/**
 * LA DIVISIÓN DE UNA CATEGORÍA: oro, plata o bronce.
 *
 * QUÉ PROBLEMA RESUELVE. «A+» y «C2» no le dicen nada a quien acaba de entrar,
 * y tampoco dicen de un vistazo QUÉ TAN ARRIBA está alguien: hay que acordarse
 * de las seis letras y de su orden. La división contesta esa pregunta sola —es
 * lo que Javier vio en MiniTour— y se dibuja, que se lee más rápido que se lee.
 *
 * TRES Y NO SEIS. Un dibujo por categoría serían seis formas arbitrarias que
 * nadie memoriza y que a 22 píxeles se ven todas iguales. Tres se aprenden de
 * una, y la letra exacta ya está escrita al lado: el icono no la repite, la
 * resume.
 *
 * EL CORTE ES PAREJO —de a dos— y es a propósito que el oro quede chico: en un
 * club de barrio casi todo el mundo es B o C, así que un oro que alcance a la
 * mitad de la gente no distingue a nadie. Si algún día el corte tiene que ser
 * otro, se cambia acá y cambia en todas las pantallas a la vez.
 */
export const CATEGORY_DIVISIONS = ['GOLD', 'SILVER', 'BRONZE'] as const;
export type CategoryDivision = (typeof CATEGORY_DIVISIONS)[number];

/**
 * CUÁNTOS GALONES LLEVA CADA DIVISIÓN. Es lo que se dibuja adentro del escudo.
 *
 * Vive en el contrato y no en la pantalla porque es parte de lo que la división
 * ES: tres galones tienen que ser siempre el oro. Dos dibujos que se contradigan
 * entre una pantalla y otra son un perfil que miente.
 */
export const DIVISION_CHEVRONS: Record<CategoryDivision, number> = {
  GOLD: 3,
  SILVER: 2,
  BRONZE: 1,
};

/**
 * En qué división cae una categoría.
 *
 * Sale del ORDEN de `CATEGORY_LEVELS`, que ya es «del más fuerte al más flojo»,
 * y no de una lista escrita aparte: una segunda lista de niveles es una que se
 * queda vieja el día que se agregue uno.
 */
export function divisionOf(level: CategoryLevel): CategoryDivision {
  const puesto = CATEGORY_LEVELS.indexOf(level);
  // Un nivel que no está en la escala no puede reventar un perfil entero: cae
  // en la división de abajo, que es lo más honesto que se puede decir de él.
  if (puesto === -1) return 'BRONZE';
  const porDivision = Math.ceil(CATEGORY_LEVELS.length / CATEGORY_DIVISIONS.length);
  const cual = Math.floor(puesto / porDivision);
  return CATEGORY_DIVISIONS[Math.min(cual, CATEGORY_DIVISIONS.length - 1)] ?? 'BRONZE';
}

/** Lo que se manda para fijar las categorías de una comunidad. */
export interface SetCommunityCategoriesRequest {
  categories: CommunityCategory[];
}

// --- Miembros ---------------------------------------------------------

export const COMMUNITY_MEMBER_ROLES = ['MEMBER', 'ADMIN'] as const;
export type CommunityMemberRole = (typeof COMMUNITY_MEMBER_ROLES)[number];

/**
 * En qué estado está una membresía.
 *
 * PENDING es una solicitud: la persona pidió entrar y eligió categoría, pero
 * todavía no la aprobaron. Es un estado de la MEMBRESÍA y no una tabla
 * aparte, a propósito: así «una persona, una membresía por club» lo garantiza
 * la misma llave primaria que ya existía. Con una tabla de solicitudes al
 * lado habría que cuidar a mano que nadie tenga una solicitud pendiente y una
 * membresía al mismo tiempo, y ese es justo el tipo de invariante que se
 * rompe seis meses después.
 *
 * QUIEN ESTÁ EN PENDING NO ES MIEMBRO PARA NADA: no cuenta en el total, no
 * sale en la lista de jugadores, no se puede inscribir en un torneo y no
 * aparece en el ranking. Toda consulta de miembros filtra por ACTIVE.
 */
export const COMMUNITY_MEMBER_STATUSES = ['PENDING', 'ACTIVE'] as const;
export type CommunityMemberStatus = (typeof COMMUNITY_MEMBER_STATUSES)[number];

/** Una solicitud de ingreso, tal como la ve quien administra el club. */
export interface JoinRequest {
  userId: string;
  displayName: string;
  fullName: string;
  avatarPath: string | null;
  /**
   * EL SEXO DE LA TABLA QUE PIDIÓ, que puede no ser el suyo: una mujer puede
   * pedir entrar al ranking masculino. Se escribe con `categoryName`, no con
   * `sex.MALE`. Ver `CommunityMember.sex`.
   */
  sex: CategorySex;
  /** La disciplina de la tabla que pidió. Hoy siempre `SINGLES`. */
  discipline: CategoryDiscipline;
  /** La categoría que la persona eligió al pedir entrar. */
  level: CategoryLevel;
  locationLabel: string;
  requestedAt: string;
  /**
   * LOS DATOS PARA DECIDIR, que es de lo que se trata esta pantalla.
   *
   * La edad y el WhatsApp: con el apodo y el nombre legal solos, aprobar a
   * alguien es decir que sí a un desconocido. La edad ubica a la persona —un
   * club sabe si espera a alguien de 15 o de 50— y el teléfono es cómo se
   * confirma en Costa Rica: se le escribe y se pregunta.
   *
   * SOLO LO VE QUIEN ADMINISTRA el club, y solo mientras la solicitud está
   * esperando: es la persona la que eligió pedirle entrada a ESE club. Si a
   * Javier le parece de más, se saca de acá y desaparece de todos lados.
   */
  age: number;
  whatsappPhone: string;
  /**
   * LOS PUNTOS QUE PIDIÓ TRAER DE OTRO RANKING. `null` = no pidió.
   *
   * Quien viene de otra liga entra en cero y aparece último, así que durante
   * meses el ranking del club dice que el mejor jugador nuevo es el peor de
   * todos.
   */
  requestedPoints: number | null;
  /** De dónde vienen. Sin esto, la solicitud es un número suelto. */
  requestedPointsNote: string | null;
}

/**
 * Pedir entrar a un club.
 *
 * La categoría es OBLIGATORIA. Sin ella no hay ranking ni torneos —es por lo
 * que se filtra todo— y pedirla después significa perseguir a la persona para
 * que complete algo que ya podría haber elegido. Se ofrecen solo las del sexo
 * que tiene en su perfil, porque una categoría es nivel × sexo.
 */
export interface RequestJoinRequest {
  level: CategoryLevel;
  /**
   * LOS PUNTOS QUE PIDE TRAER DE OTRO RANKING. Opcional.
   *
   * PEDIRLOS APAGA EL AUTO-ACEPTAR del club: el interruptor de auto-aceptar
   * dice «confío en quién entra», no «confío en cuántos puntos se pone cada
   * quien». Sin esa regla, cualquiera entra a un club abierto con mil puntos
   * y se pone primero sin jugar un partido.
   */
  requestedPoints?: number;
  /** De dónde vienen. Es lo único que quien administra tiene para decidir. */
  requestedPointsNote?: string;
}

/**
 * `POST /communities/:id/join-requests/:userId/approve`.
 *
 * `points` es LO QUE SE CONCEDE, que puede ser menos de lo pedido o cero.
 * Ausente = se concede lo que pidió, porque decir que sí a la solicitud es
 * decir que sí a lo que la solicitud pedía.
 */
export interface ApproveJoinRequest {
  points?: number;
}

/** Cambiar el rol de alguien dentro del club. Lo hace quien lo creó. */
export interface SetMemberRoleRequest {
  role: CommunityMemberRole;
}

/**
 * ALGUIEN EN UNA TABLA DE UN CLUB. UNA FILA POR TABLA, NO POR PERSONA.
 *
 * Desde que se puede competir en varias —«hay mujeres que quieren participar
 * en los rankings masculinos», «un hombre puede estar en C y B masculino»—
 * quien está en dos aparece DOS VECES en la lista del club, con el mismo
 * `userId` y distinto `(level, sex)`. Quien lea esta lista para contar
 * PERSONAS tiene que agrupar por `userId`.
 */
export interface CommunityMember {
  userId: string;
  displayName: string;
  /** Private elsewhere, but a community admin needs it to tell two "Tico" apart. */
  fullName: string;
  avatarPath: string | null;
  /**
   * EL SEXO DE LA TABLA EN LA QUE COMPITE, QUE PUEDE NO SER EL SUYO.
   *
   * Es el cambio que hizo posible todo esto: antes salía del perfil —por eso
   * eran lo mismo, y por eso podía ser `null`— y ahora es una columna de la
   * inscripción. El perfil dice qué es alguien; esto dice dónde juega.
   *
   * YA NO ES NULABLE: no se puede estar en una tabla sin saber en cuál.
   *
   * ES `CategorySex` Y NO `Sex` A PROPÓSITO: una tabla puede ser mixta —en
   * dobles— y una persona no. Para escribirlo en pantalla va `categoryName`
   * con `discipline`, NUNCA `t('sex.' + member.sex)`: escribir «Masculino» al
   * lado de una mujer que compite en la tabla masculina diría algo falso
   * sobre ella, y además no existe la traducción del caso mixto.
   */
  sex: CategorySex;
  /**
   * LA DISCIPLINA DE LA TABLA. Hoy siempre `SINGLES`.
   *
   * Viaja aunque hoy sea constante porque es la otra mitad del nombre del
   * cuadro: sin ella no se puede escribir «Singles masculino», y quien la
   * arme a mano el día que existan los dobles se va a equivocar.
   */
  discipline: CategoryDiscipline;
  /**
   * REQUIRED. It is what the ranking is filtered by, so a member without one
   * would be a player who exists and appears nowhere.
   */
  level: CategoryLevel;
  /**
   * La categoría que esta persona PIDIÓ, si está esperando respuesta.
   *
   * `null` es «no pidió nada». Solo puede haber una esperando por club: es
   * una columna de la membresía, no una fila de una tabla de solicitudes.
   */
  requestedLevel: CategoryLevel | null;
  role: CommunityMemberRole;
  joinedAt: string;
}

/** Un jugador encontrado en la búsqueda, con lo justo para reconocerlo. */
/**
 * UNA PÁGINA DE RESULTADOS, no una lista suelta.
 *
 * `hasMore` existe porque al filtrar por categoría —sin escribir un nombre—
 * los resultados son cientos y solo caben algunos. Cortar en silencio hace
 * creer que los que se ven son TODOS, y quien busca a alguien que no aparece
 * no puede distinguir «no está en la aplicación» de «quedó abajo del corte».
 */
export interface PlayerSearchPage {
  players: PlayerSearchResult[];
  /** Hubo más de los que caben. La pantalla lo dice y pide afinar. */
  hasMore: boolean;
}

export interface PlayerSearchResult {
  userId: string;
  displayName: string;
  fullName: string;
  avatarPath: string | null;
  /** `null` for an account that predates the field. They cannot be added until they fill it in. */
  sex: Sex | null;
  /** Provincia y cantón, para distinguir dos personas con el mismo apodo. */
  locationLabel: string;
  /** `true` si ya está en esta comunidad: la búsqueda lo muestra igual, pero no se puede agregar dos veces. */
  alreadyMember: boolean;
  /**
   * SU CATEGORÍA EN ESTE CLUB, o `null` si no es miembro.
   *
   * Viaja para que la pantalla ofrezca SOLO las categorías en las que esa
   * persona puede jugar —la suya y la de justo arriba, ver `eligibleLevels`—.
   * Sin este dato, la lista ofrecía «B» a alguien de «A» y el servidor lo
   * rechazaba después: un formulario que ofrece opciones que no funcionan
   * enseña a desconfiar del formulario.
   *
   * A quien NO es del club se le puede inscribir en cualquier categoría que
   * el torneo corra para su sexo: sin membresía no hay categoría asignada y
   * no hay contra qué comparar.
   */
  level: CategoryLevel | null;
}

export interface AddCommunityMemberRequest {
  userId: string;
  level: CategoryLevel;
}

export interface UpdateCommunityMemberRequest {
  level: CategoryLevel;
}

// --- Patrocinadores ---------------------------------------------------

/** Un patrocinador del TORNEO: el banner y el carrusel son suyos, no del club. */
export interface Sponsor {
  id: string;
  name: string;
  imagePath: string;
  /** Adónde lleva el logo, o `null` si no lleva a ningún lado. */
  linkUrl: string | null;
  /** Menor primero. Es el orden del carrusel. */
  position: number;
}

export const SPONSOR_LIMITS = { nameMax: 80, linkMax: 300 } as const;

// --- Las cuentas para depositar ---------------------------------------

/**
 * UNA CUENTA A DONDE DEPOSITAR.
 *
 * QUÉ PROBLEMA RESUELVE. La caja del comprobante pedía una foto del pago sin
 * decir cuánto había que pagar ni a qué cuenta: el número andaba por el grupo
 * de WhatsApp del club, y quien se anotaba de noche no tenía dónde buscarlo.
 *
 * UNA FILA POR CUENTA Y NO UN PÁRRAFO. Los clubes tienen dos o tres —el
 * banco, SINPE, a veces otra— y metidas todas en un texto largo nadie sabe
 * cuál copiar. Así cada una se copia sola.
 *
 * SON DEL TORNEO, Y SOLO DEL TORNEO. El club no guarda cuentas — Javier: «El
 * torneo va a tener reglamento y cuentas, el club solo va a tener
 * reglamento»—. La memoria la hace el torneo ANTERIOR: al crear uno nuevo,
 * sus cuentas salen del último torneo de ese club, y desde ahí son suyas. Así
 * se repiten solas sin una pantalla aparte que alguien tenga que descubrir.
 *
 * `number` ES TEXTO. Un IBAN tiene letras, un SINPE es un teléfono, y los dos
 * llevan ceros a la izquierda que un número se comería.
 */
export interface PaymentAccount {
  id: string;
  number: string;
  /** De quién es y de qué banco. Es lo que decide cuál usar cuando hay tres. */
  description: string;
  /** Menor primero. Lo decide el club: la cuenta preferida va arriba. */
  position: number;
}

export const ACCOUNT_LIMITS = { numberMax: 80, descriptionMax: 120 } as const;

// --- Los parámetros de retos del club ----------------------------------

/**
 * LOS FRENOS QUE CADA CLUB LE PONE A LOS RETOS POR RANKING.
 *
 * QUÉ PROBLEMA RESUELVEN. El reto no tenía más freno que «los dos en el mismo
 * club y el mismo ranking». Sin nada más, quien tiene la tarde libre reta a
 * diez personas el mismo día y el ranking pasa a medir tiempo disponible; dos
 * amigos se turnan las victorias y suben los dos; y el último de la tabla reta
 * al primero, pierde 6-0 6-0 y se lleva puntos por haber jugado.
 *
 * TODOS ARRANCAN APAGADOS. Un club que no entre a configurar nada se comporta
 * exactamente como antes de que esto existiera: un valor de fábrica que cambia
 * el comportamiento es cambiarle las reglas a un club que no pidió nada.
 *
 * LOS AMISTOSOS NO SE TOCAN. No son de ningún club y no suman en ningún
 * ranking, así que un parámetro del club no tiene nada que decir sobre ellos.
 */
export interface ChallengeSettings {
  /** ¿Se puede retar por ranking acá? Los retos ya abiertos no se cancelan. */
  enabled: boolean;
  /**
   * Los días en que se reinicia la cuota. ISO: 1 = lunes, 7 = domingo.
   * Vacío = SIN CUOTA. El reinicio es a medianoche de Costa Rica.
   */
  resetDays: number[];
  /**
   * Cuántos retos se pueden MANDAR —y cuántos RECIBIR— entre dos reinicios.
   * Se cuentan por separado: si compartieran el tope, quien recibe dos retos
   * se quedaría sin poder mandar ninguno sin haber hecho nada.
   */
  perReset: number;
  /** Cuántos retos hay que jugar antes de repetir rival. Cero = sin regla. */
  repeatAfter: number;
  /**
   * CUÁNTOS DÍAS HAY PARA CONTESTAR. `null` = sin plazo.
   *
   * Sin plazo, un reto sin contestar se queda abierto para siempre y ADEMÁS
   * TAPA EL LUGAR: no se puede volver a retar a esa persona mientras haya uno
   * abierto entre los dos. Quien no contesta bloquea a quien lo retó sin
   * querer y sin enterarse.
   */
  acceptDays: number | null;
  /**
   * CUÁNTOS DÍAS HAY PARA JUGARLO, desde que se aceptó. `null` = sin plazo.
   *
   * Vencido, el reto se cierra SIN GANADOR. No se inventa un resultado: que
   * dos no se hayan puesto de acuerdo no dice quién es mejor, y meterlo en el
   * ranking lo volvería un ranking de disponibilidad.
   */
  playDays: number | null;
  /**
   * PUNTOS QUE SE RESTAN a quien deja vencer un reto SIN CONTESTAR.
   * `null` = sin penalidad. Se guarda en positivo; el signo lo pone el
   * servidor al aplicarla.
   *
   * Solo al silencio: rechazar a tiempo es una respuesta válida, y cobrarla
   * empujaría a aceptar retos que no se pueden jugar.
   */
  penaltyUnanswered: number | null;
  /**
   * PUNTOS QUE GANA QUIEN RETÓ cuando el reto aceptado se vence sin jugarse.
   * `null` = sin premio. Al otro no se le resta nada: quién de los dos falló
   * no se sabe, y cobrarle a quien tal vez sí fue sería cobrar por una culpa
   * que nadie comprobó.
   */
  rewardUnplayed: number | null;
  /**
   * PUNTOS QUE SE RESTAN CADA SEMANA a quien no juega retos. `null` = nada.
   *
   * ES EL PARÁMETRO MÁS PELIGROSO: lo único que le baja puntos a alguien sin
   * que esa persona haya hecho nada, ni siquiera dejar vencer un reto. Tiene
   * tres salvedades —quien recién entró, quien jugó esta semana, y quien tiene
   * un reto abierto esperando a otro— y nunca cobra hacia atrás.
   */
  inactivityPenalty: number | null;
  /**
   * A cuántas posiciones hacia ARRIBA se puede retar. `null` = sin límite.
   *
   * Cero es distinto de `null` y es una configuración válida: «no se puede
   * retar hacia arriba». Hacia abajo nunca hubo límite —retar a alguien peor
   * ya vale casi nada— y sigue sin haberlo.
   */
  positionLimit: number | null;
}

/**
 * LOS VALORES CON LOS QUE UN CLUB SE COMPORTA COMO ANTES DE QUE ESTO EXISTIERA.
 *
 * Existe en el CONTRATO y no solo en el servidor porque lo usan tres lados que
 * si no escribirían el mismo objeto a mano: el dominio del servidor, la caja
 * de configuración —que lo necesita cuando un servidor viejo no manda el
 * bloque— y las pruebas de la web. Escrito a mano en tres lados, agregar un
 * parámetro rompe los tres y hay que acordarse de los tres; acá, el que se
 * olvida deja de compilar en un solo archivo.
 */
export const CHALLENGE_SETTINGS_OFF: ChallengeSettings = {
  enabled: true,
  resetDays: [],
  perReset: 1,
  repeatAfter: 0,
  acceptDays: null,
  playDays: null,
  penaltyUnanswered: null,
  rewardUnplayed: null,
  inactivityPenalty: null,
  positionLimit: null,
};

/**
 * Los topes de lo que se puede escribir en el formulario. No son reglas del
 * juego: existen para que un cero mal puesto o un 999 no dejen un club en un
 * estado del que después hay que sacarlo a mano.
 */
export const CHALLENGE_SETTING_LIMITS = {
  /** Uno es el mínimo útil: cero sería «nadie puede retar», y para eso está `enabled`. */
  perResetMin: 1,
  perResetMax: 20,
  repeatAfterMin: 0,
  repeatAfterMax: 20,
  positionLimitMin: 0,
  positionLimitMax: 200,
  /** Un día es el mínimo: medio día no se puede escribir y no se entendería. */
  daysMin: 1,
  /** Noventa: más que eso no es un plazo, es no tener plazo con más pasos. */
  daysMax: 90,
  /**
   * Los puntos de una penalidad. Uno es el mínimo con sentido —cero sería no
   * tenerla, y para eso está la casilla— y cien es más de lo que vale ganar un
   * torneo: una penalidad más grande que un torneo no castiga, borra a
   * alguien de la tabla.
   */
  penaltyMin: 1,
  penaltyMax: 100,
} as const;

/**
 * `PUT /communities/:id/challenge-settings`.
 *
 * TODOS OPCIONALES y se manda solo lo que cambió: con dos personas
 * administrando el mismo club, mandar los cinco siempre haría que quien toca
 * uno pise sin querer lo que la otra acaba de cambiar. Es el mismo trato que
 * `SetMatchSettingsRequest`.
 *
 * `positionLimit` acepta `null` EXPLÍCITO para borrar el límite, que es
 * distinto de no mandarlo —que lo deja como estaba—.
 */
export interface SetChallengeSettingsRequest {
  enabled?: boolean;
  resetDays?: number[];
  perReset?: number;
  repeatAfter?: number;
  /** `null` explícito quita el plazo; ausente lo deja como estaba. */
  acceptDays?: number | null;
  playDays?: number | null;
  penaltyUnanswered?: number | null;
  rewardUnplayed?: number | null;
  inactivityPenalty?: number | null;
  positionLimit?: number | null;
}

/** `POST /tournaments/:id/accounts` — una cuenta a donde depositar. */
export interface AddAccountRequest {
  number: string;
  description: string;
}

/**
 * `PUT /tournaments/:id/rules` — el reglamento de ESTE torneo.
 *
 * Vacío NO lo borra: lo devuelve al del club. Es la diferencia entre «este
 * torneo no tiene reglas» —que no existe— y «este torneo se juega con las de
 * la casa», que es de donde salió y a donde tiene que poder volver.
 */
export interface UpdateTournamentRulesRequest {
  rules: string;
}
export const SPONSOR_IMAGE_FIELD = 'image';
/** El campo del formulario con la foto de un partido. */
export const MATCH_PHOTO_FIELD = 'photo';
/**
 * EL MISMO NOMBRE DE CAMPO PARA LA FOTO DE UN RETO.
 *
 * No hay una constante aparte porque no hay dos ideas: es «la foto de este
 * partido» en las dos rutas. Una segunda constante con el mismo valor sería
 * el lugar donde un día uno de los dos cambia y el otro no.
 */

export const TOURNAMENT_BANNER_FIELD = 'banner';

// ----------------------------------------------------------------------

export interface CreateCommunityFields {
  name: string;
  description: string;
  locationText: string;
  /** Optional. An empty string and an absent field both mean "no group". */
  whatsappGroupUrl?: string;
  /** Optional. El reglamento que aplica a todos sus torneos. */
  tournamentRules?: string;
}

/**
 * `PUT /communities/:id/details`
 *
 * La ficha del club: de qué se trata, dónde se juega y el grupo de WhatsApp.
 * Se escribían solo al crearlo, y un club que cambió de cancha se quedaba con
 * la ficha del primer día.
 *
 * EL NOMBRE NO ESTÁ, a propósito: cambiarlo cambia el `slug`, y el slug está
 * adentro de los enlaces del club que ya se mandaron por WhatsApp. Renombrar
 * un club tiene consecuencias afuera del producto y merece su propio camino.
 */
export interface UpdateCommunityDetailsRequest {
  description: string;
  locationText: string;
  /** Vacío o ausente lo borra. Tiene que ser un enlace de WhatsApp. */
  whatsappGroupUrl?: string;
}

export interface UpdateCommunityRulesRequest {
  tournamentRules: string;
}

/**
 * `PUT /communities/:id/match-settings`.
 *
 * Los dos campos son OPCIONALES y se manda solo el que cambió: con dos
 * personas administrando el mismo club, mandar los dos siempre haría que
 * quien apaga uno pise sin querer lo que el otro acaba de prender.
 */
export interface SetMatchSettingsRequest {
  allowWhatsappContact?: boolean;
  allowPlayerScores?: boolean;
  celebrateBirthdays?: boolean;
}

/**
 * `PUT /profile/default-community`.
 *
 * `null` lo quita. Tiene que ser un club donde la persona esté activa: la
 * pestaña entra directo a sus torneos.
 */
export interface SetDefaultCommunityRequest {
  communityId: string | null;
}

/**
 * `PUT /profile/theme`.
 *
 * Su propio camino, como la ubicación y el club por defecto: el tema se
 * elige en «Más» —una pantalla que no carga el perfil entero— y guardar el
 * formulario del perfil no puede pisarlo.
 */
export interface SetThemeRequest {
  theme: Theme;
}

/**
 * `PUT /communities/:id/auto-approve`
 *
 * Los dos viajan juntos y ninguno es opcional: mandar solo uno dejaría al
 * otro en lo que el servidor adivine, y «adivinar» acá es decidir quién entra
 * a un club sin que nadie lo mire.
 */
export interface SetAutoApproveRequest {
  autoApproveMembers: boolean;
  autoApproveWithPoints: boolean;
}

export const COMMUNITY_LOGO_FIELD = 'logo';
export const AVATAR_FIELD = 'photo';

export interface ReviewCommunityRequest {
  /** Required to reject (the creator is owed a reason), optional to approve. */
  note?: string;
}

export interface SetTournamentPermissionRequest {
  allowed: boolean;
  note?: string;
}

// ---------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------

// ---------------------------------------------------------------------
// Torneos
// ---------------------------------------------------------------------

/**
 * DRAFT es donde se arma: se inscribe gente y el cuadro se puede reordenar
 * arrastrando. RUNNING es «ya se tiró»: el orden queda fijo, porque a partir
 * de ahí hay partidos jugados contra ese orden.
 */
export const TOURNAMENT_STATUSES = ['DRAFT', 'RUNNING', 'FINISHED'] as const;
export type TournamentStatus = (typeof TOURNAMENT_STATUSES)[number];

export const TOURNAMENT_LIMITS = {
  nameMin: 3,
  nameMax: 120,
  descriptionMax: 1000,
  /**
   * EL REGLAMENTO, LARGO DE VERDAD.
   *
   * Estaba en 4 000 y a Javier le quedó corto: un reglamento de club con
   * formato, tiempos de espera, conducta y sanciones pasa fácil de eso, y al
   * llegar al tope el campo deja de aceptar letras SIN DECIR NADA — se sigue
   * escribiendo y no aparece nada, que es la peor forma de avisar.
   *
   * «SIN LÍMITE» NO EXISTE en un campo que llega por la red. Lo que existe es
   * un tope que nadie alcanza escribiendo: 200 000 caracteres son unas cien
   * páginas. Sin ningún tope, el primero que pegue un archivo de 50 MB queda
   * guardado para siempre en la columna, y eso no es una función, es un
   * agujero.
   */
  rulesMax: 200_000,
  /** Cuántas categorías puede tomar una misma persona en un torneo. */
  maxCategoriesPerPlayer: 2,
} as const;

/** Los tamaños de cuadro. Siempre una potencia de dos. */
export const BRACKET_SIZES = [2, 4, 8, 16, 32, 64, 128] as const;
export type BracketSize = (typeof BRACKET_SIZES)[number];
export const MAX_PLAYERS_PER_CATEGORY = 128;

/**
 * LA PAREJA DE UNA INSCRIPCIÓN DE DOBLES.
 *
 * Es un objeto anidado y no cuatro campos sueltos `partnerAlgo` al lado de los
 * demás, y la diferencia no es de estilo: así «¿esta inscripción es de dobles
 * con pareja?» es UNA pregunta con UNA respuesta —`partner !== null`— en vez
 * de cuatro campos que pueden contradecirse entre sí. Con campos sueltos
 * existe el estado «hay nombre de pareja pero no hay pareja», y ese estado
 * dibuja media pareja en el cuadro.
 *
 * `userId` nulo es un INVITADO, igual que en la inscripción: alguien que llegó
 * a jugar y no tiene la aplicación.
 */
export interface EntryPartner {
  userId: string | null;
  displayName: string;
  /** Solo el primer nombre, por lo mismo que `TournamentEntry.firstName`. */
  firstName: string;
  avatarPath: string | null;
}

export interface TournamentEntry {
  id: string;
  /**
   * QUIÉN JUEGA, SI TIENE CUENTA. `null` es un INVITADO: alguien que llegó a
   * jugar y no tiene la aplicación, inscrito por el club solo con su nombre.
   *
   * Que sea nulo y no una cadena vacía es a propósito. Una cadena vacía es
   * una promesa falsa —el tipo dice que hay un usuario— y es cómo un vacío
   * termina comparándose por igualdad contra el id de quien mira y dando
   * `true` el día que alguien pasa un valor vacío por el otro lado.
   *
   * NUNCA SE USA PARA AGRUPAR DIRECTAMENTE. Ver `personKeyOf`.
   */
  userId: string | null;
  displayName: string;
  /**
   * SOLO EL PRIMER NOMBRE, no el nombre legal completo.
   *
   * En un cuadro hay tres «Beto» y el apodo no alcanza para saber cuál es
   * cuál. El primer nombre resuelve eso con lo mínimo: el cuadro es público
   * —se comparte por WhatsApp, se imprime y se pega en la pared del club— y
   * publicar «Roberto Solís Mora» ahí es más de lo que hace falta para
   * distinguir a dos personas.
   */
  firstName: string;
  avatarPath: string | null;
  /** En qué categoría del torneo está anotada. */
  level: CategoryLevel;
  /**
   * SU CATEGORÍA EN EL CLUB, que no es la misma pregunta que `level`.
   *
   * `level` es dónde está anotada; esto es qué categoría le asignó el club.
   * Con el torneo cerrado son la misma o una de diferencia, porque la regla
   * no deja más. Con el torneo ABIERTO pueden ser cualquier par, y ahí esta
   * diferencia es lo único que le avisa a quien organiza que un A se anotó
   * en C.
   *
   * `null` = NO ES SOCIA DE ESE CLUB, que no es lo mismo que «no tiene
   * nivel». Un invitado sin cuenta también da `null`.
   *
   * VIAJA SOLO DONDE HACE FALTA DECIDIR —la ficha del torneo—; en las vistas
   * públicas llega en `null` sin que eso signifique nada. Es la misma idea
   * que el comprobante: lo que no tiene por qué estar, no está.
   */
  assignedLevel: CategoryLevel | null;
  /** El cuadro en el que juega. `MIXED` solo en dobles. */
  sex: CategorySex;
  discipline: CategoryDiscipline;
  /**
   * CON QUIÉN JUEGA. `null` en singles, y nunca nulo en dobles.
   *
   * LA INSCRIPCIÓN ES DE LA PAREJA, NO DE CADA UNO. Es una sola fila con dos
   * personas adentro, y no dos filas que alguien tiene que acordarse de
   * emparejar: dos filas es cómo se llega a un cuadro donde uno de los dos fue
   * aceptado y el otro no.
   */
  partner: EntryPartner | null;
  /** Su posición de siembra dentro de la categoría, empezando en 1. */
  seed: number;
  /** `true` si esta es su SEGUNDA categoría en este torneo: paga distinto. */
  isSecondCategory: boolean;
  /** Cómo llegó el comprobante de pago, o que todavía no llegó. */
  paymentState: EntryPaymentState;
  /**
   * CÓMO DICE QUE PAGÓ. `null` si todavía no dijo nada.
   *
   * Aparte de `paymentState`, que contesta otra cosa: aquello es «¿llegó
   * comprobante?» —lo sabe el sistema mirando si hay foto o texto— y esto es
   * «¿por dónde dice que pagó?», que solo lo sabe la persona.
   */
  paymentMethod: EntryPaymentMethod | null;
  /** Qué decidió el club sobre ese pago. */
  reviewState: EntryReviewState;
  /**
   * Por qué el club rechazó el pago, cuando lo rechazó.
   *
   * LO VE QUIEN SE INSCRIBIÓ, y esa es toda la razón de que exista: un «no»
   * sin motivo deja a la persona sin saber si mandar otra foto, escribir un
   * comentario o llamar por teléfono.
   */
  reviewNote: string | null;
  /**
   * `true` si la inscripción la hizo quien organiza y no la persona misma.
   *
   * Va APARTE del estado de pago y no como un cuarto valor de él porque son
   * dos preguntas distintas: «¿pagó?» y «¿quién la anotó?». Alguien a quien
   * metió quien organiza puede mandar la foto después, y si fueran el mismo
   * campo esa foto borraría el dato de quién lo anotó.
   */
  addedByOrganizer: boolean;
  /** El comentario del comprobante, si lo hay. Solo lo ve quien organiza. */
  proofComment: string | null;
  /** La imagen del comprobante. Solo la ve quien organiza. */
  proofImagePath: string | null;
  /**
   * ISO 8601: cuándo se cae sola por falta de comprobante. `null` si no vence
   * —porque ya tiene comprobante, o porque la anotó quien organiza—.
   */
  expiresAt: string | null;
  /** `true` si es un invitado: juega, pero no tiene cuenta en la aplicación. */
  isGuest: boolean;
}

/**
 * «¿SON LA MISMA PERSONA?», que NO es lo mismo que comparar `userId`.
 *
 * Casi todo lo que el sistema hace con el `userId` de una inscripción es esa
 * pregunta: cuántas categorías tomó alguien, si esta es su segunda —que paga
 * distinto—, cuánta gente hay inscrita, cuánto se le cobra al club. Con los
 * invitados, comparar `userId` a secas contesta que TODOS son la misma
 * persona, porque todos son `null`, y eso no da un error: da una factura mal
 * hecha y un cobro de segunda categoría a alguien que se inscribió una vez.
 *
 * Un invitado es su propia inscripción y nada más. No hay forma de saber que
 * el «Marco» del cuadro A y el «Marco» del cuadro B son el mismo señor —en un
 * club hay dos Marcos— y adivinarlo por el nombre sería inventar una
 * identidad que nadie confirmó. La consecuencia es que un invitado ocupa UNA
 * categoría; quien organiza y lo quiera en dos, lo inscribe dos veces.
 *
 * El prefijo evita el otro choque, más difícil de ver: sin él, una
 * inscripción cuyo `id` fuera igual al `userId` de otra las volvería la misma
 * persona. Son dos espacios de uuid distintos y no tienen por qué no cruzarse
 * nunca.
 */
export function personKeyOf(entry: { id: string; userId: string | null }): string {
  return entry.userId ?? `invitado:${entry.id}`;
}

/**
 * CÓMO LLEGÓ EL COMPROBANTE DE PAGO.
 *
 * `IMAGE` la foto del SINPE o el depósito; `COMMENT` la palabra escrita
 * («pagué en efectivo el martes»); `PENDING` todavía nada.
 *
 * El comentario VALE IGUAL que la imagen a propósito: en un club de barrio la
 * mitad de los pagos son en la mano, y exigir la foto de algo que no existe
 * convertiría el comprobante en un trámite imposible de cumplir. Que sean
 * estados distintos y no un solo «pagó» es lo que le permite a quien organiza
 * mirar la lista y saber cuáles tiene que verificar de otra manera.
 */
export const ENTRY_PAYMENT_STATES = ['IMAGE', 'COMMENT', 'PENDING'] as const;
export type EntryPaymentState = (typeof ENTRY_PAYMENT_STATES)[number];

/**
 * CÓMO DICE QUE PAGÓ.
 *
 * Es una DECLARACIÓN, no un hecho: lo elige quien se inscribe o quien lo
 * inscribe, y el club después lo da por bueno o no (ver `EntryReviewState`).
 *
 *  · `SINPE` — SINPE Móvil, el medio de casi todo el mundo acá.
 *  · `TRANSFER` — transferencia bancaria.
 *  · `COMMENT` — pagó de otra forma y lo explica escribiendo: en efectivo el
 *    martes, se lo descuentan de otra cosa, lo que sea. En un club de barrio
 *    la mitad de los pagos son así, y no dejarlos declarar convertiría el
 *    comprobante en un trámite imposible de cumplir.
 *  · `SPONSOR` — lo paga un patrocinador. No debe plata y no es que no pagó.
 *  · `ORGANIZER` — lo metió el club sin cobrarle todavía. Es lo que pasa
 *    cuando alguien llega a la cancha y se anota en el momento.
 *
 * `null` significa QUE TODAVÍA NO DIJO NADA, que no es lo mismo que ninguna
 * de estas cinco.
 */
export const ENTRY_PAYMENT_METHODS = [
  'SINPE',
  'TRANSFER',
  'COMMENT',
  'SPONSOR',
  'ORGANIZER',
] as const;
export type EntryPaymentMethod = (typeof ENTRY_PAYMENT_METHODS)[number];

/**
 * QUÉ DECIDIÓ EL CLUB SOBRE ESE PAGO.
 *
 * `PENDING` nadie lo miró todavía; `APPROVED` el club lo dio por bueno;
 * `REJECTED` el club dice que no —comprobante falso, texto sin sentido, o
 * simplemente no llegó la plata—.
 *
 * RECHAZAR NO BORRA LA INSCRIPCIÓN, y es a propósito. Borrarla dejaría a la
 * persona sin saber qué pasó y sin forma de arreglarlo: la fila se queda,
 * marcada, y quien se inscribió puede mandar otro comprobante. Sacar a alguien
 * del torneo es otra operación, con otro botón, y con el cuadro delante.
 */
export const ENTRY_REVIEW_STATES = ['PENDING', 'APPROVED', 'REJECTED'] as const;
export type EntryReviewState = (typeof ENTRY_REVIEW_STATES)[number];

/**
 * CUÁNTO AGUANTA UNA INSCRIPCIÓN SIN COMPROBANTE.
 *
 * Una hora, y lo pidió Javier. Resuelve un problema concreto: los cupos se
 * llenan de gente que se anota «por si acaso» y nunca paga, y quien sí iba a
 * pagar se queda afuera. Sesenta minutos alcanzan para hacer el SINPE y sacarle
 * la foto, y no tanto como para retener un cupo toda la tarde.
 *
 * En minutos y no en milisegundos porque es lo que se le dice a la gente:
 * el número que aparece en pantalla y el que usa el servidor son EL MISMO,
 * y así no pueden separarse.
 */
export const ENTRY_PROOF_MINUTES = 60;

/**
 * El estado de pago a partir de lo que hay guardado.
 *
 * Vive en el contrato —y no en el servidor ni en la pantalla— porque los dos
 * lados lo necesitan: el servidor para responder, la web para pintar el ícono
 * de una inscripción recién hecha sin volver a preguntar. Dos copias de esta
 * decisión serían dos listas que a veces dicen cosas distintas de la misma
 * persona.
 */
export function paymentStateOf(entry: {
  proofImagePath: string | null;
  proofComment: string | null;
}): EntryPaymentState {
  // La imagen manda sobre el comentario cuando están los dos: es la que se
  // puede verificar.
  if (entry.proofImagePath !== null) return 'IMAGE';
  if (entry.proofComment !== null && entry.proofComment.trim() !== '') return 'COMMENT';
  return 'PENDING';
}

/**
 * `POST /tournaments/:id/entries/:entryId/review` — el club decide.
 *
 * `approve` y no un `state` con los tres valores: volver a «pendiente» no es
 * una decisión que alguien quiera tomar, y ofrecerla sería un botón que no
 * arregla nada. Quien se equivocó aprobando, rechaza; quien se equivocó
 * rechazando, aprueba.
 */
export interface ReviewEntryRequest {
  approve: boolean;
  /**
   * Por qué. Opcional al aprobar —no hace falta explicar un sí— y es lo que
   * hace útil un no.
   */
  note?: string;
}

/** Cuánto puede escribir el club al rechazar un pago. */
export const ENTRY_REVIEW_NOTE_MAX = 300;

/** El comentario del comprobante: `POST /tournaments/:id/entries/:entryId/proof`. */
export interface SetEntryProofRequest {
  comment: string;
}

/** El campo del formulario con la imagen del comprobante. */
export const ENTRY_PROOF_FIELD = 'proof';

/**
 * CUÁNDO Y DÓNDE SE JUEGA UN PARTIDO. Todo opcional.
 *
 * LA FECHA Y LA HORA VAN SEPARADAS. Un instante necesita zona horaria, y
 * guardar «sábado 3 pm» como instante es cómo un partido termina apareciendo
 * a las 9 de la mañana en el teléfono de alguien. Acá el día es un día
 * (`YYYY-MM-DD`) y la hora es `HH:MM`, que es lo que se dice en la cancha.
 *
 * `place` es el lugar («Cancha del Liceo») y `court` la cancha dentro de ese
 * lugar («Cancha 2»): en un club con cuatro canchas hacen falta las dos, y en
 * uno con una sola sobra la segunda.
 */
export interface MatchSchedule {
  date: string | null;
  time: string | null;
  place: string | null;
  court: string | null;
  notes: string | null;
}

export const EMPTY_SCHEDULE: MatchSchedule = {
  date: null,
  time: null,
  place: null,
  court: null,
  notes: null,
};

/** `PUT /tournaments/:id/matches/:matchId/schedule` */
export interface SetMatchScheduleRequest extends MatchSchedule {
  /**
   * Copiar LUGAR, CANCHA y DESCRIPCIÓN a todos los demás partidos del torneo.
   *
   * La fecha y la hora NO se copian, y es a propósito: dos partidos a la
   * misma hora en la misma cancha no se pueden jugar. Lo que se repite es lo
   * que de verdad se repite —el lugar es el mismo todo el día— y lo que
   * cambia se escribe partido por partido.
   */
  applyPlaceToAll?: boolean;
}

/**
 * La fecha límite de una ronda.
 *
 * `round` es cuánta gente queda al empezarla: 2 la final, 4 la semifinal, 8
 * los cuartos. Es el mismo número que usa el cuadro, para no tener dos formas
 * de nombrar la misma ronda.
 */
/**
 * DE QUÉ TAMAÑO ES ESA RONDA: la de 64, la de 32, los cuartos…
 *
 * LAS DOS FORMAS DE NOMBRAR UNA RONDA, Y POR QUÉ HACEN FALTA LAS DOS. Un
 * partido guarda su ronda como un NÚMERO DE ORDEN —1 es la primera que se
 * juega— porque es lo que hace avanzar el cuadro. Una fecha límite se guarda
 * por TAMAÑO —«la ronda de 32»— porque es como habla la gente y como sale
 * impreso en el cuadro. Son la misma ronda dicha de dos maneras.
 *
 * SE CALCULA DESDE EL FINAL Y NO DESDE EL PRINCIPIO: la última ronda de
 * cualquier cuadro es la final, que son 2. De ahí para atrás cada ronda dobla.
 * Contando desde el principio habría que saber el tamaño del cuadro, que
 * cambia con los byes.
 *
 * RECIBE LOS PARTIDOS DE ESA CATEGORÍA Y NADA MÁS. Con los de todo el torneo
 * adentro, el «máximo» sería el del cuadro más grande y toda categoría chica
 * quedaría con tamaños inventados —un cuartos de final llamado «ronda de 64»—.
 */
export function roundSizeOf(
  categoryMatches: readonly { round: number }[],
  round: number,
): number {
  const última =
    categoryMatches.length === 0 ? round : Math.max(...categoryMatches.map((m) => m.round));
  return 2 ** (Math.max(última, round) - round + 1);
}

export interface RoundDeadline {
  level: CategoryLevel;
  /** El cuadro en el que juega. `MIXED` solo en dobles. */
  sex: CategorySex;
  discipline: CategoryDiscipline;
  round: number;
  /** `YYYY-MM-DD`. Fecha civil: «antes del domingo» no es «a las 00:00». */
  deadline: string;
}

/** `PUT /tournaments/:id/round-deadline`. `deadline: null` la borra. */
export interface SetRoundDeadlineRequest {
  level: CategoryLevel;
  /** El cuadro en el que juega. `MIXED` solo en dobles. */
  sex: CategorySex;
  discipline: CategoryDiscipline;
  round: number;
  deadline: string | null;
}

/**
 * CÓMO SE JUEGA UN CUADRO.
 *
 * `KNOCKOUT` es lo de siempre: el que pierde se va.
 *
 * Los otros tres son tabla, y existen por una razón concreta: con pocos
 * inscritos, perder el primer partido y volverse a la casa después de manejar
 * una hora es lo que hace que la gente no vuelva. En una tabla todos juegan
 * varios partidos.
 *
 *  · `ROUND_ROBIN` — una sola tabla, todos contra todos. Gana el primero.
 *  · `TWO_GROUPS_SEMIS` — dos tablas; pasan DOS de cada una a semifinales
 *    cruzadas (1°A–2°B y 1°B–2°A) y de ahí a la final.
 *  · `TWO_GROUPS_FINAL` — dos tablas; el primero de cada una va derecho a la
 *    final. Es el formato de una tarde.
 *  · `FINALS` — el de las ATP Finals: OCHO, ni uno más ni uno menos, en dos
 *    tablas de cuatro, y de ahí a semifinales cruzadas. Por fuera es igual a
 *    `TWO_GROUPS_SEMIS`; lo que cambia es QUIÉN ENTRA —por invitación según
 *    el ranking del club, no por inscripción abierta— y EL DESEMPATE, que
 *    sigue el reglamento de la ATP al pie de la letra. Ver `compareAtp`.
 */
export const TOURNAMENT_FORMATS = [
  'KNOCKOUT',
  'ROUND_ROBIN',
  'TWO_GROUPS_SEMIS',
  'TWO_GROUPS_FINAL',
  'FINALS',
] as const;
export type TournamentFormat = (typeof TOURNAMENT_FORMATS)[number];

/** Cuántos grupos usa cada formato. `KNOCKOUT` no usa ninguno. */
export function groupCountOf(format: TournamentFormat): number {
  if (format === 'ROUND_ROBIN') return 1;
  if (format === 'TWO_GROUPS_SEMIS' || format === 'TWO_GROUPS_FINAL' || format === 'FINALS') {
    return 2;
  }
  return 0;
}

/** Cuántos pasan de cada grupo a la definición. `0` = no hay definición. */
export function advancingPerGroup(format: TournamentFormat): number {
  if (format === 'TWO_GROUPS_SEMIS' || format === 'FINALS') return 2;
  if (format === 'TWO_GROUPS_FINAL') return 1;
  return 0;
}

/**
 * CUÁNTA GENTE HACE FALTA para que un formato se pueda armar.
 *
 * Vive en el contrato porque la pantalla y el servidor tienen que contestar
 * lo mismo: la pantalla apaga el botón de «tirar» y el servidor rechaza la
 * petición, y si cada uno usara su propio número habría un botón encendido
 * que siempre falla, o uno apagado sin motivo.
 *
 * Dos es el mínimo de todo: sin dos personas no hay partido. Con dos tablas
 * hacen falta cuatro, porque con tres alguien queda solo en su grupo.
 */
export function minimumEntriesFor(format: TournamentFormat): number {
  // Las Finals son OCHO exactas: es la definición del formato, no un mínimo
  // cómodo. Con siete hay un grupo de tres y alguien juega dos partidos
  // mientras el resto juega tres, que es justo lo que el reglamento de la ATP
  // se toma el trabajo de evitar.
  if (format === 'FINALS') return FINALS_PLAYERS;
  const grupos = groupCountOf(format);
  return grupos <= 1 ? 2 : grupos * 2;
}

/** Los ocho de las Finals. Ni uno más: `maximumEntriesFor` lo hace cumplir. */
export const FINALS_PLAYERS = 8;

/**
 * LOS PLAZOS DE FÁBRICA de las tandas de invitación: 7, 3 y 2 días.
 *
 * Una semana a los ocho del ranking —que se manda con el torneo todavía lejos
 * y a los que el club quiere de verdad—, tres días a los siguientes, y dos de
 * la tercera tanda en adelante, porque el torneo ya viene encima.
 *
 * SON EL VALOR DE FÁBRICA Y NO UNA REGLA: viven como columnas en la categoría
 * y quien administra los cambia. Esto es lo que se escribe cuando nadie dijo
 * otra cosa, y está acá para que la base, el servidor y la pantalla no tengan
 * cada uno su propio 7.
 */
export const DEFAULT_INVITE_DAYS = { first: 7, second: 3, rest: 2 } as const;

/**
 * EL TECHO DE UN PLAZO, en días.
 *
 * Noventa. No es una regla deportiva: es que un plazo de mil días es un cupo
 * congelado para siempre, y quien lo escribe casi siempre se equivocó de
 * casilla. El piso es uno por lo mismo del otro lado —cero días es una
 * invitación que nace vencida—.
 */
export const MAX_INVITE_DAYS = 90;

/**
 * CUÁNTOS DÍAS TIENE LA TANDA `n` para contestar. `n` empieza en 1.
 *
 * La primera es la larga —se manda con el torneo lejos y a los que el club
 * quiere de verdad—, la segunda más corta, y de la tercera en adelante todas
 * iguales: con ocho cupos y un ranking de cuarenta puede haber muchas tandas,
 * y darle un plazo propio a cada una sería una tabla de plazos para algo que
 * después de la tercera ya no cambia.
 */
export function inviteDaysFor(
  category: { inviteDaysFirst: number; inviteDaysSecond: number; inviteDaysRest: number },
  round: number,
): number {
  if (round <= 1) return category.inviteDaysFirst;
  if (round === 2) return category.inviteDaysSecond;
  return category.inviteDaysRest;
}

/**
 * EL TOPE DE GENTE, o `null` si no hay.
 *
 * Solo las Finals tienen techo. Todo lo demás crece: un cuadro por llaves se
 * agranda a la potencia de dos siguiente y una tabla admite a quien llegue.
 */
export function maximumEntriesFor(format: TournamentFormat): number | null {
  return format === 'FINALS' ? FINALS_PLAYERS : null;
}

/**
 * Repartir en grupos, SERPENTEANDO.
 *
 * La lista llega ordenada por siembra —el 1 primero—. Repartir por mitades
 * («los cuatro mejores al grupo A») daría un grupo con todos los buenos y
 * otro con todos los demás: el A se mata entre sí y del B pasa cualquiera.
 *
 * Serpenteando —1 al A, 2 al B, 3 al B, 4 al A, 5 al A…— los dos grupos
 * quedan parejos, que es la razón de sembrar. Es el mismo criterio que usa la
 * duplicación del cuadro por llaves para separar al 1 del 2.
 *
 * VIVE EN EL CONTRATO PORQUE LA PANTALLA MUESTRA ESTE REPARTO ANTES DE
 * TIRAR: quien organiza quiere ver quién le toca a cada tabla mientras
 * todavía puede acomodar la siembra. Si la pantalla lo calculara por su
 * cuenta, la vista previa podría poner a alguien en un grupo y el servidor
 * en el otro, y nadie podría explicar la diferencia.
 */
export function snakeIntoGroups<T>(ordered: readonly T[], groups: number): T[][] {
  const salida: T[][] = Array.from({ length: groups }, () => []);
  if (groups < 1) return salida;

  ordered.forEach((item, i) => {
    const vuelta = Math.floor(i / groups);
    const dentro = i % groups;
    // En las vueltas impares se recorre al revés: eso es el serpenteo.
    const grupo = vuelta % 2 === 0 ? dentro : groups - 1 - dentro;
    (salida[grupo] as T[]).push(item);
  });

  return salida;
}

/** `PUT /tournaments/:id/format` */
/**
 * `PUT /tournaments/:id/categories`
 *
 * La lista COMPLETA de las que el torneo corre. Agregar es libre —cada
 * categoría es su propio cuadro y arranca cuando está lista—; quitar una con
 * gente inscrita, o una ya tirada, lo rechaza el servidor diciendo cuál.
 */
/**
 * `PUT /communities/:id/category-request`
 *
 * Pedirle al club pasar a otra categoría. Lo decide el club: hasta que
 * responda, la categoría de la persona sigue siendo la que tenía.
 */
export interface RequestCategoryChangeRequest {
  level: CategoryLevel;
}

/** `POST /communities/:id/members/:userId/category-request` */
export interface ReviewCategoryChangeRequest {
  approve: boolean;
}

export interface SetTournamentCategoriesRequest {
  categories: CommunityCategory[];
}

export interface SetCategoryFormatRequest {
  level: CategoryLevel;
  /** El cuadro en el que juega. `MIXED` solo en dobles. */
  sex: CategorySex;
  discipline: CategoryDiscipline;
  format: TournamentFormat;
}

/**
 * Una categoría DENTRO de un torneo.
 *
 * Es más que la pareja nivel × sexo: cada cuadro tiene su propio momento de
 * arranque, porque se llenan a ritmos distintos. El A masculino junta 16 el
 * jueves y el C2 femenino sigue esperando la cuarta el sábado; antes había
 * que esperar a que TODAS estuvieran listas para tirar el torneo, y el cuadro
 * que ya estaba se quedaba parado por otro que no.
 */
export interface TournamentCategory {
  level: CategoryLevel;
  sex: CategorySex;
  discipline: CategoryDiscipline;
  /**
   * ISO 8601 de cuándo se tiró ESTE cuadro, o `null` si sigue recibiendo
   * gente. Es lo que decide si acepta inscripciones, si se puede sortear y
   * si se pueden anotar marcadores.
   */
  startedAt: string | null;
  /** Cómo se juega. Se puede cambiar mientras el cuadro no se haya tirado. */
  format: TournamentFormat;

  /**
   * LAS INVITACIONES DE LAS FINALS. Solo significan algo con ese formato.
   *
   * `autoInvite` es el check de «invitar sola a la siguiente tanda»: apagado,
   * el barrido vence las invitaciones sin respuesta y ahí se queda; prendido,
   * llama solo a los siguientes del ranking hasta llegar a ocho.
   *
   * Los plazos VIAJAN AL NAVEGADOR porque la pantalla los dice con esas
   * palabras —«tenés siete días»— y son configurables por categoría. Un texto
   * con el número escrito adentro mentiría en el club que los cambió.
   */
  autoInvite: boolean;
  inviteDaysFirst: number;
  inviteDaysSecond: number;
  inviteDaysRest: number;
}

/**
 * UNA INVITACIÓN A LAS FINALS, como la ve la pantalla.
 *
 * LLEVA EL NOMBRE Y LA FOTO porque la lista de quien administra es de
 * personas, no de identificadores: «invitaste a Ana, a Beto y a Caro» se lee;
 * tres UUID no.
 *
 * `invitedByName` en `null` significa que la mandó el barrido automático —no
 * que no se sepa quién—: es la misma distinción que en los ajustes de puntos.
 */
export interface TournamentInvitation {
  id: string;
  level: CategoryLevel;
  sex: CategorySex;
  discipline: CategoryDiscipline;
  userId: string;
  displayName: string;
  avatarPath: string | null;
  /** 1 son los ocho del ranking, 2 los siguientes, y así. */
  round: number;
  status: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED';
  /** ISO 8601. Hasta cuándo puede contestar. */
  expiresAt: string;
  /** Quién la mandó, o `null` si la mandó el barrido. */
  invitedByName: string | null;
}

export interface Tournament {
  id: string;
  communityId: string;
  communityName: string;
  name: string;
  description: string | null;
  status: TournamentStatus;
  /** `YYYY-MM-DD`. Fecha civil: un torneo no se define al minuto. */
  startsAt: string;
  /** En colones enteros, el mismo para todas las categorías del torneo. */
  feeColones: number;
  /** La segunda categoría de la misma persona. `null` = lo mismo que la primera. */
  secondCategoryFeeColones: number | null;
  bannerPath: string | null;
  /**
   * EL REGLAMENTO QUE VALE PARA ESTE TORNEO, ya resuelto.
   *
   * Quien lo lee no tiene que saber si es el propio del torneo o el del club:
   * acá llega el que manda. Un torneo nuevo arranca con una copia del club y
   * desde ahí puede apartarse; uno que nunca lo tocó muestra el del club, y
   * lo sigue mostrando el día que el club lo corrija.
   */
  rules: string | null;
  /**
   * SI EL DE ARRIBA ES EL PROPIO DE ESTE TORNEO O EL DEL CLUB.
   *
   * Un booleano y no un segundo campo con el texto: dos textos para el mismo
   * reglamento son dos que un día dicen cosas distintas. La pantalla lo usa
   * para dos cosas que si no serían adivinanzas —decir de dónde salió lo que
   * se está leyendo, y ofrecer «volver al del club» solo cuando hay a dónde
   * volver—.
   */
  rulesAreOwn: boolean;
  categories: TournamentCategory[];
  sponsors: Sponsor[];
  /**
   * A dónde depositar la inscripción. Vacío si el club no puso ninguna.
   *
   * VIAJA CON EL TORNEO y no se pide aparte: la caja del comprobante las
   * necesita en el mismo momento en que se dibuja, y una consulta más para
   * dos renglones de texto es un viaje que se nota justo cuando alguien está
   * apurado por anotarse.
   */
  accounts: PaymentAccount[];
  /** Las fechas límite por ronda y categoría. Vacío si nadie puso ninguna. */
  roundDeadlines: RoundDeadline[];
  /**
   * LA CATEGORÍA DE QUIEN MIRA en el club que corre el torneo, o `null` si no
   * es miembro.
   *
   * Viaja para que el botón de anotarse ofrezca SOLO las categorías en las
   * que esa persona puede jugar: la suya y la de justo arriba. Alguien de C
   * femenino ve «C femenino» y «B femenino», y nada más.
   */
  myLevel: CategoryLevel | null;
  entries: TournamentEntry[];
  /**
   * LAS INVITACIONES A LAS FINALS de este torneo, si tiene alguna.
   *
   * QUIÉN VE QUÉ: quien administra el club las ve todas —es la lista con la
   * que decide si invita a la tanda siguiente—; el resto ve SOLO LAS SUYAS,
   * que es lo que necesita para contestar. Una lista pública de a quién se
   * invitó y quién dijo que no es información del club, no del torneo.
   */
  invitations: TournamentInvitation[];
  /**
   * `true` si quien pregunta puede editarlo.
   *
   * Son DOS caminos y el campo no los distingue a propósito: administrar el
   * club, o haber sido nombrado organizador de ESTE torneo. Para todo lo que
   * es organizar —sortear, anotar, aprobar— dan exactamente lo mismo, y
   * partirlo en dos obligaría a cada pantalla a preguntar por los dos.
   */
  canManage: boolean;
  /**
   * `true` solo si ADMINISTRA EL CLUB, no si es organizador de este torneo.
   *
   * Existe para una sola decisión: quién puede nombrar a otro organizador. Un
   * organizador no puede —si pudiera, el permiso se propagaría solo y el club
   * dejaría de controlar a quién le abrió la puerta—, así que la pantalla
   * necesita distinguir lo que `canManage` mezcla.
   */
  canManageCommunity: boolean;
  /**
   * ¿Se ve desde fuera del club, en la lista de torneos abiertos?
   *
   * No es lo mismo que el enlace público del cuadro: aquel se comparte uno
   * por uno, esto es aparecer en una vitrina que la gente recorre.
   */
  isPublic: boolean;

  /**
   * SI CUALQUIERA DE LA APP SE PUEDE ANOTAR SOLO, sin ser socio del club.
   *
   * NO ES `isPublic` CON OTRO NOMBRE. `isPublic` es QUIÉN LO VE —si sale en
   * la vitrina— y esto es QUIÉN SE ANOTA. Un club puede querer que su torneo
   * interno se vea desde afuera sin que entre gente de afuera, y también lo
   * contrario: uno que no está en la vitrina pero al que entra quien tenga el
   * enlace.
   *
   * CON ESTO PRENDIDO NO MANDA LA CATEGORÍA ASIGNADA: un B se puede anotar en
   * A o en C, y a quien organiza se le marca la inscripción que quedó fuera
   * de su nivel. Ver `enterableCategories`.
   *
   * No cambia que la organización anote a mano a quien quiera: eso nunca
   * dependió de esto.
   */
  openToEveryone: boolean;
}

/** `PUT /communities/:communityId/tournaments/:id/public` */
export interface SetTournamentPublicRequest {
  isPublic: boolean;
}

/**
 * UN TORNEO EN LA LISTA DE ABIERTOS, visto desde fuera de su club.
 *
 * ES OTRO TIPO Y NO UN `Tournament` RECORTADO, por la misma razón que
 * `PublicTournament`: un recorte se hace olvidando un campo, y el campo que
 * se olvida es siempre el que no tenía que salir. Acá lo que no está en este
 * archivo NO PUEDE viajar.
 *
 * QUÉ NO ESTÁ, Y NO ES UN DESCUIDO: quiénes están inscritos, el precio, las
 * reglas del club y los patrocinadores. Esto es una tarjeta para decidir si
 * uno abre el torneo, no el torneo.
 */
export interface PublicTournamentCard {
  id: string;
  name: string;
  communityId: string;
  communityName: string;
  /** `YYYY-MM-DD`. Fecha civil. */
  startsAt: string;
  status: TournamentStatus;
  bannerPath: string | null;
  /** Cuánta GENTE hay inscrita. Personas, no inscripciones. */
  entryCount: number;
  /**
   * Si cualquiera de la app se puede anotar solo.
   *
   * VIAJA EN LA TARJETA PARA DECIDIR A DÓNDE LLEVA. Un torneo abierto lleva a
   * su ficha, donde está el botón de anotarse; uno cerrado lleva al cuadro
   * público, que es lo único que alguien de afuera puede hacer con él.
   */
  openToEveryone: boolean;
}

/**
 * QUÉ HAY EN CADA UNO DE MIS CLUBES, para la pantalla que pregunta «¿de cuál?».
 *
 * POR QUÉ LO CALCULA EL SERVIDOR. «¿Me puedo anotar?» es `enterableCategories`,
 * y esa función necesita el torneo ENTERO: sus categorías, sus inscripciones y
 * mi nivel en ese club. Contestarlo en la pantalla sería bajarse todos los
 * torneos de todos mis clubes para dibujar una lista de tres renglones.
 *
 * Y ES LA MISMA FUNCIÓN, no una cuenta parecida: si esta pantalla dijera «te
 * podés anotar» y la de adentro no ofreciera ningún botón, el club quedaría
 * como el que no deja entrar.
 */
export interface CommunityTournamentsSummary {
  communityId: string;
  /**
   * Torneos que todavía reciben gente: al menos un cuadro sin sortear y el
   * torneo sin terminar. Incluye los que ya empezaron —un torneo «en curso»
   * porque el A masculino arrancó sigue recibiendo en el B femenino—.
   */
  openForEntry: number;
  /** De esos, en cuántos SE PUEDE ANOTAR quien pregunta. */
  canEnter: number;
  /** Con al menos un cuadro sorteado y sin terminar. */
  inProgress: number;
}

export interface CreateTournamentFields {
  name: string;
  description?: string;
  startsAt: string;
  feeColones: number;
  secondCategoryFeeColones?: number | null;
  categories: CommunityCategory[];
  /**
   * Si cualquiera de la app se puede anotar solo. Opcional, y apagado cuando
   * no viene: un torneo abierto es una decisión, no un descuido.
   */
  openToEveryone?: boolean;
  /**
   * EL REGLAMENTO DE ESTE TORNEO, escrito al crearlo.
   *
   * OPCIONAL, Y SIN ÉL EL TORNEO SIGUE AL DEL CLUB — que es lo normal y lo
   * que casi todos quieren. Se escribe acá solo cuando este torneo se juega
   * distinto; después se puede cambiar desde la pantalla del torneo.
   *
   * POR QUÉ ESTÁ EN CREAR Y NO SOLO DESPUÉS. Javier, armando un torneo: «no
   * me deja poner las cuentas bancarias, ni poner el reglamento». Estaban las
   * dos cosas, pero DESPUÉS de crear, y la pantalla no lo decía: quien arma un
   * torneo lo arma de una sentada, y mandarlo a buscar dos secciones más
   * adelante es cómo un torneo queda sin cuentas hasta que alguien pregunta a
   * dónde deposita.
   */
  rules?: string | null;
  /**
   * LAS CUENTAS A DONDE DEPOSITAR, escritas al crear.
   *
   * SI NO VIENEN, SE HEREDAN DEL TORNEO ANTERIOR de ese club — la memoria que
   * pidió Javier—. Si vienen, MANDAN ELLAS: quien se tomó el trabajo de
   * escribirlas al crear no quiere que se le agreguen las de antes encima.
   *
   * UNA LISTA VACÍA NO ES «NO MANDÉ NADA». `undefined` es «no toqué esto,
   * heredá»; `[]` es «este torneo va sin cuentas», que es una decisión válida
   * —un torneo gratis— y tiene que poder expresarse.
   */
  accounts?: AddAccountRequest[];
}

/**
 * Inscribir a alguien, con cuenta o sin ella.
 *
 * O VIENE `userId` O VIENE `guest`, nunca los dos. Son las dos caras de la
 * misma pregunta —«¿a quién inscribo?»— y aceptar ambas obligaría al servidor
 * a elegir una y descartar la otra en silencio, que es como se inscribe a la
 * persona equivocada.
 *
 * El SEXO viaja solo para el invitado: el de quien tiene cuenta sale de su
 * perfil, que es el único lugar donde esa respuesta es suya. Sin perfil no
 * hay de dónde sacarlo, y la categoría es nivel × sexo, así que hay que
 * preguntarlo.
 */
export interface AddTournamentEntryRequest {
  userId?: string;
  guest?: { name: string; sex: Sex; whatsappPhone?: string };
  level: CategoryLevel;
  /**
   * CÓMO PAGÓ, cuando lo elige quien inscribe.
   *
   * Opcional a propósito: quien se anota A SÍ MISMO no lo manda —declara el
   * pago después, con la foto o el comentario, y hasta entonces el reloj
   * corre—. Quien inscribe a OTRO sí, porque en ese momento ya sabe cómo
   * llegó la plata: el que se acercó a la cancha con el SINPE hecho, el que
   * viene por patrocinio, el que todavía no pagó nada.
   */
  paymentMethod?: EntryPaymentMethod;
}

/**
 * `PUT /tournaments/:id` — corregir lo que se escribió al crearlo.
 *
 * LAS CATEGORÍAS NO ESTÁN ACÁ, y no es un olvido: tienen su propia puerta,
 * que sabe que un cuadro ya sorteado no se puede sacar. Repetir esa regla en
 * este formulario sería repetirla; no repetirla sería romperla.
 *
 * Todos los campos son OBLIGATORIOS aunque no se hayan tocado: el formulario
 * manda la ficha entera, y un campo ausente se leería como «borralo» tan bien
 * como «dejalo igual». Con todos presentes no hay ambigüedad.
 */
export interface UpdateTournamentRequest {
  name: string;
  description: string | null;
  /** `YYYY-MM-DD`. Fecha civil, nunca un instante. */
  startsAt: string;
  feeColones: number;
  secondCategoryFeeColones: number | null;
  /**
   * Si cualquiera de la app se puede anotar solo.
   *
   * VIAJA CON LA FICHA Y NO POR UN ENDPOINT APARTE, al revés que `isPublic`.
   * `isPublic` tiene el suyo porque se prende y se apaga desde la vitrina, con
   * un interruptor suelto; esto se decide mientras se arma el torneo, en el
   * mismo formulario donde se ponen el nombre y el precio.
   *
   * OPCIONAL, Y LO QUE NO SE MANDA NO SE CAMBIA. Es la diferencia con crear:
   * al crear, «no vino» significa apagado, porque un torneo abierto es una
   * decisión. Al corregir la ficha no puede significar eso — alguien que
   * arregla una falta de ortografía en el nombre desde un cliente viejo
   * cerraría el torneo sin enterarse.
   */
  openToEveryone?: boolean;
}

/** Ponerle una cuenta de verdad a una inscripción de invitado. */
export interface LinkGuestEntryRequest {
  userId: string;
}

/**
 * REEMPLAZAR A QUIEN OCUPA UNA PLAZA. Sale uno, entra otro, la llave sigue.
 *
 * Es la misma forma que `LinkGuestEntryRequest` y una operación distinta:
 * aquella le pone cuenta a un invitado que no tenía, esta le cambia la persona
 * a una plaza que ya tiene dueño, con partidos jugados y todo. El historial y
 * los puntos de esa plaza pasan a quien entra.
 */
export interface ReplaceEntryPlayerRequest {
  userId: string;
}

/**
 * Sortear una categoría del cuadro.
 *
 * Los cuatro primeros del ranking van a las cuatro esquinas; el resto, al
 * azar. Se puede repetir mientras el torneo esté en borrador —un sorteo que
 * no se puede rehacer obliga a mover doce nombres a mano si alguien se
 * inscribió tarde— y el resultado se GUARDA: no se vuelve a sortear solo.
 */
export interface DrawRequest {
  level: CategoryLevel;
  /** El cuadro en el que juega. `MIXED` solo en dobles. */
  sex: CategorySex;
  discipline: CategoryDiscipline;
}

/** Reordenar el cuadro: los ids de inscripción en el orden nuevo. */
export interface ReseedRequest {
  level: CategoryLevel;
  /** El cuadro en el que juega. `MIXED` solo en dobles. */
  sex: CategorySex;
  discipline: CategoryDiscipline;
  entryIdsInOrder: string[];
}

// --- El cuadro ---------------------------------------------------------

export interface BracketSlot {
  seed: number;
  /** `null` es un lugar vacío: el rival pasa sin jugar. */
  entry: TournamentEntry | null;
}

export interface BracketMatch {
  position: number;
  home: BracketSlot;
  away: BracketSlot;
  /** Quién pasa sin jugar, o `null` si hay partido de verdad. */
  walkover: TournamentEntry | null;
}

// --- El marcador ------------------------------------------------------

/**
 * Los límites de un marcador, tal como se juega acá.
 *
 * `gamesMax: 10` y no 6 ni 7 A PROPÓSITO, y es la decisión que más hay que
 * explicar: el mismo campo guarda un set normal (6-4, 7-5) y un súper tie
 * break (10-8), que es como se resuelve el tercer set en casi todos los
 * torneos de pádel. Con dos tipos de set separados habría que preguntar en
 * cada uno cuál es, y quien anota el resultado en la cancha no está para eso.
 * El precio de esta decisión es que el validador no puede rechazar un 8-3,
 * que no es un set legal de nada; lo dice el comentario para que nadie crea
 * que se le escapó.
 */
export const SCORE_LIMITS = {
  /** Un partido tiene al menos un set. */
  setsMin: 1,
  /** Y como mucho cinco. */
  setsMax: 5,
  gamesMin: 0,
  gamesMax: 10,
} as const;

/** Un set: los games (o los puntos del súper tie break) de cada lado. */
export interface SetScore {
  home: number;
  away: number;
}

/** El marcador completo de un partido, set por set. */
export type MatchScore = SetScore[];

/**
 * Cómo terminó un partido, mirado desde UNA de las dos personas.
 *
 * SIRVE PARA LAS DOS CLASES DE PARTIDO —los de torneo y los retos— y por eso
 * ya no se llama `ChallengeOutcome`. Un partido es un partido: el cara a cara
 * los mezcla, el muro los mezcla, y tener dos tipos para la misma pregunta
 * obliga a traducir de uno al otro en cada frontera.
 *
 * `DRAW` solo existe en los retos AMISTOSOS: lo pidió Javier —«si es amistoso
 * no importa que queden empates, se reparten los puntos»—. Un cuadro de
 * torneo no puede empatar porque alguien tiene que avanzar, y el reto por
 * ranking tampoco, porque es el que ordena la tabla.
 */
export const MATCH_OUTCOMES = ['WON', 'LOST', 'DRAW'] as const;
export type MatchOutcome = (typeof MATCH_OUTCOMES)[number];

/**
 * Por qué un marcador no se puede guardar. `null` = está bien.
 *
 * Códigos y no frases: la frase se traduce en la pantalla, y el servidor no
 * tiene por qué saber en qué idioma la va a leer quien la reciba.
 */
export const SCORE_PROBLEMS = [
  'SETS_OUT_OF_RANGE',
  'GAMES_OUT_OF_RANGE',
  'SET_TIED',
  'NO_WINNER',
  /** El último set lo ganó quien perdió el partido: entonces el partido seguía. */
  'PLAYED_ON_AFTER_WINNING',
] as const;
export type ScoreProblem = (typeof SCORE_PROBLEMS)[number];

/** Lo que se sabe de un marcador válido. */
export interface ScoreOutcome {
  /**
   * Quién ganó el partido. `null` = EMPATE.
   *
   * Solo puede venir nulo si se pidió `allowDraw`, que hoy es únicamente el
   * reto amistoso. Un partido de torneo nunca llega acá con `null`: alguien
   * tiene que avanzar en el cuadro.
   */
  winner: 'home' | 'away' | null;
  setsHome: number;
  setsAway: number;
}

export type ScoreCheck =
  | { ok: true; outcome: ScoreOutcome }
  | { ok: false; problem: ScoreProblem };

/**
 * Comprueba un marcador y dice quién ganó.
 *
 * VIVE EN EL CONTRATO, no en el servidor, porque la pantalla de anotar tiene
 * que rechazar exactamente lo mismo que la API. Con la regla escrita dos
 * veces, la de la pantalla se queda vieja y aparece el peor error posible:
 * un marcador que se puede escribir y no se puede guardar, sin decir por qué.
 * Quien decide sigue siendo el servidor; esto es para decirlo en el acto.
 *
 * Las cuatro reglas, y por qué cada una:
 *
 *  1. Entre 1 y 5 sets. Cero sets no es un partido jugado; seis no existe.
 *  2. Cada número entre 0 y 10. Fuera de ahí es un dedo que resbaló.
 *  3. NINGÚN SET EMPATADO. Un 6-6 no es un set terminado: o se jugó el tie
 *     break y el marcador es 7-6, o se jugó el súper tie break y es otro set.
 *     Aceptarlo obligaría a inventar un desempate en algún lado.
 *  4. EL ÚLTIMO SET LO GANA QUIEN GANA EL PARTIDO. Es la que atrapa el error
 *     de verdad: un 2-1 donde el tercer set lo ganó el que perdió significa
 *     que el partido siguió después de estar decidido, o que alguien anotó
 *     los sets al revés. Sin esto, un marcador tecleado en el orden
 *     equivocado avanza al jugador equivocado.
 */
export function checkScore(
  score: MatchScore,
  /**
   * `allowDraw`: aceptar que nadie gane.
   *
   * APAGADO DE FÁBRICA, y eso importa: un partido de torneo TIENE que dar un
   * ganador, porque alguien avanza en el cuadro. Un empate ahí dejaría una
   * llave sin poder seguir. Solo los retos AMISTOSOS lo prenden — lo pidió
   * Javier: «si es amistoso no importa que queden empates, se reparten los
   * puntos» — y el reto por ranking tampoco, porque es el que ordena la tabla.
   */
  opciones: { allowDraw?: boolean } = {},
): ScoreCheck {
  if (score.length < SCORE_LIMITS.setsMin || score.length > SCORE_LIMITS.setsMax) {
    return { ok: false, problem: 'SETS_OUT_OF_RANGE' };
  }

  for (const set of score) {
    if (!Number.isInteger(set.home) || !Number.isInteger(set.away)) {
      return { ok: false, problem: 'GAMES_OUT_OF_RANGE' };
    }
    if (fueraDeRango(set.home) || fueraDeRango(set.away)) {
      return { ok: false, problem: 'GAMES_OUT_OF_RANGE' };
    }
    if (set.home === set.away) return { ok: false, problem: 'SET_TIED' };
  }

  const setsHome = score.filter((set) => set.home > set.away).length;
  const setsAway = score.length - setsHome;

  if (setsHome === setsAway) {
    if (opciones.allowDraw !== true) return { ok: false, problem: 'NO_WINNER' };
    /*
     * EMPATE, Y ACÁ TERMINA. No se comprueba `PLAYED_ON_AFTER_WINNING`
     * porque esa regla no existe en un empate: dice «el último set lo ganó
     * quien perdió el partido, entonces el partido seguía después de estar
     * decidido», y un empate nunca estuvo decidido. En un 1-2 2-1 el último
     * set lo ganó uno de los dos, siempre, y eso es normal.
     */
    return { ok: true, outcome: { winner: null, setsHome, setsAway } };
  }

  const winner: 'home' | 'away' = setsHome > setsAway ? 'home' : 'away';

  const ultimo = score[score.length - 1];
  if (ultimo === undefined) return { ok: false, problem: 'SETS_OUT_OF_RANGE' };
  const ganoElUltimo = ultimo.home > ultimo.away ? 'home' : 'away';
  if (ganoElUltimo !== winner) return { ok: false, problem: 'PLAYED_ON_AFTER_WINNING' };

  return { ok: true, outcome: { winner, setsHome, setsAway } };
}

function fueraDeRango(value: number): boolean {
  return value < SCORE_LIMITS.gamesMin || value > SCORE_LIMITS.gamesMax;
}

/** «6-4 7-5». Para mostrarlo en una línea. */
export function formatScore(score: MatchScore): string {
  return score.map((set) => `${set.home}-${set.away}`).join(' ');
}

/** El mismo marcador visto desde el otro lado. */
export function flipScore(score: MatchScore): MatchScore {
  return score.map((set) => ({ home: set.away, away: set.home }));
}

export interface CategoryBracket {
  level: CategoryLevel;
  /** El cuadro en el que juega. `MIXED` solo en dobles. */
  sex: CategorySex;
  discipline: CategoryDiscipline;
  size: BracketSize;
  byes: number;
  rounds: number;
  firstRound: BracketMatch[];
  /** Cuando no hay suficiente gente todavía, por qué no se puede armar. */
  problem: string | null;
  entries: TournamentEntry[];
}

// ---------------------------------------------------------------------
// Partidos
// ---------------------------------------------------------------------

/**
 * Un partido del cuadro.
 *
 * `round` cuenta DESDE LA PRIMERA: 1 es la ronda inicial, y la final es la
 * última. `position` es el lugar dentro de esa ronda, empezando en 1. Los dos
 * juntos identifican el partido dentro de la categoría, y son lo que permite
 * saber a qué partido pasa el ganador sin guardar punteros: al de la ronda
 * siguiente, posición `ceil(position / 2)`.
 */
// ---------------------------------------------------------------------
// Lo mínimo para DIBUJAR un partido
// ---------------------------------------------------------------------

/**
 * LO QUE EL CUADRO NECESITA DE UN JUGADOR PARA DIBUJARLO. Nada más.
 *
 * `Match` trae mucho más —el usuario, el estado del pago, el comprobante— y
 * el dibujo no usa nada de eso. Escribir acá el mínimo es lo que permite que
 * la MISMA pantalla dibuje el cuadro de adentro de la aplicación y el
 * público, sin que el público tenga que fabricar un `TournamentEntry` a medio
 * llenar para conformar al compilador. Un tipo a medio llenar es una promesa
 * falsa: dice que hay un `userId` cuando hay una cadena vacía.
 *
 * `firstName` y `avatarPath` van opcionales porque en la vista pública NO
 * VIAJAN: el nombre legal y la cara de la gente no salen de la aplicación.
 */
export interface DrawableEntry {
  id: string;
  displayName: string;
  seed: number;
  firstName?: string;
  avatarPath?: string | null;
}

/** Lo que el cuadro necesita de un partido. `Match` lo cumple; `PublicMatch` también. */
export interface DrawableMatch {
  id: string;
  level: CategoryLevel;
  /** El cuadro en el que juega. `MIXED` solo en dobles. */
  sex: CategorySex;
  discipline: CategoryDiscipline;
  round: number;
  position: number;
  home: DrawableEntry | null;
  away: DrawableEntry | null;
  score: MatchScore;
  winnerEntryId: string | null;
  noShowEntryId: string | null;
  schedule: { date: string | null; time: string | null; place: string | null; court: string | null };
  /** Opcionales: la vista pública no los manda, y el dibujo se arregla sin ellos. */
  photoPath?: string | null;
  contacts?: readonly MatchContact[];
  canScore?: boolean;
  /**
   * ¿QUIEN MIRA PUEDE BORRAR EL MARCADOR DE ESTE PARTIDO?
   *
   * OTRA PREGUNTA QUE `canScore`, y más estrecha. Anotar lo puede quien
   * organiza y —si el club lo permite— quien jugó. Borrar NO: deja el partido
   * abierto y, si había avanzado alguien, se lleva la ronda siguiente por
   * delante. Solo quien administra el club del torneo, o la plataforma. Ver
   * `borrar-marcador.ts` en el servidor.
   *
   * Opcional como `canScore` y por lo mismo: la vista pública no lo manda.
   */
  canClearScore?: boolean;
}

// ---------------------------------------------------------------------
// El cuadro público
// ---------------------------------------------------------------------

/**
 * `GET /public/tournaments/:id` — SIN SESIÓN.
 *
 * PARA QUÉ EXISTE. El cuadro de un torneo se comparte por WhatsApp y lo abre
 * gente que no tiene cuenta: familiares, el otro club, el que todavía no se
 * registró. Con la vista de adentro, ese enlace llevaba a una pantalla de
 * ingreso, y quien la ve piensa que el enlace estaba roto.
 *
 * ES OTRO TIPO Y NO UN `Tournament` RECORTADO, a propósito. Un recorte se
 * hace olvidando un campo, y el campo que se olvida es siempre el que no
 * tenía que salir. Acá lo que no está en este archivo NO PUEDE viajar: el
 * mapeador no compila si intenta poner un teléfono, porque no hay dónde.
 *
 * QUÉ NO ESTÁ, Y NO ES UN DESCUIDO: el nombre legal, el teléfono, la foto de
 * perfil, el id de usuario, el estado del pago, el comprobante y las fechas
 * de vencimiento de las inscripciones.
 */
export interface PublicEntry {
  id: string;
  /** El apodo, que es como se le dice a la persona en el club. */
  displayName: string;
  seed: number;
}

export interface PublicMatch {
  id: string;
  level: CategoryLevel;
  /** El cuadro en el que juega. `MIXED` solo en dobles. */
  sex: CategorySex;
  discipline: CategoryDiscipline;
  round: number;
  position: number;
  home: PublicEntry | null;
  away: PublicEntry | null;
  score: MatchScore;
  winnerEntryId: string | null;
  noShowEntryId: string | null;
  group: number | null;
  /**
   * Cuándo y dónde. SIN `notes`: la descripción larga la escribe quien
   * organiza para los que juegan —«traigan bolas», «el portón de atrás»— y no
   * para internet.
   */
  schedule: { date: string | null; time: string | null; place: string | null; court: string | null };
}

export interface PublicTournament {
  id: string;
  name: string;
  communityName: string;
  status: TournamentStatus;
  startsAt: string;
  /**
   * LOS PATROCINADORES SÍ VIAJAN, y son lo único de esta respuesta que está
   * puesto para que se VEA y no para informar. Son quienes pagan el torneo, y
   * el enlace público es donde más gente lo abre: dejarlos afuera de la
   * pantalla que más se comparte sería el peor lugar donde ahorrarse un
   * campo. No son datos de nadie — son marcas que pidieron aparecer.
   */
  sponsors: Sponsor[];
  /** Solo las categorías YA TIRADAS: una previsión mirada de afuera se lee como definitiva. */
  categories: TournamentCategory[];
  roundDeadlines: RoundDeadline[];
  matches: PublicMatch[];
}

/**
 * A QUIÉN SE LE PUEDE ESCRIBIR PARA ACORDAR ESTE PARTIDO.
 *
 * Un torneo de club se juega poniéndose de acuerdo por WhatsApp: «¿te sirve
 * el jueves a las 7?». Sin esto, quien tiene que jugar sabe contra quién le
 * tocó y no tiene forma de escribirle, y termina preguntándole al del club
 * que a su vez pregunta en el grupo.
 *
 * EL TELÉFONO NO SALE PARA CUALQUIERA. La API lo pone únicamente en el
 * partido de quien lo necesita: los dos que lo juegan —cada uno ve el del
 * otro— y quien organiza el torneo. Para el resto la lista viene vacía. Un
 * cuadro se comparte, se imprime y se pega en la pared del club: publicar
 * ahí el número de cincuenta personas es exactamente lo que no se puede
 * hacer, y por eso se decide en el servidor y no escondiendo un botón.
 */
export interface MatchContact {
  entryId: string;
  displayName: string;
  /** En E.164, como está en el perfil: `+50688887777`. */
  whatsappPhone: string;
}

export interface Match {
  id: string;
  level: CategoryLevel;
  /** El cuadro en el que juega. `MIXED` solo en dobles. */
  sex: CategorySex;
  discipline: CategoryDiscipline;
  round: number;
  position: number;
  /** `null` mientras no se sepa quién llega: el partido anterior no se jugó. */
  home: TournamentEntry | null;
  away: TournamentEntry | null;
  /** El marcador, o `[]` si todavía no se jugó. */
  score: MatchScore;
  /** Quién ganó. `null` = sin jugar. */
  winnerEntryId: string | null;
  /**
   * `true` si pasó sin jugar porque del otro lado había un HUECO DEL CUADRO.
   *
   * NO ES EL W.O. DEPORTIVO. «Pasó porque el cuadro no estaba lleno» y «pasó
   * porque el rival no llegó» son dos cosas distintas: la primera no se jugó
   * ni le cuenta a nadie, la segunda es un partido que existió y que en las
   * estadísticas de alguien es una derrota. Eso otro es `noShowEntryId`.
   */
  walkover: boolean;
  /**
   * QUIÉN NO SE PRESENTÓ. `null` en un partido normal.
   *
   * Cuando viene, el otro es `winnerEntryId` y el marcador está vacío: no hay
   * sets que mostrar porque no se jugó ninguno. Se guarda quién FALTÓ y no
   * quién pasó, porque quién pasó ya está en `winnerEntryId`, y tener las dos
   * caras del mismo dato es cómo terminan contradiciéndose.
   */
  noShowEntryId: string | null;
  /**
   * En qué grupo se juega, para los formatos de tabla. `null` en las llaves
   * y en los partidos de definición (semifinal, final).
   *
   * 0 es el grupo A y 1 el grupo B. Números y no letras porque lo que se hace
   * con esto es agrupar y ordenar; el nombre lo pone la pantalla.
   */
  group: number | null;
  /** Cuándo y dónde. Todo en `null` si nadie lo programó. */
  schedule: MatchSchedule;
  /**
   * UNA FOTO DEL PARTIDO, o `null`. La del final, o el marcador escrito a
   * mano en la pizarra.
   *
   * UNA SOLA Y NO UN ÁLBUM: lo que se quiere de un partido es la foto, no
   * elegir entre catorce. Con varias hay que decidir cuál va en el cuadro, y
   * esa decisión no la quiere tomar nadie.
   */
  photoPath: string | null;
  /**
   * A quién se le puede escribir para acordar ESTE partido. Vacío cuando
   * quien mira no es ninguno de los dos ni organiza, y cuando el club apagó
   * el contacto por WhatsApp. Ver `MatchContact`.
   */
  contacts: MatchContact[];
  /**
   * ¿PUEDE QUIEN MIRA ANOTAR EL MARCADOR DE ESTE PARTIDO?
   *
   * Lo decide el servidor y no la pantalla, porque depende de tres cosas que
   * la pantalla no tiene juntas: si quien mira organiza el torneo, si el club
   * dejó que los jugadores anoten, y si es UNO DE LOS DOS que juegan este
   * partido. Un jugador puede anotar el suyo y ninguno más, así que es una
   * respuesta por partido y no por torneo.
   */
  canScore: boolean;
  /**
   * ¿PUEDE QUIEN MIRA BORRAR EL MARCADOR DE ESTE PARTIDO?
   *
   * OTRA PREGUNTA QUE `canScore`, y más estrecha: anotar lo puede quien
   * organiza y —si el club lo permite— quien jugó; borrar, solo quien
   * administra el CLUB del torneo o la plataforma. La diferencia es que
   * borrar deja el partido abierto y, si alguien ya había avanzado, se lleva
   * la ronda siguiente por delante. Ver `borrar-marcador.ts` en el servidor.
   *
   * ES `false` EN UN PARTIDO SIN JUGAR: no hay nada que deshacer.
   */
  canClearScore: boolean;
}

/**
 * UN PARTIDO JUGADO, en el historial de una persona.
 *
 * ESTÁ ESCRITO DESDE EL LADO DE QUIEN MIRA EL HISTORIAL, no desde el lado
 * «home/away» del cuadro. En una llave, estar arriba o abajo es un detalle
 * del dibujo; leyendo el historial de alguien, lo único que importa es si
 * ganó y contra quién. Por eso acá no hay `home` ni `away`: hay `won`,
 * `opponent` y un marcador ya dado vuelta cuando hacía falta.
 *
 * `score` VIENE EN EL ORDEN DE ESA PERSONA: su número primero. Un 6-4 en la
 * fila de quien perdió sería mentira, y voltearlo en la pantalla obliga a
 * cada pantalla a acordarse de hacerlo.
 *
 * SIN MARCADOR Y CON `won` PUESTO es un partido ganado por W.O.: el rival no
 * se presentó y no se jugó ningún set. `walkover` lo dice sin que la pantalla
 * tenga que deducirlo de un marcador vacío.
 */
/**
 * DE QUÉ CLASE ES UN PARTIDO YA JUGADO.
 *
 * Los tres se cuentan juntos en el cara a cara —«jugamos seis veces» son seis
 * veces, salgan de donde salgan— pero se distinguen para poder filtrarlos, que
 * es lo que Javier pidió con los chips de arriba: un torneo no es un amistoso
 * de un martes.
 *
 * EL RETO POR RANKING Y EL AMISTOSO SON DOS CLASES Y NO UNA CON UNA BANDERA,
 * porque se comportan distinto en todo lo demás: uno tiene club y categoría y
 * mueve la tabla, el otro no tiene ninguna de las tres cosas y puede empatar.
 */
export const PLAYED_MATCH_KINDS = ['TOURNAMENT', 'CHALLENGE_RANKING', 'CHALLENGE_FRIENDLY'] as const;
export type PlayedMatchKind = (typeof PLAYED_MATCH_KINDS)[number];

export interface PlayedMatch {
  matchId: string;
  /** De qué clase: un cuadro de torneo, un reto por ranking, un amistoso. */
  kind: PlayedMatchKind;
  /**
   * EL TORNEO DONDE SE JUGÓ, o `null` si fue un reto.
   *
   * Estos campos eran obligatorios cuando la única clase de partido era la de
   * torneo. Volverlos nulables es lo que dejó entrar a los retos sin inventar
   * un torneo falso para ellos — que era la otra salida, y la que después
   * hace que alguien busque ese torneo y no lo encuentre.
   */
  tournamentId: string | null;
  tournamentName: string | null;
  /** El club. `null` en un AMISTOSO: un amistoso no se juega «en» ningún club. */
  communityId: string | null;
  communityName: string | null;
  /**
   * Cuándo se jugó. `YYYY-MM-DD`.
   *
   * EN UN TORNEO ES EL DÍA QUE ARRANCÓ EL TORNEO y no el del partido, porque
   * es lo único que se guarda. En un reto es el día real. Se mezclan en la
   * misma lista ordenada, y eso es lo mejor que se puede hacer con lo que
   * hay: un torneo de tres días queda ubicado en su primer día.
   */
  playedOn: string;
  /** La categoría. `null` en un amistoso, que no tiene ninguna. */
  level: CategoryLevel | null;
  /**
   * EL CUADRO donde se jugó. `null` en un amistoso: se juega con quien sea.
   *
   * Es el sexo de la CATEGORÍA y no el de quien jugó: en un dobles mixto el
   * cuadro es `MIXED` y adentro hay un hombre y una mujer.
   */
  sex: CategorySex | null;
  /** Singles o dobles. `null` en un amistoso. */
  discipline: CategoryDiscipline | null;
  /** El formato del cuadro donde se jugó. `null` si no fue un torneo. */
  format: TournamentFormat | null;
  /**
   * CONTRA QUIÉN JUGÓ. `null` cuando el rival era un INVITADO.
   *
   * Un invitado no tiene cuenta, así que no hay ficha que abrir ni rivalidad
   * que acumular: `opponentName` sigue viniendo —el partido pasó y el nombre
   * es lo que se recuerda— pero el nombre no es una identidad. Dos invitados
   * llamados «Marco» no son la misma persona, y tratarlos como una sola es
   * exactamente lo que este `null` impide.
   */
  opponentUserId: string | null;
  opponentName: string;
  /**
   * Su foto, o `null` si no tiene.
   *
   * VIAJA CON EL PARTIDO Y NO SE BUSCA APARTE. Quien dibuja la fila ya tiene
   * el nombre acá; pedir la foto por separado sería una consulta por rival
   * para completar algo que la fila ya podía traer, y es lo que hacía que el
   * cara a cara mostrara al rival con iniciales teniendo foto puesta.
   */
  opponentAvatarPath: string | null;
  /**
   * Cómo terminó, para quien mira.
   *
   * ERA UN `won: boolean` Y NO ALCANZABA desde que un amistoso puede quedar
   * empatado: un booleano tiene dos valores y los finales son tres. Con el
   * booleano, un empate se contaba como derrota.
   */
  outcome: MatchOutcome;
  /** Los sets, con el número de esta persona primero. `[]` si fue W.O. */
  score: MatchScore;
  /** Ganado o perdido sin jugar porque el otro no llegó. */
  walkover: boolean;
}

// ---------------------------------------------------------------------
// Los retos entre dos personas
// ---------------------------------------------------------------------

/**
 * EN QUÉ ANDA UN RETO.
 *
 * `PENDING` mandado y sin contestar; `ACCEPTED` aceptado y sin jugar;
 * `DECLINED` le dijeron que no; `PLAYED` ya se jugó; `CANCELLED` lo canceló
 * quien retó antes de que le contestaran; `EXPIRED` se venció solo.
 *
 * `EXPIRED` ES SU PROPIO ESTADO Y NO `CANCELLED`. Cancelar es una decisión de
 * alguien; vencerse es que no pasó nada. Mezclarlos haría que el historial
 * dijera que una persona canceló un reto que en realidad dejó morir —o al
 * revés—, y eso es justo lo que alguien va a reclamar el día que le cobren
 * una penalidad por no haber contestado.
 *
 * La clase del reto —amistoso o de ranking— es `ChallengeKind`, que ya existe
 * más arriba: es el mismo concepto que decide contra quién se puede jugar.
 */
export const CHALLENGE_STATUSES = [
  'PENDING',
  'ACCEPTED',
  'DECLINED',
  'PLAYED',
  'CANCELLED',
  'EXPIRED',
] as const;
export type ChallengeStatus = (typeof CHALLENGE_STATUSES)[number];

/** Quién es la otra persona de un reto, con lo mínimo para dibujar su fila. */
export interface ChallengePerson {
  userId: string;
  displayName: string;
  avatarPath: string | null;
}

/**
 * Un reto, como lo ve quien lo mira.
 *
 * `me` Y `them` Y NO `from` Y `to`, y esa es toda la diferencia con la fila de
 * la base. Quien mira quiere saber «¿yo reté o me retaron?» y «¿quién es el
 * otro?»; con `from`/`to` cada pantalla tendría que averiguar cuál de los dos
 * es uno mismo, y la que se equivoque muestra la foto del rival donde va la
 * propia. Se resuelve UNA vez, en el servidor, que es el único que sabe con
 * certeza quién está preguntando.
 */
export interface Challenge {
  id: string;
  kind: ChallengeKind;
  status: ChallengeStatus;
  /** `true` si el reto lo mandó quien está mirando. */
  mine: boolean;
  /** La otra persona, sea quien retó o a quien retaron. */
  them: ChallengePerson;
  /**
   * SINGLES O DOBLES, y quiénes son las parejas.
   *
   * En `SINGLES` las dos parejas van en `null`. En dobles, `myPartner` es
   * quien juega DEL LADO DE QUIEN MIRA y `theirPartner` del otro: igual que
   * `me` y `them`, se cuentan desde quien pregunta y no desde cómo está
   * guardado. Quien mira quiere saber «¿con quién juego?», no «¿soy el
   * `fromUser`?».
   */
  discipline: CategoryDiscipline;
  myPartner: ChallengePerson | null;
  theirPartner: ChallengePerson | null;
  /** El club donde cuenta. `null` en los amistosos. */
  communityId: string | null;
  communityName: string | null;
  /** La categoría donde cuenta. `null` en los amistosos. */
  level: CategoryLevel | null;
  /** ISO 8601 de cuándo se mandó. */
  createdAt: string;
  /**
   * HASTA CUÁNDO HAY PARA JUGARLO Y ANOTARLO. `YYYY-MM-DD`, fecha civil.
   *
   * Sale de cuándo se aceptó más `CHALLENGE_REPORT_DAYS`. `null` mientras
   * nadie contestó y también una vez jugado: en los dos casos no hay ningún
   * plazo corriendo, y mostrar una fecha vencida al lado de un partido ya
   * jugado sería un reproche por algo que se hizo.
   *
   * VIENE CALCULADO DEL SERVIDOR y no se arma en la pantalla: el plazo lo
   * define el servidor, y una pantalla que lo calcula sola es una pantalla
   * que un día muestra otro plazo que el que se aplica.
   */
  reportDeadline: string | null;
  /** `YYYY-MM-DD` de cuándo se jugó, o `null`. Fecha civil. */
  playedOn: string | null;
  /**
   * CÓMO LE FUE A QUIEN MIRA. `null` mientras no se haya jugado.
   *
   * ERA UN `won: boolean | null` Y NO ALCANZABA. Desde que un amistoso puede
   * quedar empatado hay TRES finales —ganó, perdió, empataron— y un booleano
   * con nulo solo tiene dos más «todavía no». Agregarle un `draw` al lado
   * habría sido dos campos para tres estados, que es exactamente la forma de
   * que un día aparezca un reto «no jugado pero empatado».
   */
  result: MatchOutcome | null;
  /** El marcador, SIEMPRE en el orden de quien mira: lo suyo primero. */
  score: MatchScore;
  /**
   * La foto del partido, o `null`.
   *
   * La misma idea que en un partido de cuadro: la del final, o el marcador en
   * la pizarra. Es lo que hace que un reto de hace tres meses sea algo que se
   * mira y no un renglón con dos números.
   */
  photoPath: string | null;
  /**
   * Cuántos puntos de ranking dejó ESTE reto a quien mira.
   *
   * `0` en los amistosos y en los perdidos —perder no resta, lo decidió
   * Javier— y `null` mientras no se haya jugado. Viaja ya calculado porque el
   * cálculo depende de cuántas veces se enfrentaron antes, que la pantalla no
   * sabe. Ver `domain/challenge-points.ts`.
   */
  points: number | null;
  /**
   * ¿ESTE MARCADOR ESTÁ CONFIRMADO POR EL OTRO?
   *
   * Solo es `false` en los amistosos que alguien anotó SIN reto previo y que
   * el rival todavía no dio por buenos. Los que salieron de un reto nacen en
   * `true`: ahí los dos ya se habían puesto de acuerdo para jugar.
   *
   * MIENTRAS SEA `false` NO SUMA PUNTOS. Es lo único que evita que alguien
   * escriba «le gané 6-0 6-0» a quien ni estaba y suba en la tabla.
   */
  confirmed: boolean;
  /**
   * ¿A QUIEN MIRA LE TOCA CONFIRMAR ESTE PARTIDO?
   *
   * Viaja calculado porque depende de quién pregunta: es `true` solo para el
   * rival —nunca para quien lo anotó, que no puede darse la razón a sí mismo—
   * y solo mientras siga sin confirmar.
   */
  waitingMyConfirmation: boolean;
  /**
   * ¿QUIEN MIRA PUEDE CORREGIR ESTE MARCADOR?
   *
   * Los dos que jugaron pueden durante 24 horas desde que se anotó; quien
   * administra la plataforma, siempre. Lo contesta el SERVIDOR con la misma
   * regla que aplica al guardar: si la pantalla lo dedujera, el día que el
   * plazo cambie habría que acordarse de dos lugares — y uno se olvidaría.
   */
  canFixScore: boolean;
  /**
   * ¿QUIEN MIRA PUEDE BORRAR ESTE MARCADOR, dejando el reto reanotable?
   *
   * OTRA PREGUNTA QUE `canFixScore`, y más estrecha: corregir deja un
   * marcador, borrar deja el partido abierto para que cualquiera anote de
   * nuevo. Por eso en un reto solo puede la plataforma, y sin plazo. Ver
   * `borrar-marcador.ts` en el servidor.
   *
   * LO DECIDE EL SERVIDOR, como `canFixScore`, y por la misma razón: si la
   * pantalla lo dedujera por su cuenta, el día que la regla cambie habría que
   * acordarse de los dos lados — y el que se olvida es el que muestra un botón
   * que da 403.
   */
  canClearScore: boolean;

  /**
   * EL ÚLTIMO SET FUE UN SÚPER TIE BREAK.
   *
   * VUELVE EN LA RESPUESTA para que al CORREGIR un marcador el check aparezca
   * como estaba. Sin esto, quien corrige un 10-8 lo vería desmarcado y al
   * guardar borraría el dato sin enterarse.
   */
  superTieBreak: boolean;
}

/** `POST /challenges` */

/**
 * LAS DOS PAREJAS DE UN DOBLES.
 *
 * `myPartnerId` es quien juega CONMIGO y `theirPartnerId` quien juega con el
 * rival. Van las dos o ninguna: un dobles con una sola pareja no existe, y por
 * eso viajan juntas en un objeto y no como dos campos sueltos.
 *
 * NO IMPORTA EL SEXO: cualquier combinación vale, mixta incluida. Lo único que
 * se exige es que las cuatro personas sean distintas.
 */
export interface DoublesPartners {
  myPartnerId: string;
  theirPartnerId: string;
}

export interface CreateChallengeRequest {
  toUserId: string;
  kind: ChallengeKind;
  /**
   * Dónde cuenta. Obligatorio para `RANKING`, y tiene que ser un club donde
   * los DOS estén activos y en la misma categoría.
   */
  communityId?: string;
  /**
   * EN CUÁL DE LAS TABLAS COMPARTIDAS cuenta este reto.
   *
   * Opcional porque casi siempre se comparte una sola y no hay nada que
   * elegir. Cuando se comparten dos —los dos en B masculino y en C masculino—
   * el servidor elegía «la más fuerte de quien reta», y eso hacía que el mismo
   * partido contara en la B si retaba uno y en la C si retaba el otro. Quien
   * reta lo dice y deja de depender de quién apretó el botón.
   *
   * TIENE QUE SER UNA TABLA QUE JUEGUEN LOS DOS, o el reto se rechaza: elegir
   * no es inscribirse.
   */
  ranking?: { level: CategoryLevel; sex: CategorySex };
  /** Las parejas, si es dobles. Ausente = singles. */
  doubles?: DoublesPartners;
}

/**
 * CUÁNTO HAY PARA JUGAR UN RETO ACEPTADO Y ANOTAR EL RESULTADO.
 *
 * Una semana, y la pidió Javier: «una vez aceptado el reto poner fecha límite
 * de una semana para reportar el marcador». Resuelve un problema que se ve
 * en la lista de retos: dos personas aceptan, no se ponen de acuerdo, y el
 * reto queda ahí meses sin que nadie sepa si va a pasar.
 *
 * SIETE DÍAS PORQUE UN RETO SE JUEGA EL FIN DE SEMANA. Menos deja afuera a
 * quien acepta un lunes y juega el sábado; más y deja de ser un plazo.
 *
 * NO CIERRA NADA, Y ESO TAMBIÉN LO PIDIÓ ASÍ: «si no, solo se pone una
 * leyenda». El reto se puede anotar igual después del plazo — el partido
 * pudo haberse jugado tarde, y borrar un resultado real por llegar tarde
 * sería peor que la demora. Lo que cambia es que la lista lo dice, y eso
 * alcanza para que alguien escriba por WhatsApp.
 */
export const CHALLENGE_REPORT_DAYS = 7;

/** `POST /challenges/:id/answer` */
export interface AnswerChallengeRequest {
  accept: boolean;
}

/**
 * `POST /challenges/:id/result` — anotar quién ganó.
 *
 * `winnerUserId` y no «gané yo»: al resultado lo puede anotar cualquiera de
 * los dos —o quien administra el club— y «yo» significaría algo distinto
 * según quién apriete.
 */
export interface SetChallengeResultRequest {
  /**
   * Quién ganó, o `null` SI EMPATARON.
   *
   * El empate solo se acepta en los AMISTOSOS. En un reto por ranking, `null`
   * rebota: es el que ordena la tabla y un empate no mueve a nadie de puesto.
   *
   * Tiene que coincidir con el marcador. Son dos datos que dicen lo mismo, y
   * si se contradicen no hay forma de saber cuál vale.
   */
  winnerUserId: string | null;
  score: MatchScore;
  /**
   * EL ÚLTIMO SET FUE UN SÚPER TIE BREAK. Ausente = no.
   *
   * SE PREGUNTA Y NO SE DEDUCE DEL MARCADOR. Un 10-8 en el último set es casi
   * seguro un súper tie break, pero un 7-5 también puede serlo si jugaron a
   * 7, y un tercer set normal puede terminar 10-8. Adivinarlo mete errores en
   * una estadística que nadie va a poder corregir después, porque nadie se
   * acuerda de un partido de hace tres meses.
   */
  superTieBreak?: boolean;
  /** `YYYY-MM-DD`. Si falta, es hoy. */
  playedOn?: string;
}

/**
 * `POST /challenges/friendly` — ANOTAR UN AMISTOSO QUE YA SE JUGÓ.
 *
 * QUÉ PROBLEMA RESUELVE. Hasta acá, para que un partido quedara registrado
 * había que haberlo pactado ANTES en la aplicación: uno reta, el otro acepta,
 * y recién entonces se puede anotar el marcador. Pero la mayoría de los
 * partidos no pasan así —se juegan porque coincidieron en la cancha— y esos
 * quedaban afuera de todo: del historial, de la actividad y del ranking.
 *
 * NO PIDE QUE EXISTA UN RETO, y por eso el rival tiene que confirmar después:
 * ver `confirmed` en `Challenge`. Se guarda igual que un amistoso normal
 * —misma tabla, misma tarjeta en la actividad, mismos comentarios— porque es
 * la misma cosa; lo único distinto es cómo empezó.
 *
 * POR AHORA SOLO SINGLES. Dobles necesita que un lado pueda tener dos
 * personas, que hoy no existe ni en la tabla ni en la tarjeta de actividad.
 */
export interface ReportFriendlyRequest {
  /**
   * Contra quién se jugó. Tiene que ser una cuenta de la aplicación: es quien
   * va a recibir el aviso para confirmar, y sin cuenta no hay a quién
   * preguntarle si el partido existió.
   */
  opponentUserId: string;
  /**
   * Quién ganó: el propio `userId`, el del rival, o `null` si empataron.
   * Tiene que coincidir con el marcador, igual que en un reto.
   */
  winnerUserId: string | null;
  /** El marcador, en el orden de quien lo anota: lo suyo primero. */
  score: MatchScore;
  /** El último set fue un súper tie break. Ausente = no. Ver `SetChallengeResultRequest`. */
  superTieBreak?: boolean;
  /** `YYYY-MM-DD`. Si falta, es hoy. No puede ser en el futuro. */
  playedOn?: string;
  /** Las parejas, si el partido fue de dobles. Ausente = singles. */
  doubles?: DoublesPartners;
}

/**
 * Las disciplinas que se pueden anotar como amistoso.
 *
 * DOBLES ESTÁ EN LA LISTA PERO TODAVÍA NO SE PUEDE. Está acá para que la
 * pantalla lo muestre apagado y se entienda que viene, en vez de que parezca
 * que la aplicación no sabe que el tenis se juega de a cuatro. El servidor lo
 * rechaza; ver `ReportFriendlyRequest`.
 */
export const FRIENDLY_DISCIPLINES = ['SINGLES', 'DOUBLES'] as const;
export type FriendlyDiscipline = (typeof FRIENDLY_DISCIPLINES)[number];

// ---------------------------------------------------------------------
// El Muro
// ---------------------------------------------------------------------

/**
 * LOS EMOJIS CON LOS QUE SE PUEDE REACCIONAR.
 *
 * UNA LISTA CERRADA Y NO UN CAMPO LIBRE. Con un campo libre, cualquiera
 * manda cualquier cadena: un emoji que no existe, una bandera política, o
 * doscientos caracteres que rompen la fila. Cinco alcanzan para decir lo que
 * se quiere decir debajo de un resultado de tenis, y una lista corta se
 * dibuja como cinco botones en vez de como un selector.
 *
 * El servidor rechaza cualquier otro. Ver `REACTION_NOT_ALLOWED`.
 */
export const WALL_REACTIONS = ['🔥', '👏', '😮', '💪', '😂'] as const;
export type WallReaction = (typeof WALL_REACTIONS)[number];

/** Cuántas letras entran en un comentario. Es un comentario, no una crónica. */
export const COMMENT_MAX = 500;

/**
 * Un comentario debajo de un partido.
 *
 * `canDelete` LO DECIDE EL SERVIDOR, y es lo que hace que el botón de borrar
 * no sea una sugerencia: quien administra el club puede borrar cualquiera
 * —el primer comentario feo tiene que tener arreglo desde adentro de la
 * aplicación— y cada quien puede borrar el suyo.
 */
export interface MatchComment {
  id: string;
  /**
   * DE QUÉ CUELGA: un partido de torneo O un reto, nunca los dos.
   *
   * Dos campos nulables y no uno con el «tipo» al lado, por lo mismo que en
   * la base: con un id suelto, quien lo lee tiene que acordarse de mirar el
   * tipo antes de usarlo, y el día que se olvide va a buscar un reto en la
   * tabla de partidos.
   */
  matchId: string | null;
  challengeId: string | null;
  /** O de un meme. Ver `Meme`. */
  memeId: string | null;
  authorId: string;
  authorName: string;
  authorAvatarPath: string | null;
  body: string;
  createdAt: string;
  canDelete: boolean;
}

/**
 * Cuánta gente puso ESE emoji, y si yo soy una de ellas.
 *
 * `mine` ES LO QUE DECIDE SI EL BOTÓN SE VE PRENDIDO, y por eso viaja en vez
 * de deducirse: sin él, la pantalla tendría que recibir la lista de quiénes
 * reaccionaron —los nombres de todo el club debajo de cada partido— para
 * buscarse a sí misma.
 */
export interface WallReactionCount {
  emoji: string;
  count: number;
  mine: boolean;
}

/**
 * UNA TARJETA DEL MURO.
 *
 * Es un partido con lo que hace falta para contarlo como novedad: de qué
 * torneo y club es, en qué ronda, cuántas veces se enfrentaron esos dos, y lo
 * que la gente dijo abajo.
 *
 * LLEVA UN `Match` ENTERO ADENTRO y no una copia recortada: el dibujo del
 * partido, el editor de marcador y los permisos ya existen y trabajan sobre
 * `Match`. Recortarlo obligaría a mantener dos ideas de qué es un partido.
 */
export interface WallEntry {
  tournamentId: string;
  tournamentName: string;
  communityId: string;
  communityName: string;
  /**
   * Cuánta gente quedaba al empezar esa ronda: 2 es la final, 4 la semi.
   * Viaja el número y no la palabra porque la palabra se escribe en el idioma
   * de quien mira.
   */
  roundSize: number;
  /** Hasta cuándo hay para jugarla, si el torneo lo puso. */
  deadline: string | null;
  /** Cuántas veces se enfrentaron ESTOS DOS, contando este partido. */
  playedTogether: number;
  match: Match;
  reactions: WallReactionCount[];
  comments: MatchComment[];
}

// ---------------------------------------------------------------------
// La Actividad
// ---------------------------------------------------------------------

/**
 * UNO DE LOS DOS LADOS DE UNA TARJETA DE ACTIVIDAD.
 *
 * TRAE TODO RESUELTO —nombre, foto, categoría, puntos y cómo le fue— porque
 * la tarjeta es una lectura y no un formulario: quien la dibuja no tiene que
 * ir a buscar nada, y así una pantalla no puede equivocarse al cruzar los
 * datos de un lado con los del otro.
 */
export interface ActivitySide {
  /** `null` si fue un invitado: jugó, pero no tiene cuenta que abrir. */
  userId: string | null;
  displayName: string;
  avatarPath: string | null;
  /** Su categoría, para la insignia. `null` en un amistoso. */
  level: CategoryLevel | null;
  /**
   * Cuántos puntos le dejó ESTE partido, o `null` si no deja ninguno.
   *
   * `null` y no `0`: un cero al lado de un amistoso invita a preguntar por
   * qué no dio ninguno, cuando la respuesta es que nunca iba a dar.
   */
  points: number | null;
  outcome: MatchOutcome;
}

/**
 * UNA TARJETA DE LA ACTIVIDAD: un partido que ya pasó.
 *
 * NO ES `WallEntry`, Y SON DOS TIPOS A PROPÓSITO. `WallEntry` es la agenda
 * —«lo que se viene»— y arrastra el tamaño de la ronda y la fecha límite, que
 * son cosas de un cuadro sin jugar. Esto es lo que YA pasó, y puede ser un
 * reto, que no tiene cuadro ni ronda. Con un solo tipo, cada uno cargaría la
 * mitad de los campos en nulo y ninguna pantalla sabría cuáles esperar.
 */
export interface ActivityEntry {
  /** El id del partido o el del reto. Estable, sirve de clave. */
  id: string;
  kind: PlayedMatchKind;
  /** Cuándo se jugó. `YYYY-MM-DD`, fecha civil. */
  playedOn: string;
  /** El club. `null` en un amistoso, que no se juega en ninguno. */
  communityId: string | null;
  communityName: string | null;
  /** El torneo. `null` si fue un reto. */
  tournamentId: string | null;
  tournamentName: string | null;
  /** La categoría del partido. `null` en un amistoso. */
  level: CategoryLevel | null;
  /** El CUADRO donde se jugó. `null` en un amistoso, que no tiene ninguno. */
  sex: CategorySex | null;
  /** Singles o dobles. `null` en un amistoso. */
  discipline: CategoryDiscipline | null;
  /**
   * LOS DOS LADOS, SIEMPRE EN EL ORDEN DEL MARCADOR: el primero es `home`.
   *
   * Una tupla y no una lista: son exactamente dos, y un arreglo de largo
   * variable obliga a cada pantalla a preguntarse qué hacer con tres.
   */
  sides: readonly [ActivitySide, ActivitySide];
  /** Los sets, en el orden de `sides`. `[]` si fue W.O. o no se jugó ninguno. */
  score: MatchScore;
  walkover: boolean;
  photoPath: string | null;
  /** Cuántas veces se enfrentaron ESTOS DOS, contando este partido. */
  playedTogether: number;

  /**
   * ¿QUIEN MIRA PUEDE CORREGIR ESTE MARCADOR?
   *
   * LO DICE EL SERVIDOR, con la misma regla que aplica al guardar: los dos
   * que jugaron por un día desde que se anotó, la plataforma sin plazo. Ver
   * `corregir-marcador.ts`.
   *
   * ESTABA EN `Challenge` Y NO ACÁ, y ése fue el error: el botón de corregir
   * existía solo en «Mis retos», y el muro —que es donde la gente mira los
   * partidos— no tenía con qué dibujarlo. Javier lo encontró siendo admin de
   * plataforma, que es justamente quien puede corregir siempre.
   *
   * `false` en los partidos de TORNEO: ésos se corrigen desde el cuadro, que
   * es donde vive el permiso del organizador, y son otra regla.
   */
  canFixScore: boolean;

  /** El último set fue un súper tie break. Ver `SetChallengeResultRequest`. */
  superTieBreak: boolean;

  reactions: WallReactionCount[];
  comments: MatchComment[];
}

/** Cuántas tarjetas trae una página de la Actividad. */
export const ACTIVITY_PAGE = 10;

/**
 * LOS FILTROS DE LA ACTIVIDAD. Todo opcional; sin nada, todo lo visible.
 *
 * VAN AL SERVIDOR Y NO SE APLICAN EN LA PANTALLA, al revés que los chips del
 * cara a cara. Allá los partidos ya habían viajado enteros; acá la lista
 * puede ser de miles y viene de a diez, así que filtrar después de paginar
 * daría páginas de dos elementos y páginas vacías.
 */
export interface ActivityQuery {
  /** Un club en particular. Sin esto, todos los que se puedan ver. */
  communityId?: string;
  level?: CategoryLevel;
  sex?: Sex;
  /**
   * Quiénes jugaron. Con dos, los partidos donde jugaron LOS DOS —que es lo
   * que alguien busca al poner dos nombres: el historial entre ellos—.
   */
  userIds?: string[];
  /** `YYYY-MM-DD`, inclusive los dos. */
  from?: string;
  to?: string;
  /** Desde 1. */
  page?: number;
}

/** `GET /wall/activity` */
export interface ActivityPage {
  entries: ActivityEntry[];
  /** Cuántas páginas hay en total. Nunca menos de 1. */
  pages: number;
  /** En cuál se está, desde 1. */
  page: number;
}

/** `POST /wall/matches/:id/comments` */
export interface AddCommentRequest {
  body: string;
}


// ---------------------------------------------------------------------
// Los ajustes de puntos
// ---------------------------------------------------------------------

/** Por qué se ajustaron los puntos de alguien. */
export const ADJUSTMENT_REASONS = [
  /** Entró al club trayendo puntos de otro ranking, aprobados por el club. */
  'SEEDED',
  /** Quien administra los corrigió a mano. */
  'ADMIN_EDIT',
  /** Dejó vencer un reto sin contestar. */
  'CHALLENGE_UNANSWERED',
  /** Aceptó un reto y se venció sin jugarse: el premio va a quien retó. */
  'CHALLENGE_UNPLAYED',
  /** Penalidad semanal por no jugar retos. */
  'INACTIVITY',
] as const;
export type AdjustmentReason = (typeof ADJUSTMENT_REASONS)[number];

/**
 * UNA LÍNEA DEL HISTORIAL DE PUNTOS.
 *
 * QUÉ PROBLEMA RESUELVE. Los puntos se mueven sin que se haya jugado nada
 * —una penalidad, los puntos con los que alguien entró, una corrección— y sin
 * esto la única respuesta a «¿por qué perdí cinco puntos?» es «no sé». Un
 * ranking que se mueve sin explicación es un ranking en el que nadie confía.
 *
 * LO VE QUIEN ADMINISTRA Y LA PROPIA PERSONA. Esconderlo detrás del rol sería
 * guardar la explicación donde el que pregunta no la puede leer.
 */
export interface PointAdjustment {
  id: string;
  /** Positivo suma, negativo resta. */
  points: number;
  reason: AdjustmentReason;
  /** Lo que se escribió, si se escribió algo. */
  note: string | null;
  /** En qué ranking cayó, congelado al aplicarse. */
  level: CategoryLevel;
  /**
   * EL SEXO DE LA TABLA donde cayeron los puntos, que puede no ser el de la
   * persona: los de una mujer que compite en la masculina cuentan ahí.
   */
  sex: CategorySex;
  /**
   * Quién lo hizo. `null` = LO HIZO EL SISTEMA.
   *
   * `null` y no una raya: «lo hizo el sistema» y «no sé quién» son cosas
   * distintas, y la pantalla las dice distinto.
   */
  byName: string | null;
  at: string;
}

/** `POST /communities/:id/members/:userId/adjustments` */
export interface AdjustPointsRequest {
  /**
   * SUMA O RESTA, NO FIJA UN TOTAL.
   *
   * Fijar el total obligaría a recalcular todo lo demás —partidos, retos,
   * penalidades— y a decidir qué pasa cuando esa persona juegue después. Un
   * «+30» es una fila más, y el total sigue saliendo de sumar.
   */
  points: number;
  /** Obligatorio: un número que cambia sin explicación no se le puede creer. */
  note: string;
  /**
   * EN CUÁL DE SUS TABLAS. Opcional solo porque casi todo el mundo tiene una.
   *
   * Desde que se puede competir en dos rankings del mismo club, «ponerle 40
   * puntos a Silvia» no es una instrucción completa: tiene B femenino y C
   * masculino, y las dos son igual de suyas. Antes el servidor elegía la
   * primera en silencio y los puntos aparecían en el ranking equivocado.
   *
   * CON UNA SOLA TABLA SE PUEDE OMITIR —no hay nada que elegir— y CON DOS es
   * obligatorio: el servidor no adivina, pide que se diga.
   */
  ranking?: { level: CategoryLevel; sex: CategorySex };
}

export const ADJUSTMENT_LIMITS = {
  /**
   * EL TOPE DE UNA CORRECCIÓN A MANO. Un torneo ganado vale 250; más que eso
   * de un saque necesita otra conversación.
   *
   * OJO: NO es el tope de los puntos que alguien PIDE TRAER de otro ranking.
   * Ese es `JOIN_POINTS_MAX` y es otra cosa —ver ahí el porqué—. Los dos
   * salían de este número y eso era el error.
   */
  pointsMax: 500,
  noteMax: 300,
} as const;

/**
 * CUÁNTOS PUNTOS SE PUEDEN PEDIR AL ENTRAR A UN CLUB.
 *
 * NO ES UN LÍMITE DE POLÍTICA, ES UNA RED CONTRA EL DESBORDE. Javier lo pidió
 * sin tope: «quitar límite de solicitud de puntos».
 *
 * POR QUÉ ESTABA EN 500 Y POR QUÉ ESTABA MAL. Salía de `ADJUSTMENT_LIMITS`,
 * que es el tope de una CORRECCIÓN a mano —un «+30» de quien administra—. Son
 * dos cosas distintas: corregir es acomodar un número que ya existe, y pedir
 * puntos es traer un ranking entero de otra liga. Quien llega con tres mil
 * puntos de otro lado no estaba pidiendo una corrección de tres mil.
 *
 * POR QUÉ NO ES INFINITO. La columna es `INTEGER` en Postgres y arriba de
 * 2.147 millones la base tira un error que no dice nada. Cien mil es ocho
 * veces los puntos del número uno del mundo: nadie lo va a tocar jugando, y
 * deja lugar de sobra para sumar sin que el entero se dé vuelta.
 *
 * NO LO APRUEBA SOLO: el club decide cuántos da, y puede dar menos o ninguno.
 * El tope de acá es contra un dedazo, no contra alguien pidiendo de más.
 */
export const JOIN_POINTS_MAX = 100_000;

// ---------------------------------------------------------------------
/**
 * CUÁNTAS COSAS NUEVAS HAY EN CADA ACCESO RÁPIDO DEL INICIO.
 *
 * Javier: «cuando hay memes nuevos o partidos nuevos poner un círculo en rojo
 * la cantidad de cosas nuevas que hayan para que los usuarios abran los
 * accesos rápidos».
 *
 * EL TELÉFONO DICE QUÉ VIO Y EL SERVIDOR CUENTA. Quién recuerda la última
 * visita es el teléfono —lo eligió Javier— así que estas fechas viajan en la
 * consulta. La consecuencia hay que saberla: quien entre desde otro aparato ve
 * el globito de nuevo con cosas que ya miró. A cambio, el servidor no guarda
 * por persona el rastro de cuándo abre cada pantalla.
 *
 * UNA SOLA CONSULTA PARA LOS CUATRO NÚMEROS. Contar en la base es barato;
 * descargar cuatro listas enteras para contarlas en el teléfono, no — y abrir
 * el inicio es lo primero que hace todo el mundo, con datos móviles.
 */
export interface NoveltyQuery {
  /** «YYYY-MM-DDTHH:mm:ss.sssZ» de la última visita, o nada si nunca entró. */
  memes?: string;
  challenges?: string;
  posts?: string;
  tournaments?: string;
}

/** Cuántas cosas nuevas hay en cada acceso rápido. Cero = no se dibuja nada. */
/**
 * EL TOPE DE LO QUE SE DIBUJA EN EL GLOBITO: «9+».
 *
 * Un globito con «143» no cabe en la esquina de una tarjeta y además no dice
 * nada que «muchas» no diga: nadie decide entrar distinto con 143 que con 12.
 * El número exacto importa cuando es chico —«2» es «leelos ahora»— y deja de
 * importar en cuanto pasa de un puñado.
 *
 * VIVE EN EL CONTRATO porque lo usan los dos lados: el servidor no recorta
 * —manda el número de verdad— y la pantalla decide cómo dibujarlo. Tenerlo en
 * un solo lugar es lo que evita que uno diga «9+» y el otro «99+».
 */
export const MAX_EN_EL_GLOBITO = 9;

export interface Novelties {
  memes: number;
  challenges: number;
  posts: number;
  tournaments: number;
}

// Los memes
// ---------------------------------------------------------------------

/**
 * CUÁNTO VIVE UN MEME. Un día.
 *
 * ES EL DISEÑO ENTERO, no un detalle. Un muro que se borra solo no acumula
 * historial que moderar, no premia al que publica mucho, y hace que valga la
 * pena entrar hoy. Un meme viejo no le interesa a nadie; un muro viejo lleno
 * de memes es una carga que alguien tiene que limpiar.
 */
export const MEME_LIFE_HOURS = 24;

export const MEME_LIMITS = {
  /** El pie. Corto a propósito: es un meme, no una columna de opinión. */
  captionMax: 280,
  /** Lo que escribe quien reporta. */
  reportNoteMax: 500,
  /**
   * Y ES OBLIGATORIO. Tres letras: lo mínimo para que sea una palabra y no una
   * tecla suelta.
   *
   * Esto era opcional y se pidió cambiar. El costo está medido: pedir algo
   * escrito hace que alguna gente no reporte. Lo que se gana es que quien
   * revisa sepa QUÉ mirar — la misma foto es un meme de tenis o un insulto
   * según a quién retrate, y «alguien avisó» no alcanza para decidir.
   */
  reportNoteMin: 3,
  /** Cuántos trae una página del muro. */
  pageSize: 20,
} as const;

/** El campo del formulario con la imagen del meme. */
export const MEME_IMAGE_FIELD = 'image';

/**
 * LA CLÁUSULA QUE SE ACEPTA AL PUBLICAR.
 *
 * VIVE EN EL CONTRATO Y NO EN EL CATÁLOGO DE TEXTOS, y es a propósito: es una
 * regla del producto, no una etiqueta de pantalla. Traducida a nueve idiomas
 * sería nueve reglas que se pueden separar, y la que manda cuando alguien
 * reclama tiene que ser una sola. La pantalla la muestra tal cual.
 */
export const MEME_RULE_ES =
  'Solo contenido relacionado con el tenis. Si se detecta comportamiento indebido, ' +
  'la administración puede bajar la publicación y silenciar el perfil por un tiempo.';

/** Un meme del muro, con lo que hace falta para dibujarlo. */
export interface Meme {
  id: string;
  authorId: string;
  authorName: string;
  authorAvatarPath: string | null;
  /** Relativa a la base de la API — ver ASSET_PATH_PREFIX. */
  imagePath: string;
  caption: string | null;
  createdAt: string;
  /** Cuándo se va. La pantalla muestra «le quedan N horas» con esto. */
  expiresAt: string;
  reactions: WallReactionCount[];
  comments: MatchComment[];
  /** ¿Es mío? Decide si se ofrece borrarlo. */
  mine: boolean;
  /**
   * ¿Puede quien mira bajarlo? Solo la administración de la plataforma, y
   * quien lo publicó sobre el suyo. Lo decide el servidor: un botón que la
   * pantalla se inventa es un 403 con forma de botón.
   */
  canRemove: boolean;
  /** ¿Ya lo reporté? Para no ofrecer reportar dos veces. */
  reportedByMe: boolean;
}

/** Una página del muro de memes. */
export interface MemePage {
  memes: Meme[];
  /** Cuántas páginas hay. Al menos 1, aunque esté vacío. */
  pages: number;
  page: number;
}

/** `POST /memes` — el pie va en el mismo formulario que la imagen. */
export interface CreateMemeRequest {
  caption?: string;
}

/** `POST /memes/:id/reports` */
export interface ReportMemeRequest {
  note?: string;
}

/**
 * UN REPORTE, como lo ve la administración de la plataforma.
 *
 * TRAE EL MEME ADENTRO y no solo su id: quien revisa tiene que ver la imagen
 * para decidir, y pedirla aparte sería una consulta por reporte y una pantalla
 * que carga de a pedazos.
 */
export interface MemeReport {
  id: string;
  meme: Meme;
  reporterId: string;
  reporterName: string;
  note: string | null;
  createdAt: string;
  /** `null` mientras nadie lo haya mirado. */
  handledAt: string | null;
}

/**
 * EL SILENCIO DE UN PERFIL, como se guarda y como se muestra.
 *
 * QUÉ SIGNIFICA: no publica memes, no comenta y no reacciona. NADA MÁS. Sigue
 * entrando, sigue viendo todo, sigue jugando torneos y retos, y sus puntos no
 * se tocan. El problema era lo que escribe, no que juegue.
 */
export interface MuteState {
  /** `null` = no está callado. ISO. */
  until: string | null;
  /** Lo que la administración dejó escrito. Se le muestra a la persona. */
  reason: string | null;
}

/** `PUT /admin/users/:id/mute` — `days: 0` lo levanta. */
export interface SetMuteRequest {
  /** Cuántos días. Cero o menos levanta el silencio. */
  days: number;
  reason?: string;
}

export const MUTE_LIMITS = {
  /** Un día es el mínimo con sentido; menos no se nota. */
  daysMin: 1,
  /** Noventa: más que eso no es un silencio, es una expulsión sin nombre. */
  daysMax: 90,
  reasonMax: 500,
} as const;

/** `PUT /wall/matches/:id/reactions` — poner o quitar, según `on`. */
export interface SetReactionRequest {
  emoji: string;
  on: boolean;
}

// ---------------------------------------------------------------------
// El cara a cara
// ---------------------------------------------------------------------

/**
 * Una cuenta de dos lados: lo mío primero, lo del otro después.
 *
 * SIEMPRE EN ESE ORDEN, en toda la respuesta. Un objeto que a veces pone
 * primero al que mira y a veces al otro es cómo una pantalla termina
 * mostrando el porcentaje del rival como si fuera el propio.
 */
export interface RivalryTally {
  mine: number;
  theirs: number;
}

/**
 * `GET /players/:id/rivalry` — cuántas veces jugamos y cómo nos fue.
 *
 * DOS PUERTAS: `:id/rivalry` es «quien mira contra `:id`», y
 * `:id/rivalry/:otherId` es entre dos personas cualesquiera.
 *
 * LA SEGUNDA NO EXISTÍA, Y NO POR OLVIDO. Acá decía que «el cara a cara de
 * dos terceros no es una pregunta que la aplicación tenga que contestar, y
 * ponerlo en la URL invita a recorrer las rivalidades de todo el club».
 *
 * SE ABRIÓ, y el argumento que lo contesta es que los resultados YA son
 * públicos: el tablón muestra cada partido con los dos nombres y el marcador
 * a cualquiera con cuenta. Contar los que ya se ven no revela nada nuevo —
 * solo ahorra contarlos a mano. Javier: «eso es información publica».
 *
 * LO QUE NO CAMBIÓ es la guarda de esconderse: las dos personas pasan por
 * `findPublic`, así que quien se escondió no aparece ni como sujeto ni como
 * rival.
 *
 * SALE DEL HISTORIAL Y NO DE UNA TABLA NUEVA. Los partidos ya están
 * guardados; esto es la misma lista mirada de otra forma. Una tabla de
 * «rivalidades» sería un segundo lugar donde vive el mismo hecho, y tarde o
 * temprano diría otra cosa que el cuadro.
 */
export interface Rivalry {
  /**
   * EL PRIMERO DE LOS DOS, el dueño de `mine`.
   *
   * Cuando el cara a cara es «yo contra alguien» esto es quien mira, y la
   * pantalla ya sabe su nombre. Entre dos terceros NO lo sabe: poner ahí el
   * nombre de quien mira sería mentir en la línea más visible, así que el
   * servidor lo manda.
   */
  subjectName: string;
  subjectAvatarPath: string | null;
  opponentUserId: string;
  opponentName: string;
  /** Su foto. Sale de los partidos, igual que el nombre. */
  opponentAvatarPath: string | null;
  /** Cuántos partidos hay registrados entre los dos. */
  played: number;
  /** Partidos ganados y perdidos. */
  matches: RivalryTally;
  /** Sets ganados y perdidos. Un walkover no tiene sets y no cuenta acá. */
  sets: RivalryTally;
  /**
   * Games ganados y perdidos.
   *
   * EL SÚPER TIEBREAK NO SON GAMES. Un «10-8» es un desempate que reemplaza
   * al tercer set, no diez games; sumarlo infla la cuenta de games de
   * cualquiera que juegue formatos cortos. Cuenta en `tiebreaks`.
   */
  games: RivalryTally;
  /**
   * Desempates ganados y perdidos: los sets 7-6 y los súper tiebreaks.
   *
   * Es el número que la gente busca de verdad: quién aguanta cuando el
   * partido se pone parejo.
   */
  tiebreaks: RivalryTally;
  /**
   * Partidos que quedaron EMPATADOS, que no son de nadie.
   *
   * Van aparte y no dentro de `matches` porque un empate no es media victoria
   * para cada uno: si se repartiera, dos empates se leerían como un partido
   * ganado. Solo pueden salir de un amistoso.
   */
  drawn: number;
  /**
   * Cuántos hay de cada clase, para los chips de filtrar de arriba.
   *
   * SE CUENTAN SOBRE EL TOTAL Y NO SOBRE LO FILTRADO: el chip tiene que decir
   * cuántos hay si lo tocás, no cuántos quedan de lo que ya elegiste.
   */
  byKind: Readonly<Record<PlayedMatchKind, number>>;
  /** Los partidos entre los dos, del más nuevo al más viejo. */
  history: PlayedMatch[];
}

/**
 * EL CARA A CARA, CONTADO A PARTIR DEL HISTORIAL.
 *
 * NO HAY TABLA DE RIVALIDADES. Los partidos ya están guardados; esto es la
 * misma lista mirada de otra forma. Una tabla aparte sería un segundo lugar
 * donde vive el mismo hecho, y tarde o temprano diría algo distinto del
 * cuadro — que es lo que la gente cree.
 *
 * VIVE EN EL CONTRATO, como `checkScore` y `enterableCategories`, y por la
 * misma razón: la usan LOS DOS LADOS. El servidor para armar la respuesta, y
 * la pantalla para volver a contar cuando alguien toca un chip de filtrar.
 * Escrita dos veces, un día el total de la pantalla no coincide con el que
 * mandó el servidor y nadie sabe cuál creer.
 *
 * `partidos` son los de quien mira —ver `PlayedMatch`, que trae el marcador
 * con SU número primero—, así que acá «mine» siempre es de quien mira. Esa es
 * toda la razón por la que este cálculo puede ser tan simple.
 *
 * `kinds` filtra por clase. Vacío o ausente = todas.
 */
export function rivalryOf(
  partidos: readonly PlayedMatch[],
  opponentUserId: string,
  kinds?: readonly PlayedMatchKind[],
): Rivalry | null {
  const contraÉl = partidos.filter((p) => p.opponentUserId === opponentUserId);
  if (contraÉl.length === 0) return null;

  /*
   * LOS CHIPS CUENTAN SOBRE EL TOTAL, no sobre lo filtrado. Se calcula antes
   * de aplicar el filtro a propósito: si contaran lo filtrado, al elegir
   * «amistosos» los demás chips dirían cero y ya no se podría volver.
   */
  const byKind: Record<PlayedMatchKind, number> = {
    TOURNAMENT: 0,
    CHALLENGE_RANKING: 0,
    CHALLENGE_FRIENDLY: 0,
  };
  for (const partido of contraÉl) byKind[partido.kind] += 1;

  const suyos =
    kinds === undefined || kinds.length === 0
      ? contraÉl
      : contraÉl.filter((p) => kinds.includes(p.kind));

  const matches = ceroTally();
  const sets = ceroTally();
  const games = ceroTally();
  const tiebreaks = ceroTally();
  let drawn = 0;

  for (const partido of suyos) {
    if (partido.outcome === 'DRAW') drawn += 1;
    else sumarTally(matches, partido.outcome === 'WON');
    // Un walkover no tiene sets: `score` viene vacío y el bucle no corre. No
    // hace falta saltearlo a mano, y no se saltea a propósito — el partido SÍ
    // cuenta como ganado o perdido, que es lo que pasó.
    contarSets(partido.score, sets, games, tiebreaks);
  }

  return {
    /*
     * EL SUJETO NO SALE DE LOS PARTIDOS: `PlayedMatch` describe al RIVAL —el
     * otro lado siempre fue implícito, «el dueño de esta lista»—. Así que acá
     * va vacío y lo rellena quien sí lo sabe: el servicio, que acaba de leer
     * ese perfil para comprobar los permisos. Meterlo en `PlayedMatch` sería
     * repetir el mismo nombre en cada fila de un historial de cien partidos.
     */
    subjectName: '',
    subjectAvatarPath: null,
    opponentUserId,
    // De `contraÉl` y no de `suyos`: con un filtro que no deja nada, el
    // nombre del rival tiene que seguir estando para poder dibujar la cabecera.
    opponentName: contraÉl[0]?.opponentName ?? '',
    opponentAvatarPath: contraÉl[0]?.opponentAvatarPath ?? null,
    played: suyos.length,
    matches,
    sets,
    games,
    tiebreaks,
    drawn,
    byKind,
    // Del más nuevo al más viejo: lo último que pasó es lo que se busca.
    history: [...suyos].sort((a, b) => b.playedOn.localeCompare(a.playedOn)),
  };
}

/**
 * ¿ESTE SET SE DEFINIÓ EN UN DESEMPATE?
 *
 * Dos formas, y las dos cuentan:
 *
 *  · 7-6 — el tiebreak de toda la vida al final de un set.
 *  · 10-8, 11-9, 7-10… — el súper tiebreak que reemplaza al tercer set. Se
 *    reconoce porque alguien pasó de 7, que en un set normal no ocurre.
 *
 * 7-5 NO ES un tiebreak: se ganó quebrando, no desempatando. Y 6-4 tampoco.
 */
export function esDesempate(set: SetScore): boolean {
  const alto = Math.max(set.home, set.away);
  const bajo = Math.min(set.home, set.away);
  return (alto === 7 && bajo === 6) || alto >= 10;
}

/**
 * ¿ES UN SÚPER TIEBREAK, el que reemplaza al set entero?
 *
 * Importa porque SUS PUNTOS NO SON GAMES. Un «10-8» es un desempate, no diez
 * games ganados; sumarlo a la cuenta de games infla el número de cualquiera
 * que juegue formatos cortos, y ese número es el que se compara contra el de
 * quien juega a tres sets completos.
 */
export function esSuperDesempate(set: SetScore): boolean {
  return Math.max(set.home, set.away) >= 10;
}

/**
 * CONTRA QUIÉN JUGÓ Y CUÁNTAS VECES, todo de una pasada.
 *
 * Lo usa la lista de Retos para poner «vs 3» al lado de cada persona. Se
 * devuelve un `Map` y no una lista de rivalidades enteras porque ahí solo
 * hace falta el número: armar cincuenta rivalidades completas para mostrar
 * cincuenta números sería contar sets que nadie va a mirar.
 */
export function playedTogetherCounts(partidos: readonly PlayedMatch[]): Map<string, number> {
  const cuenta = new Map<string, number>();
  for (const partido of partidos) {
    /*
     * LOS INVITADOS NO ENTRAN EN LA CUENTA, y no por descuido.
     *
     * Sin este salto, todos los partidos contra invitados caerían bajo la
     * misma clave nula y el «vs 3» de la lista de Retos diría que jugaste
     * tres veces contra alguien con quien jugaste una — porque las otras dos
     * fueron contra dos señores distintos que no tienen cuenta.
     */
    if (partido.opponentUserId === null) continue;
    cuenta.set(partido.opponentUserId, (cuenta.get(partido.opponentUserId) ?? 0) + 1);
  }
  return cuenta;
}

function ceroTally(): RivalryTally {
  return { mine: 0, theirs: 0 };
}

function sumarTally(cuenta: RivalryTally, mío: boolean): void {
  if (mío) cuenta.mine += 1;
  else cuenta.theirs += 1;
}

function contarSets(
  score: MatchScore,
  sets: RivalryTally,
  games: RivalryTally,
  tiebreaks: RivalryTally,
): void {
  for (const set of score) {
    // Un set empatado no existe en tenis, pero un marcador cargado a mano sí
    // puede traerlo. No se le regala a nadie: no cuenta para ningún lado.
    if (set.home !== set.away) sumarTally(sets, set.home > set.away);

    if (esSuperDesempate(set)) {
      sumarTally(tiebreaks, set.home > set.away);
      continue; // Ver `esSuperDesempate`: sus puntos NO son games.
    }

    games.mine += set.home;
    games.theirs += set.away;
    if (esDesempate(set)) sumarTally(tiebreaks, set.home > set.away);
  }
}


/**
 * UN TORNEO QUE YA TERMINÓ, en la lista del historial.
 *
 * NO ES UN `Tournament` ENTERO a propósito. La ficha trae inscritos,
 * patrocinadores, categorías y fechas límite: para una lista de años de
 * torneos eso son cientos de kilobytes de cosas que la lista no dibuja. Acá
 * va lo que se lee en la fila y nada más; quien quiera el resto entra.
 *
 * `startsAt` ES UN DÍA («2026-10-11»), no un instante, y de ahí salen el mes
 * y el año. El texto del mes lo pone la pantalla en el idioma de quien mira:
 * mandar «octubre» armado congelaría el idioma del día en que se jugó.
 */
export interface TournamentHistoryRow {
  id: string;
  name: string;
  communityId: string;
  communityName: string;
  startsAt: string;
  /** Cuánta gente jugó. Es lo que dice de qué tamaño fue ese torneo. */
  entryCount: number;
}

/**
 * UN PARTIDO MÍO QUE TODAVÍA NO SE JUGÓ, con el contexto para entenderlo.
 *
 * `Match` solo describe el partido; visto en la ficha de un torneo eso
 * alcanza, porque el torneo está arriba en la pantalla. En una lista que
 * junta los partidos de TODOS mis torneos, un partido sin decir de cuál es no
 * se puede leer: hace falta el nombre del torneo y el del club.
 *
 * `kind` existe desde el primer día con un solo valor posible. Es a
 * propósito: esta lista va a juntar los partidos de torneo con los retos
 * —amistosos y por ranking— y el día que eso pase, el que lee esta lista no
 * tiene que adivinar de dónde salió cada partido. Un campo que hoy siempre
 * dice lo mismo es más barato que agregarlo después a una pantalla que ya
 * está en uso.
 */
export interface UpcomingMatch {
  kind: 'TOURNAMENT';
  tournamentId: string;
  tournamentName: string;
  communityName: string;
  match: Match;
}

export interface SaveMatchScoreRequest {
  /** Los sets, en orden. Mandar `[]` borra el resultado y deshace el avance. */
  score: MatchScore;
  /**
   * O, EN VEZ DE SETS: quién no se presentó.
   *
   * Va por el MISMO endpoint que el marcador y no por uno aparte, porque las
   * dos cosas contestan la misma pregunta —cómo terminó este partido— y las
   * dos hacen avanzar a alguien. Con dos endpoints habría dos lugares que
   * mueven el cuadro, y tarde o temprano uno de los dos se olvida de limpiar
   * lo que venía después.
   *
   * `score` tiene que venir vacío: un partido no puede tener sets y a la vez
   * no haberse jugado. `null` o ausente borra el W.O.
   */
  noShowEntryId?: string | null;
}

// ---------------------------------------------------------------------
// Ranking
// ---------------------------------------------------------------------

/**
 * CUÁNTOS PUNTOS VALE GANAR UN TORNEO: doscientos cincuenta, el que sea.
 *
 * Lo pidió Javier con estas palabras: «que el tope de puntos por torneo
 * llegue a 250 sin importar la cantidad de jugadores», y eligió que fuera
 * parejo para todos los tamaños, no un techo con los chicos abajo.
 *
 * ESTO ERA AL REVÉS HASTA HOY, y conviene decir qué se perdió. La tabla
 * copiaba al ATP —128:1000, 64:750, 32:500, 16:300, 8:150, 4:75, 2:40— para
 * que llegar a la final de un torneo grande valiera más que ganar uno chico,
 * porque hubo que ganarle a más gente. Ese argumento ya no está: en un club
 * donde cada torneo vale lo mismo, entrar al difícil no paga más que entrar
 * al fácil.
 *
 * LO QUE HAY QUE VIGILAR, dicho claro: un cuadro de 2 son DOS personas y UN
 * partido, y ahora paga los mismos 250 que un cuadro de 128. Armar torneos
 * de dos en serie es la forma barata de inflar el ranking, y el código no lo
 * frena — nada acá mira cuánta gente jugó. Si alguna vez aparece, el arreglo
 * natural es un tamaño mínimo para que el torneo cuente, no volver a la
 * escala.
 *
 * EL TAMAÑO SIGUE IMPORTANDO PARA LAS RONDAS, y eso no cambió: en un cuadro
 * de 128 hay que ganar siete partidos para llevarse los 250 y en uno de 2
 * basta uno, pero quien cae en la primera ronda de 128 se lleva el 1 % y
 * quien cae en la primera de 8 se lleva el 20 %. La escalera vive en
 * `RANKING_SHARE_BY_ROUND`.
 */
export const RANKING_BASE_BY_SIZE: Readonly<Record<BracketSize, number>> = {
  128: 250,
  64: 250,
  32: 250,
  16: 250,
  8: 250,
  4: 250,
  2: 250,
} as const;

/**
 * Qué parte de esa base se lleva quien llegó hasta ahí.
 *
 * Se indexa por CUÁNTA GENTE QUEDABA en la ronda donde lo eliminaron: 2 es la
 * final (le ganó al que quedó segundo), 4 la semifinal, y así. Quien pierde
 * la final se llevó el 65 % y quien pierde en la ronda de 32 el 5 %, valga
 * ese 32 lo que valga según el tamaño del cuadro. Son las proporciones del
 * ATP (2000 / 1300 / 800 / 400 / 200 / 100 / 50), que llevan cuarenta años
 * puestas a prueba.
 */
export const RANKING_SHARE_BY_ROUND: Readonly<Record<number, number>> = {
  /** Campeón. */
  1: 1,
  /** Perdió la final. */
  2: 0.65,
  4: 0.4,
  8: 0.2,
  16: 0.1,
  32: 0.05,
  64: 0.025,
  128: 0.01,
} as const;

/** Cuántos días vale un resultado. Un año: el ranking dice quién juega bien AHORA. */
export const RANKING_WINDOW_DAYS = 364;

// --- Lo que vale un reto ----------------------------------------------

/*
 * ESTAS TRES CONSTANTES VIVÍAN EN `apps/api/src/domain/challenge-points.ts`.
 *
 * Se mudaron acá cuando apareció la pantalla que EXPLICA cómo funcionan los
 * puntos: esa pantalla tiene que decir los mismos números que usa la cuenta, y
 * escribirlos a mano en el texto es la forma garantizada de que el día que
 * cambie uno, la explicación siga diciendo el viejo. El contrato es el único
 * lugar que ven el servidor y la pantalla.
 *
 * LA CUENTA SIGUE EN EL SERVIDOR: acá están los números, no la fórmula.
 */

/**
 * LO QUE VALE GANAR UN RETO **PAREJO**, que es el partido normal.
 *
 * ERA EL TECHO Y AHORA ES EL CENTRO, y el cambio de significado es el
 * arreglo. Antes valía 25 y era «la victoria perfecta»: el máximo se
 * alcanzaba ganándole a alguien muchísimo mejor —el caso raro— y un partido
 * entre iguales pagaba la mitad. Javier lo leyó al revés, y con razón:
 * «Ganar paga 25», esperando que fuera lo que da un reto normal.
 *
 * TRECE, PORQUE ESA ES SU CALIBRACIÓN y no se toca: unas veinte victorias
 * parejas igualan un torneo (250). Antes la base era 25 y lo parejo pagaba
 * 12.5 redondeado a 13; ahora la base ES 13 y lo parejo paga 13. El número
 * que cobra la gente por un reto normal no cambió — cambió cuál de los dos
 * números lleva el nombre de «la base».
 *
 * LA ESCALA SE MUEVE ALREDEDOR DE ESTO: ganarle a alguien superior paga hasta
 * 1.6 veces esto, y a alguien inferior la mitad. Ver `rawWinPoints`, que es
 * donde viven esos dos extremos.
 *
 * Contra rivales DISTINTOS, que es lo que el descuento `1/n` obliga. Veinte
 * victorias parejas contra veinte personas distintas son una temporada, no
 * una tarde.
 */
export const CHALLENGE_WIN_POINTS = 13;

/**
 * CUÁNTA DIFERENCIA DE PUNTOS ES «UNA CATEGORÍA», para la escala.
 *
 * La constante de Elo adaptada a esta escala: con 200 puntos de ventaja se
 * gana ~9 de cada 10.
 */
export const CHALLENGE_RIVAL_SCALE = 200;

/**
 * LOS DOS EXTREMOS DE LO QUE PAGA UNA VICTORIA, como fracción de un parejo.
 *
 * Ganarle a alguien muy por debajo deja algo menos de la mitad; a alguien muy
 * por encima, algo más de vez y media.
 *
 * SIMÉTRICOS ALREDEDOR DE 1: en un partido parejo la sorpresa vale 0.5, así
 * que el factor es el PROMEDIO de los dos, y con estos da exactamente 1 — lo
 * parejo paga la base clavada.
 *
 * EL PISO ES EL MÍNIMO QUE CUMPLE «GANAR SIEMPRE PAGA MÁS QUE PERDER»: da
 * seis contra los cinco de una derrota. Fue una decisión de Javier sabiendo
 * el costo de subirlo: con la mitad exacta, cien victorias contra los más
 * flojos del club darían setecientos puntos.
 */
export const CHALLENGE_WIN_FLOOR = 0.47;
export const CHALLENGE_WIN_CEILING = 1.53;

/**
 * LA PROBABILIDAD DE QUE GANE QUIEN TIENE `minePoints`. Elo de toda la vida.
 *
 * VIVE EN EL CONTRATO porque la usan los dos lados: el servidor para calcular
 * los puntos y la pantalla del sistema de puntaje para EXPLICAR la escala con
 * los números de verdad. Escrita dos veces, el texto empieza a mentir el día
 * que cambie un extremo.
 */
export function expectedScore(minePoints: number, theirPoints: number): number {
  return 1 / (1 + 10 ** ((theirPoints - minePoints) / CHALLENGE_RIVAL_SCALE));
}

/**
 * LO QUE VALE GANAR UN RETO, antes del descuento por repetir rival.
 *
 * `CHALLENGE_WIN_POINTS` es lo que paga un PAREJO, y la sorpresa mueve ese
 * número entre el piso y el techo. Ver `CHALLENGE_WIN_FLOOR`.
 */
export function rawWinPoints(winnerPoints: number, loserPoints: number): number {
  const sorpresa = 1 - expectedScore(winnerPoints, loserPoints);
  const factor =
    CHALLENGE_WIN_FLOOR + (CHALLENGE_WIN_CEILING - CHALLENGE_WIN_FLOOR) * sorpresa;
  return Math.max(1, Math.round(CHALLENGE_WIN_POINTS * factor));
}

/**
 * LO QUE DEJA UNA DERROTA. Antes: cero.
 *
 * Cinco, que sale de la otra cuenta de Javier: cincuenta derrotas igualan ese
 * mismo torneo. 250 / 50 = 5.
 *
 * AHORA ESCALA CON EL RIVAL, Y ANTES NO. Acá decía que era fija, con este
 * argumento —que se deja escrito porque era razonable cuando se escribió—:
 * «podría valer más perder contra el primero que contra el último, pero
 * entonces habría que explicarlo».
 *
 * LO QUE CAMBIÓ ES QUE AHORA SE PUEDE RETAR ENTRE CATEGORÍAS. Con todos en
 * la misma, «perder es perder» se sostenía: los rivales eran parecidos. Con
 * un C jugando contra un A deja de sostenerse, porque el mismo 5 le tocaba al
 * Open que perdió contra un C2 y al C2 que perdió contra el Open, que son dos
 * cosas muy distintas.
 *
 * ESTE NÚMERO ES EL DEL PARTIDO PAREJO, y eso conserva la cuenta de Javier:
 * cincuenta derrotas parejas siguen siendo un torneo. Perder contra alguien
 * muy superior llega a diez; contra alguien muy inferior baja a uno.
 *
 * LE APLICA EL MISMO DESCUENTO POR REPETIR RIVAL que a las victorias. Sin
 * eso, perder cincuenta veces contra el mismo amigo son 250 puntos en una
 * tarde.
 */
export const CHALLENGE_LOSS_POINTS = 5;

/**
 * CUÁNTO DE ESO SE LLEVA UN AMISTOSO. Antes: nada, no contaban.
 *
 * Dos tercios: unos ocho al ganar parejo, unos tres al perder. Javier quiso
 * que los amistosos motivaran a jugar, y cero no motiva nada.
 *
 * MENOS QUE EL DE RANKING, Y NO MÁS. Un amistoso se arregla con quien uno
 * quiera y no le mueve el puesto a nadie; si valiera igual o más, nadie
 * usaría el reto por ranking, que es el que ordena la tabla. Suman, pero el
 * que cuenta en serio sigue conviniendo.
 */
export const FRIENDLY_FACTOR = 2 / 3;


/**
 * Los puntos de una persona en su club, sin la tabla alrededor.
 *
 * Va aparte de `RankingRow` porque responde otra pregunta. `RankingRow` es
 * «cómo va la tabla de C masculino», con puesto y cantidad de torneos;
 * esto es «cuántos puntos tiene cada quien», que es lo que el listado de
 * jugadores necesita para ordenarse y no depende de ninguna categoría.
 */
/**
 * LOS PUNTOS DE ALGUIEN EN UNA TABLA. UNA FILA POR TABLA, NO POR PERSONA.
 *
 * ANTES ERA UNA POR PERSONA, y con varios rankings por club eso se volvió
 * mentira: quien tiene 300 en B y 80 en C no tiene 380 en ningún lado. Ese
 * número —la suma de dos tablas que nunca se comparan— salía en el perfil
 * y en la lista de socios como si fuera el del ranking.
 *
 * POR ESO VIAJA LA TERNA. Sin ella la fila no se puede juntar con nada: la
 * lista del club trae una fila por inscripción, y para ponerle su número al
 * lado hay que saber de QUÉ tabla es cada puntaje.
 */
export interface PlayerPoints {
  userId: string;
  /** La categoría ASIGNADA en el club: la tabla donde cuentan estos puntos. */
  level: CategoryLevel;
  /** El sexo DE LA TABLA, que puede no ser el de la persona. */
  sex: CategorySex;
  /** Singles o dobles: son dos tablas distintas del mismo nivel y sexo. */
  discipline: CategoryDiscipline;
  /**
   * El total: torneos, retos por ranking y ajustes. Es el número del ranking,
   * EL MISMO que sale en `RankingRow.points` — y tiene que seguir siéndolo:
   * dos números distintos para la misma persona según qué pantalla se mire es
   * cómo se deja de creer en los dos.
   */
  points: number;
  /**
   * Cuántos de esos puntos vienen de retos por ranking.
   *
   * VIAJA APARTE PARA PODER DECIRLO. «¿Por qué tiene 300?» es la pregunta que
   * mata un ranking cuando no tiene respuesta; con el desglose, la tabla puede
   * mostrar «de los cuales 24 de retos» y se acabó la discusión.
   */
  challengePoints: number;
  /**
   * Y cuántos vienen de ajustes. PUEDE SER NEGATIVO: una penalidad por dejar
   * vencer un reto resta. Viaja aparte por lo mismo que los de reto: «¿por qué
   * tiene 300?» es la pregunta que mata un ranking cuando no tiene respuesta.
   */
  adjustmentPoints: number;
}

/** Una línea del ranking de una categoría. */
export interface RankingRow {
  userId: string;
  displayName: string;
  avatarPath: string | null;
  level: CategoryLevel;
  /**
   * LA TABLA A LA QUE PERTENECE ESTA FILA, que no es «el sexo de esta
   * persona»: una mujer inscrita en el ranking masculino aparece en ESE, con
   * `MALE`, porque es donde compite. En dobles mixtos el cuadro es otro.
   */
  sex: CategorySex;
  /** De cuál de las dos tablas es esta fila: la de singles o la de dobles. */
  discipline: CategoryDiscipline;
  /** El total: torneos, retos por ranking y ajustes. */
  points: number;
  /** Cuántos de esos puntos vienen de retos por ranking. Ver `PlayerPoints`. */
  challengePoints: number;
  /**
   * CUÁNTOS VIENEN DE AJUSTES, y puede ser NEGATIVO.
   *
   * Penalidades del club, puntos con los que alguien entró trayendo un ranking
   * de afuera, y correcciones de quien administra. Viaja aparte por la misma
   * razón que `challengePoints`: «¿por qué perdí cinco puntos?» es la pregunta
   * que mata un ranking cuando no tiene respuesta.
   *
   * El total puede quedar negativo y se muestra así: recortarlo en cero
   * escondería que la penalidad se aplicó, y quien la recibió vería el mismo
   * cero que alguien que nunca jugó.
   */
  adjustmentPoints: number;
  /** En cuántos torneos sumó, dentro de la ventana. */
  tournaments: number;
  /** La posición, empezando en 1. Empatados comparten posición. */
  position: number;
}

/**
 * Alguien que jugó por encima de su categoría en un torneo.
 *
 * Sus puntos suman en la categoría que tiene ASIGNADA, no en la que jugó
 * —así el ranking de B no se llena de gente de C— pero el club tiene que
 * enterarse: quien llega lejos jugando arriba es justo a quien hay que subir.
 */
export interface PlayedUpNotice {
  userId: string;
  displayName: string;
  assignedLevel: CategoryLevel;
  playedLevel: CategoryLevel;
  tournamentId: string;
  tournamentName: string;
  /** Cuánta gente quedaba cuando lo eliminaron. 1 = lo ganó. */
  reachedRound: number;
}

// ---------------------------------------------------------------------
// Comisión de la plataforma
// ---------------------------------------------------------------------

/**
 * Lo que Tennis Up Club le cobra a los clubes. Lo fija quien administra la plataforma.
 *
 * CUATRO CONCEPTOS, Y CADA UNO MIDE ALGO DISTINTO:
 *
 *  · por inscripción — cuánta gente jugó. Crece con el torneo.
 *  · por segunda categoría — quien tomó dos, la segunda paga aparte.
 *  · POR TORNEO — un monto por armarlo, sin importar cuánta gente vino.
 *  · POR CATEGORÍA DEL TORNEO — cuántos cuadros corre. Un torneo con seis
 *    categorías es seis veces el trabajo de uno con una, aunque tenga la
 *    misma cantidad de gente.
 *
 * Los dos últimos son lo que pidió Javier: «cobrar por torneos y no solo por
 * categorías, dependiendo de la cantidad de categorías». Arrancan en cero, y
 * en cero la cuenta da exactamente lo mismo que antes: nadie que tenga sus
 * tarifas puestas se despierta cobrando distinto.
 */
/**
 * QUÉ PARTES DE LA APP ESTÁN PRENDIDAS.
 *
 * APARTE DE `PlatformSettings` Y NO UN CAMPO MÁS ADENTRO. Aquel tipo son
 * TARIFAS, y se reusa tal cual como la tarifa propia de un club
 * (`CommunityCommission.rates`): meter acá el interruptor de los memes le daría
 * a cada club un campo que no significa nada —¿los memes prendidos «para ese
 * club»?— y que alguien terminaría leyendo.
 *
 * LO LEE CUALQUIERA, SIN SESIÓN. La pantalla de inicio tiene que saber si
 * dibuja la tarjeta antes de saber quién mira, y esconder que un muro de memes
 * existe no protege nada.
 */
export interface PlatformFeatures {
  /** El muro de memes en la app de jugadores. */
  memes: boolean;
}

/** `PUT /admin/settings/features` */
export interface SetPlatformFeaturesRequest extends PlatformFeatures {}

/**
 * LAS TARIFAS, Y SI CADA UNA SE COBRA.
 *
 * POR QUÉ UN INTERRUPTOR Y NO PONER EL MONTO EN CERO. En cero el concepto
 * también suma cero, así que la cuenta da igual — pero apagarlo así PIERDE el
 * monto, y volver a prenderlo obliga a acordarse de cuánto era. Con el
 * interruptor, ₡150 sigue diciendo ₡150 mientras está apagado.
 *
 * Y en el reporte se ven distinto: un cobro en cero desaparece entre los
 * demás, uno apagado se lee como una decisión que alguien tomó.
 *
 * LOS CINCO SON INDEPENDIENTES —incluidos los dos de inscripción— porque un
 * club puede querer cobrar por persona y regalar la segunda categoría, que es
 * justo la promoción que se hace para llenar cuadros vacíos.
 *
 * CADA CLUB CON TARIFA PROPIA TIENE LOS SUYOS: este tipo se reusa tal cual en
 * `CommunityCommission.rates`, así que un club puede tener apagado un concepto
 * que la tarifa general cobra. Es a propósito, y es lo que hay que mirar
 * cuando una cuenta no cuadra con la lista de precios.
 */
export interface PlatformSettings {
  feePerEntryColones: number;
  /** Si se cobra por cada persona inscrita. */
  chargePerEntry: boolean;

  feePerSecondEntryColones: number;
  /** Si se cobra el adicional a quien tomó una segunda categoría. */
  chargePerSecondEntry: boolean;

  feePerSponsorColones: number;
  /** Si se cobra por cada logo en el carrusel del torneo. */
  chargePerSponsor: boolean;

  /** Un monto fijo por torneo, cualquiera sea su tamaño. */
  feePerTournamentColones: number;
  /** Si se cobra ese monto fijo. */
  chargePerTournament: boolean;

  /** Y otro por cada categoría que el torneo corre. */
  feePerTournamentCategoryColones: number;
  /** Si se cobra por cada categoría. */
  chargePerTournamentCategory: boolean;
}

/**
 * La tarifa propia de un club, o `null` si le corresponde la general.
 *
 * Una excepción es una decisión COMPLETA: «a este club se le cobra así». No
 * hay medias excepciones —la comisión por jugador propia y la de patrocinador
 * heredada— porque entonces saber cuánto se le cobra a alguien obligaría a
 * mirar cinco lugares y acordarse de la regla de cada uno.
 *
 * Quitarla devuelve el club al precio de lista.
 */
export interface CommunityCommission {
  communityId: string;
  communityName: string;
  rates: PlatformSettings;
}

/**
 * QUIÉN AYUDA A ORGANIZAR UN TORNEO.
 *
 * Es un organizador PLENO pero SOLO de ese torneo: sortea, anota, revisa
 * inscripciones y pone fechas, ahí y en ningún otro lado. Quien administra el
 * club sigue pudiendo todo en todos sus torneos, esté asignado o no.
 *
 * Se elige entre miembros del club: dar permiso a alguien de afuera sería una
 * puerta lateral a un club al que no entró.
 */
export interface TournamentAdmin {
  userId: string;
  displayName: string;
  avatarPath: string | null;
  /** Quién lo puso, para poder preguntarle. */
  addedByName: string;
  createdAt: string;
}

/** `POST /tournaments/:id/admins` */
export interface AddTournamentAdminRequest {
  userId: string;
}

// ---------------------------------------------------------------------
// Invitaciones a administrar
// ---------------------------------------------------------------------

/**
 * DAR PERMISO DE ADMINISTRAR NO SE HACE DE UN TOQUE: SE INVITA Y SE ACEPTA.
 *
 * QUÉ PROBLEMA RESUELVE. Antes, quien administraba un club marcaba a otra
 * persona como administrador y en ese mismo segundo esa persona lo era, sin
 * enterarse. Administrar no es un adorno: es aprobar pagos, corregir puntos,
 * tirar cuadros y responder cambios de categoría. Que a alguien le caiga
 * encima ese poder sin decir que sí tiene dos lados malos, y el segundo es el
 * peor:
 *
 *  1. La persona no se entera. Descubre meses después que era administradora
 *     de un club, o nunca, y mientras tanto los pedidos se acumulan esperando
 *     a alguien que no sabe que le tocan.
 *
 *  2. QUIEN INVITA PUEDE EQUIVOCARSE DE PERSONA. Dos socios con el mismo
 *     nombre en la lista y el permiso se lo lleva el que no era. Con el
 *     correo de por medio, el error no se convierte en acceso: el enlace le
 *     llega a la dirección de esa cuenta, y el permiso empieza a existir
 *     cuando esa persona lo abre.
 *
 * Javier: «Para dar permiso de administrar prefiero que lo confirmen por
 * correo, al igual que dar permiso de administrar un torneo, hay que enviar
 * correo para que lo acepten mediante un link», «tanto para un club como para
 * un torneo».
 *
 * ES LA MISMA INVITACIÓN PARA LAS DOS COSAS, distinguidas por `scope`. Un
 * club y un torneo son objetos distintos, pero la pregunta que se le hace a
 * la persona es idéntica —«¿aceptás administrar esto?»— y el camino también:
 * un correo, un enlace, un botón. Dos tablas y dos pantallas para la misma
 * pregunta serían dos lugares donde arreglar el mismo error.
 */
export const ADMIN_INVITE_SCOPES = ['CLUB', 'TOURNAMENT'] as const;
export type AdminInviteScope = (typeof ADMIN_INVITE_SCOPES)[number];

/**
 * CUÁNTO VIVE EL ENLACE.
 *
 * Siete días y no una hora como el de la contraseña: ese lo pide la persona y
 * lo está esperando; éste le llega sin haberlo pedido, y puede caer un viernes
 * en la noche o durante unas vacaciones. Un plazo corto acá no protege nada
 * —el permiso no se otorga hasta que alguien lo acepte— y solo garantiza que
 * haya que volver a mandarlo.
 */
export const ADMIN_INVITE_TTL_DAYS = 7;

/**
 * UNA INVITACIÓN A ADMINISTRAR, COMO LA VE QUIEN LA MANDÓ.
 *
 * Aparece en la lista de administradores junto a los que ya lo son, marcada
 * como pendiente. ESO ES LO QUE EVITA QUE SE INVITE DOS VECES: sin verla, la
 * invitación es un correo que se fue y no dejó rastro, y quien administra
 * vuelve a apretar el botón porque no pasó nada visible.
 */
export interface AdminInvitation {
  id: string;
  scope: AdminInviteScope;
  /** A quién se invitó. */
  userId: string;
  displayName: string;
  avatarPath: string | null;
  /** Quién invitó. */
  invitedByName: string;
  /** ISO 8601. Hasta cuándo sirve el enlace. */
  expiresAt: string;
  createdAt: string;
}

/**
 * LO QUE VE QUIEN ABRE EL ENLACE, ANTES DE DECIDIR.
 *
 * No trae `userId` ni nada de quien invita más allá del nombre: es una
 * pantalla que se abre con un token, y todo lo que ponga acá es visible para
 * cualquiera que tenga ese token. Lo justo para saber qué se está aceptando.
 */
export interface AdminInvitationPreview {
  scope: AdminInviteScope;
  /** El nombre del club o del torneo. */
  name: string;
  /** El club del torneo, cuando `scope` es 'TOURNAMENT'. */
  communityName: string | null;
  invitedByName: string;
  expiresAt: string;
}

/** `POST /admin-invitations/:token/accept` */
export interface AcceptAdminInvitationResponse {
  scope: AdminInviteScope;
  /** Adónde mandar a la persona después de aceptar. */
  linkPath: string;
  name: string;
}

/**
 * UNA NOTA INTERNA DE UN TORNEO.
 *
 * Lo que quien organiza necesita acordarse —que la cancha 3 está en
 * refacción, que fulano avisó que llega tarde— y que hoy se pierde en un chat.
 *
 * NO LA VE QUIEN JUEGA: es el punto entero. Una nota que la gente del torneo
 * puede leer es una nota que nadie escribe con franqueza.
 *
 * NO SE BORRA NI SE EDITA, y por eso no hay `updatedAt`: es una bitácora, y lo
 * que sirve de una bitácora es que lo escrito quede.
 */
export interface TournamentNote {
  id: string;
  tournamentId: string;
  authorId: string;
  authorName: string;
  authorAvatarPath: string | null;
  body: string;
  createdAt: string;
}

/** `POST /tournaments/:id/notes` */
export interface AddTournamentNoteRequest {
  body: string;
}

export const TOURNAMENT_NOTE_LIMITS = {
  /**
   * Largo a propósito: acá el detalle es el valor, igual que en los reportes
   * de jugador. Una nota que no entra obliga a partirla en dos y se lee peor.
   */
  bodyMax: 2000,
  bodyMin: 2,
  /**
   * CUÁNTOS ORGANIZADORES por torneo. Diez es más de los que cualquier torneo
   * necesita; el tope existe para que un dedazo en un bucle no convierta al
   * club entero en organizador sin que nadie lo note.
   */
  adminsMax: 10,
} as const;

/** `PUT /admin/communities/:id/commission` */
export interface SetCommunityCommissionRequest extends PlatformSettings {}

/**
 * Por qué estado filtrar el reporte.
 *
 * `ALL` incluye los borradores, que todavía no cobran nada pero se ven en la
 * cuenta como ceros: sirve para revisar antes de facturar. `RUNNING` y
 * `FINISHED` son los que de verdad se cobran.
 */
export const BILLING_FILTERS = ['ALL', 'RUNNING', 'FINISHED'] as const;
export type BillingFilter = (typeof BILLING_FILTERS)[number];

/** La cuenta de un torneo, con lo que se cobró y por qué. */
/**
 * El desglose de cómo llegó el pago de las inscripciones de un torneo.
 *
 * Sirve para lo que quien cobra hace de verdad: mirar un torneo y saber
 * cuánto de lo que factura está respaldado por algo que se puede verificar
 * —una foto del SINPE— y cuánto es palabra de quien organiza.
 */
export interface BillingPayments {
  /** Mandaron la foto del SINPE o del depósito. Es lo único verificable. */
  image: number;
  /** Lo dejaron por escrito: «pagué en efectivo el martes». */
  comment: number;
  /** Todavía sin comprobante. Ver `pendingWithinHour` para la diferencia. */
  pending: number;
  /**
   * De las pendientes, las que TODAVÍA ESTÁN EN HORA.
   *
   * Una inscripción sin comprobante que se anotó sola tiene sesenta minutos
   * antes de caerse. Mientras corre ese reloj no es un problema —es una
   * inscripción recién hecha— y contarla junto a las demás pendientes haría
   * ver un torneo como si tuviera diez morosos cuando tiene diez personas
   * que se anotaron hace cinco minutos.
   */
  pendingWithinHour: number;
  /**
   * Las que anotó quien organiza, y no la persona.
   *
   * VA APARTE Y NO ES UN CUARTO ESTADO DE PAGO: son dos preguntas distintas
   * —«¿pagó?» y «¿quién la anotó?»— y una inscripción puesta por la
   * organización puede tener foto igual. Por eso este número se solapa con
   * los de arriba en vez de sumarse a ellos.
   */
  addedByOrganizer: number;
}

export interface TournamentBilling {
  tournamentId: string;
  tournamentName: string;
  communityName: string;
  startsAt: string;
  /** Personas inscritas en UNA sola categoría. */
  singleCategoryPlayers: number;
  /** Personas inscritas en DOS categorías. Cuentan una vez, no dos. */
  twoCategoryPlayers: number;
  sponsors: number;
  /** Cuántas categorías corre el torneo. */
  categories: number;
  /** En qué anda el torneo. El reporte se puede filtrar por esto. */
  status: TournamentStatus;
  /**
   * CÓMO LLEGÓ EL PAGO DE CADA INSCRIPCIÓN, contado.
   *
   * SON INSCRIPCIONES Y NO PERSONAS, al revés que los dos números de arriba.
   * Es a propósito y hay que leerlo así: quien tomó dos categorías mandó un
   * comprobante por cada una —o ninguno por una y foto por la otra—, así que
   * la pregunta «¿cómo pagó?» se hace por inscripción. Los de arriba
   * contestan «¿a cuánta gente se le cobra?», que es otra cosa; por eso los
   * dos grupos no suman lo mismo y no tienen por qué.
   */
  payments: BillingPayments;
  /** `true` si al club se le aplicó su tarifa propia y no la general. */
  ownRates: boolean;
  rates: PlatformSettings;
  /** Los subtotales y el total, ya calculados por el servidor: la cuenta se hace UNA vez. */
  singleCategoryTotal: number;
  secondCategoryTotal: number;
  sponsorsTotal: number;
  /** El monto fijo por torneo. */
  tournamentTotal: number;
  /** Lo que suman las categorías que corre. */
  categoriesTotal: number;
  total: number;
}

// ---------------------------------------------------------------------
// Perfil
// ---------------------------------------------------------------------

/**
 * Todo lo del perfil que la persona puede cambiar.
 *
 * El correo NO está: cambiarlo es cambiar con qué se inicia sesión y con qué
 * se recupera la cuenta, así que necesita su propio camino con verificación.
 * La contraseña tampoco: va aparte, porque pide la actual.
 */
export interface UpdateProfileRequest {
  displayName: string;
  fullName: string;
  idDocumentType: IdDocumentType;
  idDocumentNumber: string;
  dateOfBirth: string;
  nationalityCode: string;
  /** REQUIRED to save. Reading it back can be `null` — see PlayerProfile. */
  sex: Sex;
  provinceCode: string;
  cantonCode: string;
  districtCode: string;
  addressLine: string | null;
  whatsappPhone: string;
  locale: SupportedLocale;
  theme: Theme;
}

/**
 * `PUT /profile/preferences` — CÓMO ME ENCUENTRAN PARA JUGAR.
 *
 * SON TRES COSAS QUE NO SON «quién soy». El formulario del perfil es la
 * cédula: nombre, documento, dónde vivo. Esto es de qué juego, si quiero que
 * me reten y por dónde avisarme, y se cambia de opinión sobre ello mucho más
 * seguido que sobre el apellido. Javier las mandó a «Más», que es donde
 * viven los ajustes.
 *
 * RUTA PROPIA Y NO PARTE DE GUARDAR EL PERFIL, por lo mismo que el tema y la
 * ubicación: si vivieran en el mismo formulario, guardar el perfil con un
 * valor viejo cargado pisaría lo que se acaba de cambiar en la otra pantalla.
 * Con dos rutas, cada cosa la escribe quien la muestra.
 */
export interface SetPreferencesRequest {
  /**
   * EL NIVEL QUE UNO DICE TENER. `null` es «no lo dije».
   *
   * NO ES LA CATEGORÍA DE NINGÚN CLUB, y no la pisa. Se es «B masculino»
   * DENTRO de un club (`Community.myLevel`), y la misma persona puede ser
   * otra cosa en otro. Que sean dos campos distintos es lo que impide que un
   * jugador se suba de ranking cambiándose el perfil.
   */
  declaredLevel: CategoryLevel | null;
  availableForChallenges: boolean;
  /**
   * ESCONDIDO: QUE NO ME ENCUENTRE QUIEN NO ME CONOCE.
   *
   * NO ES LO MISMO QUE `availableForChallenges`, y por eso son dos. Ese dice
   * «no me mandes retos» y deja el perfil abierto; este dice «no me busques»
   * y deja los retos como estén. Quien quiere las dos cosas prende las dos.
   *
   * ESCONDE DOS COSAS Y NADA MÁS: el buscador de retos deja de devolverme, y
   * mi perfil le responde «no existe» a quien no comparte club conmigo.
   * SIGO en las tablas de ranking de mis clubes y en los cuadros de torneo
   * que ya jugué: esconderse es de los desconocidos, no de la gente con la
   * que uno juega.
   */
  hidden: boolean;
  /**
   * SI LOS AVISOS TAMBIÉN LLEGAN POR CORREO. Prendido de fábrica.
   *
   * NO APAGA LA CAMPANITA: el aviso se guarda igual y se ve adentro de la
   * aplicación. Destildar esto es «no me escribas al correo», no «no me
   * avises» — y son dos cosas que alguien puede querer por separado.
   */
  emailNotifications: boolean;
}

/**
 * El perfil completo, tal como se edita.
 *
 * `sex` puede venir `null` aunque guardarlo lo exija: una cuenta anterior a
 * que existiera el campo no lo tiene, y el formulario tiene que poder abrirse
 * con el select en blanco en vez de mostrar una opción que nadie eligió.
 */
export interface PlayerProfile extends Omit<UpdateProfileRequest, 'sex'> {
  id: string;
  email: string;
  systemRole: SystemRole;
  avatarPath: string | null;
  sex: Sex | null;

  /*
   * LO DE `SetPreferencesRequest`, REPETIDO ACÁ A PROPÓSITO.
   *
   * Son parte del perfil que se LEE —la pantalla de «Más» los dibuja— pero no
   * del formulario del perfil que se ESCRIBE, igual que la ubicación y el
   * club por defecto. Si vinieran heredados de `UpdateProfileRequest`,
   * guardar el perfil tendría que mandarlos, y mandarlos es pisarlos.
   */
  declaredLevel: CategoryLevel | null;
  availableForChallenges: boolean;
  /** Ver `SetPreferencesRequest.hidden`. */
  hidden: boolean;
  emailNotifications: boolean;

  /**
   * LA UBICACIÓN POR GPS, para buscar retos por distancia más adelante.
   *
   * ACÁ Y NO EN `UpdateProfileRequest`, que es lo que la persona escribe en
   * el formulario: esto no se escribe, se mide, y tiene su propio camino
   * (`PUT /profile/location`). Si viviera en el formulario, guardar el perfil
   * sin tocar el botón tendría que mandar las coordenadas viejas —o
   * borrarlas—, y las dos cosas están mal.
   *
   * Aparte de provincia/cantón/distrito y sin reemplazarlos: aquello es una
   * DIRECCIÓN y esto una MEDICIÓN del aparato, que sirve para calcular
   * kilómetros y nada más.
   *
   * SOLO SE LE DEVUELVE A SU DUEÑO. `PlayerProfile` es el perfil propio;
   * ninguna respuesta que mire otra persona lleva coordenadas. Lo que se va a
   * compartir cuando exista la búsqueda es la DISTANCIA ya calculada.
   */
  latitude: number | null;
  longitude: number | null;
  /** ISO 8601: cuándo se tomó, para poder decir «hace tres meses». */
  locationTakenAt: string | null;

  /**
   * El club que abre la pestaña de Torneos. Como la ubicación, NO está en
   * `UpdateProfileRequest`: se elige en su propia pantalla y guardar el
   * formulario del perfil no puede pisarlo.
   */
  defaultCommunityId: string | null;

  /**
   * A QUIÉN LLAMAR Y QUÉ SABER SI LE PASA ALGO EN LA CANCHA.
   *
   * Javier: «es un deporte de riesgo y las personas pueden actuar
   * inmeditamente». Se LEE con el perfil —y es lo único que importa: quien
   * lo necesita lo necesita ya— pero se ESCRIBE por su propio camino
   * (`PUT /profile/emergency`), igual que la ubicación y el club por
   * defecto. Si viviera en el formulario del perfil, guardar la cédula con
   * un valor viejo cargado borraría el tipo de sangre, y ese es exactamente
   * el dato que no se puede perder en silencio.
   */
  emergency: EmergencyInfo;
}

/**
 * LO MÉDICO DE UNA PERSONA: a quién llamar, de qué sangre es, qué hay que
 * saber antes de darle algo.
 *
 * TODO ES OPCIONAL, A PROPÓSITO. Nada de esto se puede exigir para
 * registrarse ni para jugar: quien no quiere darlo no lo da. Un campo
 * obligatorio acá no produce el dato — produce «O+» puesto al azar para
 * poder pasar de pantalla, que es PEOR que vacío, porque a un campo vacío
 * nadie le cree y a uno lleno sí.
 */
export interface EmergencyInfo {
  /**
   * EL TIPO DE SANGRE, tal como la persona lo escribe. `null` es «no lo
   * puso».
   *
   * TEXTO LIBRE Y NO UNA LISTA CERRADA, decidido a sabiendas: quien no está
   * seguro escribe «no sé» o «creo que O+», y eso es MÁS útil que un campo
   * vacío para quien está atendiendo a alguien en el piso — le dice que
   * pregunte en vez de asumir. Una lista de ocho opciones obliga a elegir
   * una mal o a no poner nada.
   *
   * `BLOOD_TYPES` de abajo son SUGERENCIAS que la pantalla ofrece, no lo
   * único que se acepta.
   */
  bloodType: string | null;

  /**
   * ALERGIAS, CONDICIONES, MEDICAMENTOS. Varias líneas. `null` si no puso
   * nada.
   *
   * UN SOLO CAMPO Y NO TRES SEPARADOS: quien escribe esto lo hace una vez y
   * de corrido, y tres cajas hacen que se llene una y se dejen dos. Lo que
   * importa es que ALGO quede escrito.
   */
  medicalNotes: string | null;

  /** En el orden en que hay que llamarlos. Puede venir vacío. */
  contacts: EmergencyContact[];
}

/** Un contacto de emergencia ya guardado. */
export interface EmergencyContact extends EmergencyContactInput {
  id: string;
}

/**
 * Un contacto tal como se manda a guardar.
 *
 * SIN CUENTA Y SIN BUSCAR A NADIE EN LA APLICACIÓN: el contacto es la mamá,
 * el vecino, el doctor — gente que no juega tenis y nunca va a entrar acá.
 * Es un nombre y un teléfono escritos a mano, y tiene que seguir siéndolo.
 */
export interface EmergencyContactInput {
  /** Cómo se llama, para poder decir a quién se está llamando. */
  name: string;
  /** «mamá», «esposo», «vecina». `null` porque el teléfono sirve igual sin esto. */
  relationship: string | null;
  /** LO ÚNICO IMPRESCINDIBLE: es para llamar. */
  phone: string;
}

/**
 * `PUT /profile/emergency` — se manda TODO junto y reemplaza lo que había.
 *
 * LA LISTA COMPLETA Y NO UN `POST` POR CONTACTO: la pantalla es un
 * formulario con tres filas que se editan, se reordenan y se borran a la
 * vez, y quien lo llena espera que «Guardar» guarde lo que ve. Con una ruta
 * por contacto, agregar el tercero y borrar el primero serían dos viajes
 * que pueden quedar a medias, y quedarse a medias acá es quedarse con el
 * teléfono de alguien que ya no es el contacto.
 *
 * EL ORDEN ES EL DEL ARREGLO. No se manda un número de posición: quien
 * atiende a alguien en el piso llama al primero de la lista, y el primero
 * de la lista es el primero que se ve en la pantalla.
 */
export interface SetEmergencyRequest {
  bloodType: string | null;
  medicalNotes: string | null;
  contacts: EmergencyContactInput[];
}

/**
 * LOS TIPOS DE SANGRE QUE LA PANTALLA SUGIERE.
 *
 * SON SUGERENCIAS, NO VALIDACIÓN: el campo acepta cualquier texto (ver
 * `EmergencyInfo.bloodType`). Están acá para que quien SÍ sabe el suyo lo
 * elija de un toque en vez de escribirlo, que es la mayoría de la gente.
 */
export const BLOOD_TYPES = ['O+', 'O-', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-'] as const;

/** Los topes, iguales que en la base. Ver la migración `emergencia_y_datos_medicos`. */
export const EMERGENCY_BLOOD_TYPE_MAX = 20;
export const EMERGENCY_MEDICAL_NOTES_MAX = 1000;
export const EMERGENCY_CONTACT_NAME_MAX = 120;
export const EMERGENCY_CONTACT_RELATIONSHIP_MAX = 60;
export const EMERGENCY_CONTACT_PHONE_MAX = 20;

/**
 * CUÁNTOS CONTACTOS CABEN.
 *
 * Javier los pidió «que se puedan ir agregando», y este tope no lo
 * contradice: es el punto donde la lista deja de ser útil para lo que
 * existe. Quien está atendiendo a alguien en el piso llama a uno, tal vez a
 * dos; una lista de treinta números no se recorre en una emergencia — se
 * abandona. Cinco caben en la pantalla del teléfono sin desplazarse.
 */
export const EMERGENCY_CONTACTS_MAX = 5;

// ---------------------------------------------------------------------
// Retos
// ---------------------------------------------------------------------

/**
 * De qué se trata el reto, que decide CONTRA QUIÉN se puede jugar.
 *
 * `FRIENDLY` es un partido y ya: cualquiera que quiera jugar, cerca.
 * `RANKING` suma puntos, así que solo tiene sentido entre dos personas DEL
 * MISMO CLUB Y DEL MISMO RANKING: los puntos de un club no significan nada en
 * otro, y un ranking es nivel × sexo. Es una regla del producto y no un
 * filtro que la pantalla pueda apagar.
 */
export const CHALLENGE_KINDS = ['FRIENDLY', 'RANKING'] as const;
export type ChallengeKind = (typeof CHALLENGE_KINDS)[number];

/** Los kilómetros a la redonda con los que arranca la búsqueda. */
export const CHALLENGE_DEFAULT_KM = 10;
/** El tope: más que esto es «todo el país» y deja de ser un filtro. */
export const CHALLENGE_MAX_KM = 300;
export const CHALLENGE_PAGE_SIZE = 50;

/** Cuántas letras hacen falta para buscar por nombre. Ver `parsePlayerQuery`. */
export const CHALLENGE_TEXT_MIN = 2;

/**
 * `GET /challenges/players`
 *
 * SON DOS BÚSQUEDAS EN UNA, y cuál se hace lo decide `text`:
 *
 *  - VACÍO: «quién anda cerca», dentro de `km` a la redonda. Hace falta tener
 *    la ubicación puesta... salvo que se quite el límite: ver `km`.
 *  - CON TEXTO: «dónde está fulano», en todo el país. Los kilómetros dejan de
 *    aplicar a propósito — quien escribe un nombre está buscando A ESA
 *    PERSONA, y que no aparezca por estar a 40 km es un resultado vacío que
 *    no hay forma de entender desde la pantalla. La distancia se sigue
 *    mostrando en cada tarjeta cuando se puede calcular.
 */
export interface ChallengeSearchRequest {
  kind: ChallengeKind;
  /**
   * El radio en kilómetros, o `null` para no limitar por distancia.
   *
   * `null` NO ES «cero kilómetros» NI «todos los del país que quepan en la
   * página»: es que el filtro de distancia está apagado. Y apagarlo apaga
   * también el único requisito que tenía esta pantalla — sin radio no hace
   * falta la ubicación de quien busca, porque no hay nada que medir. Esa es
   * la salida para quien todavía no la puso y para quien vive donde no hay
   * nadie a diez kilómetros.
   *
   * Se ignora cuando hay `text`, igual que antes.
   */
  km: number | null;
  /**
   * Apodo o nombre. Vacío —o de menos de `CHALLENGE_TEXT_MIN` letras— es no
   * buscar por nombre.
   *
   * SOLO NOMBRE: no busca por teléfono ni por correo, aunque la columna lo
   * permitiría. Eso convertiría esta pantalla en una guía telefónica al revés
   * —de un número a una persona— y no es para lo que la gente prendió «quiero
   * recibir retos».
   */
  text: string;
  /**
   * Solo gente de ESE club, sea mío o no. `null` = de cualquiera.
   *
   * Puede ser un club ajeno: ver a quién tiene el Club de Escazú es
   * exactamente cómo se encuentra un rival nuevo. Lo que NO puede ser ajeno
   * es el club de un reto por ranking ni el de un filtro por categoría: ver
   * `sameCategory` y `CHALLENGE_NEEDS_CLUB`.
   */
  communityId: string | null;
  /**
   * Solo mi misma categoría. Obligatorio si el reto es por ranking.
   *
   * OBLIGA A QUE EL CLUB SEA MÍO, y no es un descuido: nadie «es» de una
   * categoría a secas — se es B masculino DENTRO de un club. Sin `communityId`
   * el servidor usa mi club por defecto; con uno que no sea mío contesta
   * `CHALLENGE_NEEDS_CLUB` en vez de una lista que no significa nada.
   */
  sameCategory: boolean;
}

/**
 * Alguien a quien se le puede pedir un reto.
 *
 * SOLO APARECE QUIEN MARCÓ «quiero recibir retos» en su perfil. Ese
 * interruptor ya existía y es exactamente este permiso: sin él, esta pantalla
 * sería un directorio con el nombre, el pueblo y el teléfono de todas las
 * personas registradas, que es lo contrario de lo que el producto decidió
 * cuando escondió el nombre legal en el resto de las pantallas.
 */
export interface ChallengePlayer {
  userId: string;
  displayName: string;
  /** El nombre legal. Viaja acá porque esta persona pidió que la contacten. */
  fullName: string;
  avatarPath: string | null;
  sex: Sex | null;
  /** Provincia y cantón. La coordenada exacta NO viaja nunca. */
  locationLabel: string;
  /**
   * A cuántos kilómetros está, redondeado a un decimal.
   *
   * `null` cuando no se puede saber: quien busca no tiene ubicación puesta, o
   * la otra persona tampoco. La pantalla lo dice en vez de inventar un cero.
   */
  distanceKm: number | null;
  /**
   * Su categoría en el club del que se está hablando, o `null` si no hay
   * ninguno: el club filtrado si se filtró por uno, y si no, el que
   * compartimos.
   */
  level: CategoryLevel | null;
  /**
   * CUÁNTAS VECES YA JUGAMOS CONTRA ESTA PERSONA.
   *
   * Viaja con la lista y no se pide aparte: una fila que tiene que salir a
   * buscar su propio dato son cincuenta consultas más por búsqueda, y la
   * lista de Retos se rehace con cada tecla.
   *
   * `0` es «nunca», y la pantalla no dibuja nada: un «vs 0» al lado de un
   * desconocido no significa nada.
   */
  playedTogether: number;
  /** Ese mismo club, para poder decir «los dos en el Club X». */
  communityName: string | null;
  /**
   * El nivel que declaró en su perfil, o `null`.
   *
   * Viaja aparte de `level` a propósito: uno es «el club dice que es B» y el
   * otro «ella dice que juega en B». Juntarlos en un campo daría una
   * categoría que a veces está respaldada por un club y a veces no, sin forma
   * de saber cuál se está viendo.
   */
  declaredLevel: CategoryLevel | null;
  /** En E.164. Viaja porque esta persona pidió que la contacten. */
  whatsappPhone: string;
}

/**
 * EL PERFIL DE OTRA PERSONA. `GET /players/:id`
 *
 * Lo que hace falta para decidir si retarla: quién es, en qué juega, dónde y
 * a cuánto. No es el perfil propio —ese es `PlayerProfile` y lleva el
 * documento, la dirección, el correo y las coordenadas— y nada de eso viaja
 * acá.
 *
 * LA COORDENADA NO SALE NUNCA. Lo que viaja es «San José, Escazú» y un
 * número de kilómetros ya calculado.
 */
// ---------------------------------------------------------------------
// Los reportes de jugador dentro del club
// ---------------------------------------------------------------------

/**
 * UN REPORTE SOBRE ALGUIEN DEL CLUB.
 *
 * QUÉ PROBLEMA RESUELVE. Cuando alguien no se presenta, se va a mitad del
 * partido o trata mal a otro, eso hoy vive en el grupo de WhatsApp: se discute
 * un rato y se pierde. Cuando tres meses después hay que decidir si esa persona
 * sigue en el club, no queda nada —ni la fecha, ni qué pasó, ni la foto del
 * marcador—. Javier: «Reportes de jugador dentro del club, solo para
 * administradores: fecha, problema y fotos».
 *
 * LO ESCRIBE CUALQUIER MIEMBRO Y LO LEE SOLO QUIEN ADMINISTRA. Si lo pudiera
 * escribir solo quien administra, sería una libreta y no un reporte: quien ve
 * el problema casi nunca es quien manda. Y si lo pudiera leer cualquiera, sería
 * un tablón de acusaciones públicas —y nadie reportaría nada de nadie con quien
 * va a tener que jugar el sábado—.
 *
 * LA PERSONA REPORTADA NO SE ENTERA por esta pantalla. No es para esconderlo:
 * es que un reporte es el principio de una conversación que tiene que tener el
 * club, no una notificación automática que empeora la pelea. Quien administra
 * decide cómo y cuándo hablarlo.
 */
export interface PlayerReport {
  id: string;
  communityId: string;
  /** Quién lo escribió. Quien administra tiene que poder preguntarle. */
  reporterId: string;
  reporterName: string;
  /** Sobre quién es. */
  aboutId: string;
  aboutName: string;
  aboutAvatarPath: string | null;
  /**
   * CUÁNDO PASÓ, como día civil «YYYY-MM-DD», y no cuándo se escribió.
   *
   * Son dos fechas distintas y las dos importan: se reporta el martes algo que
   * pasó el sábado, y el sábado es el dato que sirve para cruzarlo con un
   * partido. Guardar solo el día en que se escribió obligaría a leer «pasó hace
   * unos días» en el texto.
   */
  happenedOn: string;
  /** Qué pasó, con las palabras de quien lo vio. */
  problem: string;
  /** Las fotos, ya guardadas. Puede venir vacío: no todo se puede fotografiar. */
  photoPaths: string[];
  createdAt: string;
  /**
   * CUÁNDO LO MIRÓ QUIEN ADMINISTRA. `null` mientras espera.
   *
   * Existe por lo mismo que en los memes: sin un «lo miré», la única forma de
   * vaciar la lista es borrar reportes, y eso es un incentivo pésimo.
   */
  handledAt: string | null;
  handledByName: string | null;
  /**
   * CUÁNTOS REPORTES LLEVA LA PERSONA REPORTADA EN ESTE CLUB, éste incluido.
   *
   * Es el dato por el que se abre esta lista cuando hay que decidir algo: un
   * reporte suelto es un mal día, el cuarto sobre la misma persona es un
   * patrón. Sin el número hay que contar a ojo en una lista paginada, que es
   * como no tenerlo.
   *
   * Cuenta TODOS —atendidos y sin atender—, porque un problema resuelto
   * también pasó. No confundir con el freno anti-spam, que cuenta los que
   * ESCRIBIÓ alguien: ver `PLAYER_REPORT_LIMITS.openMax`.
   */
  timesReported: number;
}

/** El campo del formulario con las fotos del reporte. */
export const PLAYER_REPORT_PHOTO_FIELD = 'photos';

export const PLAYER_REPORT_LIMITS = {
  /**
   * CUÁNTAS FOTOS. Cuatro es una conversación de WhatsApp en dos capturas más
   * el marcador más la cancha: más que eso no aclara nada y convierte la
   * pantalla de quien administra en una galería.
   */
  photosMax: 4,
  /** Lo que se escribe. Largo a propósito: acá el detalle es el valor. */
  problemMax: 2000,
  problemMin: 10,
  /**
   * HASTA CUÁNTOS DÍAS ATRÁS se puede fechar. Un año: más viejo que eso ya no
   * se resuelve, y una fecha de 2019 en la lista es casi siempre un dedazo.
   */
  daysBack: 365,
  /**
   * CUÁNTOS SIN ATENDER puede tener una misma persona en un mismo club.
   *
   * No es desconfianza: es que veinte reportes sin mirar de la misma persona
   * son una conversación que hay que tener cara a cara, no más formularios. Y
   * de paso, es lo que impide que alguien enojado llene la lista en una noche.
   */
  openPerReporter: 10,
} as const;

/**
 * Lo que se manda al reportar. Las fotos viajan aparte, en el mismo formulario.
 */
export interface CreatePlayerReportRequest {
  aboutUserId: string;
  /** El día civil en que pasó, «YYYY-MM-DD». */
  happenedOn: string;
  problem: string;
}

/** Cuántos reportes por página. Ver `PlayerReportPage`. */
export const PLAYER_REPORT_PAGE = 10;

/**
 * CON QUÉ SE FILTRA LA LISTA. Todo opcional: sin nada, la primera página.
 *
 * Las fechas son días civiles «YYYY-MM-DD» y filtran por `happenedOn` —cuándo
 * pasó— y no por `createdAt`: quien busca «lo del torneo de marzo» piensa en
 * el sábado del torneo, no en el martes en que alguien se sentó a escribirlo.
 */
export interface PlayerReportQuery {
  /** Sobre quién. `null` = cualquiera. */
  aboutUserId?: string | null;
  /** Quién lo escribió. `null` = cualquiera. */
  reporterId?: string | null;
  /** Desde este día inclusive. */
  from?: string | null;
  /** Hasta este día inclusive. */
  to?: string | null;
  /** Desde 1. Fuera de rango se recorta al extremo más cercano. */
  page?: number;
}

export interface PlayerReportPage {
  reports: PlayerReport[];
  /** La página que se está viendo, desde 1. */
  page: number;
  /** Cuántas hay en total con ESTE filtro. Cero cuando no hay nada. */
  pages: number;
  /** Cuántos reportes hay en total con este filtro, para decir «12 de 34». */
  total: number;
}

/**
 * QUÉ SE OCULTA EN EL RESUMEN IMPRIMIBLE.
 *
 * Los tres son independientes y quien administra los elige antes de generarlo,
 * porque el mismo resumen sirve para cosas distintas: mostrárselo a la persona
 * reportada sin exponer a quien la reportó, llevar un patrón del club a una
 * junta sin señalar a nadie, o guardar el respaldo completo.
 */
export interface PlayerReportSummaryOptions {
  /** Esconder quién escribió cada reporte. */
  hideReporter: boolean;
  /** Esconder sobre quién es. Deja una lista de problemas, sin personas. */
  hideAbout: boolean;
  /** Esconder las fechas —cuándo pasó y cuándo se escribió—. */
  hideDates: boolean;
}

// ---------------------------------------------------------------------
// La administracion de personas
// ---------------------------------------------------------------------

/**
 * UNA PERSONA, COMO LA VE LA ADMINISTRACION DE LA PLATAFORMA.
 *
 * QUE PROBLEMA RESUELVE. Hoy no hay ninguna pantalla de personas: la unica
 * forma de llegar a alguien desde el panel es rebotando en un reporte de meme.
 * Cuando alguien escribe «me equivoque al escribir mi cedula» o «no me llega
 * el correo», no hay nada que hacer salvo un UPDATE a mano en la base.
 *
 * Javier: «como administrador de tennis up club puedo editar los perfiles de
 * todos los usuarios menos sus contrasenas pero puedo disparar un recuperar
 * contrasena».
 *
 * TRAE DATOS PERSONALES —documento, telefono, direccion, fecha de
 * nacimiento— y por eso NO es el mismo tipo que `PlayerSearchResult`, que es
 * lo que ve quien administra un club. Son dos publicos distintos con dos
 * derechos distintos, y un solo tipo para los dos termina filtrando el de
 * adentro hacia afuera.
 */
export interface AdminUser {
  userId: string;
  /** Con lo que entra. Cambiarlo mueve la cuenta: ver `AdminSaveUserRequest`. */
  email: string;
  status: UserStatus;
  systemRole: SystemRole;

  displayName: string;
  fullName: string;
  idDocumentType: IdDocumentType;
  idDocumentNumber: string;
  /** `YYYY-MM-DD`. */
  dateOfBirth: string;
  nationalityCode: string;
  sex: Sex | null;

  provinceCode: string;
  cantonCode: string;
  districtCode: string;
  /** «San Jose, Escazu», ya armado. */
  locationLabel: string;
  addressLine: string | null;
  whatsappPhone: string;

  avatarPath: string | null;

  /**
   * QUIEN LE TOCO EL PERFIL DESDE LA ADMINISTRACION, y cuando. `null` si
   * nadie lo hizo nunca.
   *
   * Es la misma costumbre que `mutedById` o `reviewedById`: en este repo
   * «quien hizo esto» se guarda en la fila afectada. Para datos de identidad
   * importa mas que en ningun otro lado — es lo que permite contestar «yo no
   * cambie eso» sin que sea la palabra de uno contra la del otro.
   */
  editedByName: string | null;
  editedAt: string | null;
}

/** Cuantas personas devuelve una busqueda del panel. */
export const ADMIN_USER_SEARCH_LIMIT = 25;

/**
 * LO QUE SE PUEDE CAMBIAR DESDE EL PANEL.
 *
 * TODO EL PERFIL Y ADEMAS EL CORREO, que es lo que pidio Javier. NO la
 * contrasena: no existe ningun camino para escribirla desde afuera, ni
 * siquiera para la administracion. Lo que si hay es disparar el correo de
 * reseteo — ver `POST /admin/users/:id/password-reset`.
 *
 * POR QUE EL CORREO ES DISTINTO DE TODO LO DEMAS. Cambiar la cedula de
 * alguien es corregir un dato suyo; cambiar su correo es MOVER SU CUENTA a
 * otra direccion, y quien tenga esa direccion puede pedir un reseteo y
 * entrar. Por eso —y solo por eso— el cambio de correo le avisa a la
 * direccion vieja. El resto de las correcciones son silenciosas, como las
 * pidio Javier.
 */
export interface AdminSaveUserRequest {
  email: string;
  displayName: string;
  fullName: string;
  idDocumentType: IdDocumentType;
  idDocumentNumber: string;
  dateOfBirth: string;
  nationalityCode: string;
  sex: Sex;
  provinceCode: string;
  cantonCode: string;
  districtCode: string;
  addressLine?: string;
  whatsappPhone: string;
}

/** `POST /admin/users/:id/password-reset` */
export interface AdminPasswordResetResponse {
  /**
   * ADONDE SE MANDO. El token NO viaja: ni en esta respuesta ni en ninguna.
   *
   * Quien administra dispara el envio y nunca ve el enlace. Es lo que separa
   * «le ayude a recuperar su cuenta» de «puedo entrar a la cuenta de
   * cualquiera», y la unica forma de que esa diferencia sea real es que el
   * secreto no pase por esta pantalla.
   */
  sentTo: string;
}

// ---------------------------------------------------------------------
// Las canchas
// ---------------------------------------------------------------------

/**
 * UNA CANCHA DONDE SE PUEDE IR A JUGAR.
 *
 * QUÉ PROBLEMA RESUELVE. «¿Dónde jugamos?» se contesta hoy en un chat, y la
 * respuesta —cuánto cobran, si tienen luz, a qué hora abren, el teléfono para
 * reservar— vive en la cabeza de tres personas. Quien llega nuevo al deporte
 * no tiene a quién preguntarle, y quien viaja a otra provincia tampoco.
 *
 * Javier: «Información de canchas, que los usuarios lo puedan agregar, tarifas
 * con luz, tarifas sin luz, página web, teléfono, y que se pueda seleccionar
 * provincia, cantón, distrito, rango de horas».
 *
 * LA CARGA CUALQUIERA Y LA CORRIGE CUALQUIERA, como una wiki. Es una decisión
 * y tiene su contrapartida —alguien puede escribir una tarifa vieja— pero la
 * alternativa es una lista vacía: si cada cancha tiene que esperar a que
 * alguien la apruebe, nadie carga la primera. Por eso queda escrito QUIÉN la
 * creó y QUIÉN la tocó de último: no para culpar a nadie, sino para que quien
 * lee una tarifa que le suena rara sepa a quién preguntarle.
 */
export interface Court {
  id: string;
  name: string;
  /** Las señas de a pie: «200 metros norte de la iglesia». Puede faltar. */
  addressNote: string | null;

  /**
   * LO QUE HAY QUE SABER ANTES DE IR: superficie, parqueo, si hay que
   * reservar, cómo se entra. Varias líneas, texto libre.
   *
   * APARTE DE `addressNote`, que contesta «dónde queda» en una línea. Son dos
   * preguntas distintas y juntarlas obligaría a elegir cuál contestar.
   */
  description: string | null;

  provinceCode: string;
  cantonCode: string;
  districtCode: string;
  /** «San José, Escazú» ya armado, que es lo que se muestra. */
  locationLabel: string;

  /**
   * LAS DOS TARIFAS, EN COLONES POR HORA, y cualquiera de las dos puede faltar.
   *
   * Dos columnas y no una con un recargo: en la práctica cada cancha pone el
   * precio que quiere para cada caso, y un recargo obligaría a inventar una
   * regla que no existe. `null` significa «no sabemos», NO «gratis» — por eso
   * no es 0, que sí querría decir gratis y es un dato distinto.
   */
  priceWithLightColones: number | null;
  priceWithoutLightColones: number | null;

  /** Para reservar. E.164, igual que el del perfil. Puede faltar. */
  phone: string | null;
  /** La página o el Instagram, si tienen. Puede faltar. */
  websiteUrl: string | null;

  /**
   * DESDE Y HASTA QUÉ HORA, como «HH:MM» de 24 horas.
   *
   * UN SOLO RANGO Y NO UN HORARIO POR DÍA, a propósito: lo que la gente
   * necesita saber es «¿está abierto a las 8 de la noche?», y para eso un
   * rango alcanza. Un horario por día son siete pares de campos que nadie
   * llena completos, y medio llenos mienten más que un rango.
   *
   * CIERRE DESPUÉS DE MEDIANOCHE: si `closesAt` es menor o igual que
   * `opensAt`, el cierre es del día siguiente. «18:00 a 02:00» es una cancha
   * que cierra a las dos de la mañana, no un error.
   */
  opensAt: string | null;
  closesAt: string | null;

  /** Quién la cargó, y quién la tocó de último. Ver el encabezado. */
  createdByName: string;
  updatedByName: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * UN AVISO DEL TABLÓN: «busco partido».
 *
 * Javier: «publicar retos donde pueda seleccionar la fecha, la gente puede
 * seleccionar el lugar, y las categorías que quieran retar, uno toca el
 * whatsapp con los datos de publicación y comienza a conversar, el que crea el
 * reto lo puede borrar».
 *
 * ES UN AVISO, NO UN RETO. No crea un partido, no suma puntos y no pasa por
 * ninguna regla del reto por ranking. El tablón sirve para encontrarse; lo que
 * pase después se arregla por WhatsApp, o retándose por ranking dentro del
 * club, que es el camino que ya existe.
 *
 * ES DE TODA LA APLICACIÓN, no de un club: lo ve cualquiera con cuenta. Es lo
 * que lo hace útil —quien busca partido un sábado no quiere que su aviso solo
 * lo vean los doce de su club— y también lo que obliga a lo de abajo.
 *
 * ACÁ NO ESTÁ EL TELÉFONO, Y ESO ES LA MITAD DEL DISEÑO. Un tablón que ve
 * cualquiera con cuenta, con el WhatsApp de cada quien adentro, es una lista de
 * teléfonos servida en bandeja: basta abrir la pantalla para llevarse todos,
 * sin tocar un solo botón. Por eso el número NO viaja en esta respuesta: se
 * pide aparte, de a uno, cuando alguien toca «escribirle». Ver
 * `GET /challenge-posts/:id/whatsapp`.
 */
export interface ChallengePost {
  id: string;
  /** Quién lo publicó. El id sirve para abrir su perfil. */
  createdById: string;
  createdByName: string;
  createdByAvatarPath: string | null;
  /** Cuándo quiere jugar. Fecha civil, «YYYY-MM-DD». */
  playsOn: string;
  /**
   * DÓNDE, EN TEXTO LIBRE y no una cancha de la lista.
   *
   * Lo eligió Javier. Y es lo correcto para esto: se juega en canchas que no
   * están cargadas, en el club de un amigo, «en Grecia o por ahí cerca». Atar
   * el aviso al directorio de canchas haría que publicar empiece por cargar
   * una cancha, y entonces nadie publica.
   */
  placeText: string;
  /** Contra qué categorías quiere jugar. Al menos una, sin repetir. */
  levels: CategoryLevel[];
  /** Lo que quiera agregar. Puede faltar. */
  note: string | null;
  /** Su fecha ya pasó. No se esconde: se marca. Ver `ordenarAvisos`. */
  past: boolean;
  /**
   * CUÁNDO DIJO QUE YA CONSIGUIÓ PARTIDO. `null` = sigue buscando.
   *
   * Javier: «quiero mantenerlo visible por 7 días, la idea es que la gente
   * pueda contactarlo para un futuro reto, sabemos que es una persona activa
   * para jugar».
   *
   * O SEA QUE CONCRETAR NO ES BORRAR: el aviso deja de ofrecer ESE partido y
   * pasa a decir otra cosa, que también sirve —«esta persona juega»—. El
   * botón de escribirle sigue ahí a propósito: es para lo que se lo deja.
   *
   * A LOS SIETE DÍAS DESAPARECE SOLO, contados DESDE ESTA FECHA y no desde el
   * día del partido: lo que caduca es la señal de «anda jugando», y esa
   * empieza a correr cuando lo dijo. Es lo único del tablón que se va sin que
   * nadie lo saque; un aviso abierto no se vence nunca.
   */
  settledAt: string | null;
  /** Si lo publicó quien mira: puede editarlo y borrarlo. */
  mine: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Lo que se manda al publicar o editar un aviso. El mismo cuerpo en los dos. */
export interface SaveChallengePostRequest {
  /** «YYYY-MM-DD». */
  playsOn: string;
  placeText: string;
  levels: CategoryLevel[];
  note?: string;
}

export interface ChallengePostPage {
  items: ChallengePost[];
  total: number;
  page: number;
  pageSize: number;
}

/** El teléfono de un aviso, servido de a uno y solo al tocar el botón. */
export interface ChallengePostContact {
  /** E.164, como el del perfil. `null` si esa persona no tiene WhatsApp. */
  phone: string | null;
}

export const CHALLENGE_POST_LIMITS = {
  placeMin: 3,
  placeMax: 120,
  noteMax: 300,
  pageSize: 20,
  /**
   * CUÁNTOS DÍAS SIGUE VIENDOSE UN AVISO YA CONCRETADO.
   *
   * Siete, y se cuentan desde que lo marcó. Lo que se deja a la vista no es el
   * partido —ese ya está arreglado— sino la señal de que esa persona juega,
   * para quien busque para la semana que viene. Pasada la semana la señal ya
   * no dice nada y el aviso se va solo.
   */
  settledVisibleDays: 7,
  /**
   * CUÁNTOS PUEDE TENER PUBLICADOS A LA VEZ UNA PERSONA.
   *
   * Existe porque los avisos NO se vencen solos —Javier eligió que los borre
   * quien los publicó— y el tablón es de toda la aplicación. Las dos cosas
   * juntas significan que nada saca un aviso de la lista salvo su dueño: sin
   * un techo, una sola persona publicando todos los días tapa el tablón para
   * todos los demás y no hay quien lo limpie.
   *
   * CINCO Y NO UNO: es normal querer jugar el sábado y el martes, y en dos
   * lugares distintos. El techo está para el caso raro, no para el normal.
   *
   * CUENTA SOLO LOS ABIERTOS. Los concretados no ocupan lugar, y eso NO es un
   * detalle: si contaran, quien consigue partido cinco veces en una semana
   * —justo la persona activa que el tablón quiere mostrar— se quedaría sin
   * poder publicar, castigada por usarlo bien.
   */
  perPerson: 5,
  /**
   * Hasta cuántos días adelante se puede publicar. Un aviso a dos años vista
   * no es un partido: es basura que nadie va a borrar nunca.
   */
  daysAhead: 120,
} as const;

export const COURT_LIMITS = {
  nameMin: 3,
  nameMax: 80,
  addressNoteMax: 200,
  /**
   * 600 CARACTERES para la descripción, que son unas ocho líneas.
   *
   * Es texto libre de varias líneas, pero con techo: sin límite alguien pega
   * un reglamento entero y la ficha de la cancha deja de poder leerse de un
   * vistazo. Ocho líneas alcanzan para superficie, parqueo, reservas y cómo
   * se entra, que es lo que la gente pregunta.
   */
  descriptionMax: 600,
  websiteUrlMax: 300,
  /**
   * EL TECHO DE LA TARIFA POR HORA, en colones.
   *
   * No es una opinión sobre lo que vale una cancha: es el freno al dedazo.
   * Quien escribe 150000 queriendo 15000 tiene que enterarse al guardar y no
   * cuando alguien llegue a la cancha con la plata contada.
   */
  priceMaxColones: 200_000,
  /** Cuántas caben en una página. */
  pageSize: 30,
} as const;

/** Con qué se filtra la lista. Todo opcional: sin nada, todas. */
export interface CourtQuery {
  provinceCode?: string;
  cantonCode?: string;
  /** Busca en el nombre y en las señas. */
  text?: string;
  page?: number;
}

export interface CourtPage {
  items: Court[];
  total: number;
  page: number;
  pageSize: number;
}

/** `POST /courts` y `PUT /courts/:id` mandan lo mismo. */
export interface SaveCourtRequest {
  name: string;
  addressNote?: string;
  description?: string;
  provinceCode: string;
  cantonCode: string;
  districtCode: string;
  priceWithLightColones?: number | null;
  priceWithoutLightColones?: number | null;
  phone?: string;
  websiteUrl?: string;
  opensAt?: string;
  closesAt?: string;
}

// ---------------------------------------------------------------------
// Los logros del perfil
// ---------------------------------------------------------------------

/**
 * QUÉ HIZO ESTA PERSONA, en iconos.
 *
 * QUÉ PROBLEMA RESUELVE. El perfil decía cuántos puntos lleva y nada más, y
 * un número suelto no cuenta una historia: 240 puntos pueden ser un torneo
 * ganado o veinte retos parejos, y son dos jugadores distintos. Javier:
 * «quiero llevar de esta manera un historial del jugador, por lo tanto si
 * gana partidos ponerle un icono y al igual que el ranking si va de primero
 * un icono».
 *
 * SOLO HASTA SEMIFINAL. Perder en octavos no es un logro, es haber jugado;
 * un perfil con un icono por cada torneo en el que alguien se inscribió no
 * dice nada de nadie porque todos tienen los mismos. Campeón, finalista y
 * semifinalista son los tres que la gente cuenta.
 *
 * VENCEN AL AÑO, con la misma ventana que el ranking (`RANKING_WINDOW_DAYS`).
 * Es la regla que ya rige todo lo demás de la app: los puntos dicen quién
 * juega bien AHORA, y un perfil con la copa de hace cinco años diría otra
 * cosa que el ranking de al lado. Javier: «se vencen por año».
 */
export const ACHIEVEMENT_KINDS = [
  /** Ganó el torneo. Copa. */
  'TOURNAMENT_WON',
  /** Perdió la final. Copa. */
  'TOURNAMENT_FINAL',
  /** Llegó a semifinal. Copa. */
  'TOURNAMENT_SEMIFINAL',
  /** Retos por ranking ganados. Dos raquetas. */
  'CHALLENGE_WINS',
  /** Va de primero en el ranking de su club y su categoría. Corona. */
  'RANKING_FIRST',
  /**
   * EN QUÉ PUESTO VA HOY, para quien NO va de primero.
   *
   * Javier: «también anota la posición que están en el club con otro icono
   * para el resto de los integrantes».
   *
   * ES EL COMPLEMENTO EXACTO DE `RANKING_FIRST` y nunca salen los dos juntos
   * en la misma categoría: el primero tiene corona y el resto, su puesto.
   * Quien mira un perfil quiere saber dónde está parado, y hasta ahora eso
   * solo lo decía el perfil de una persona por club y categoría.
   *
   * VENCE COMO TODO LO DEMÁS porque es de AHORA: sale del mismo ranking de
   * los últimos doce meses. Lo que no vence es `YEAR_END_RANK`, que dice cómo
   * cerró un año y es un hecho terminado.
   */
  'RANKING_POSITION',
  /**
   * SÚPER TIE BREAKS GANADOS. Raquetas.
   *
   * Javier: «eso va parte del historial de los perfiles de cuantos tie break
   * pierde y gana».
   *
   * SOLO LOS GANADOS LLEVAN ICONO, y los perdidos van en el número de al lado
   * (ver `Achievement.lost`). Un logro es algo que se muestra con orgullo; un
   * icono por perder no es un logro, es un reproche. Pero el número de los
   * perdidos SÍ importa: «7 de 9» cuenta una historia que «7» no cuenta, y es
   * exactamente lo que Javier pidió ver.
   */
  'SUPER_TIE_BREAKS',
  /**
   * EN QUÉ PUESTO CERRÓ EL AÑO, al 31 de diciembre. Corona.
   *
   * Javier: «también en logro el ranking en que quedó en el club el 31 de
   * Diciembre».
   *
   * ES EL ÚNICO LOGRO QUE NO VENCE, y tiene que ser así: los demás dicen
   * «esta persona juega bien AHORA» y por eso miran los últimos doce meses.
   * Éste dice otra cosa —«así terminó el año 2026»— y un hecho cerrado no
   * deja de haber pasado. Es, de hecho, lo que hace que el perfil valga la
   * pena dentro de tres años: sin esto, un campeón de 2026 queda idéntico a
   * alguien que nunca jugó.
   *
   * NO SE GUARDA EN NINGUNA TABLA. Se calcula pidiéndole al ranking de
   * siempre que conteste con fecha del 31 de diciembre: los puntos nunca se
   * guardan —se suman de los partidos cada vez— así que preguntar por una
   * fecha vieja devuelve el ranking de esa fecha, sin inventar nada. Ver
   * `profileInCommunity(…, today)`.
   */
  'YEAR_END_RANK',
] as const;
export type AchievementKind = (typeof ACHIEVEMENT_KINDS)[number];

/**
 * CON QUÉ SE DIBUJA. Javier: «dependiendo de los partidos si son retos sale
 * como dos raquetas si es por torneo son como una copa».
 *
 * Vive en el contrato y no en la pantalla porque es parte de lo que el logro
 * ES: una copa que un día saliera al lado de unos retos ganados sería un
 * perfil que miente. Agregar un tipo obliga a decir con qué se dibuja.
 */
export const ACHIEVEMENT_ICONS: Record<
  AchievementKind,
  'TROPHY' | 'RACKETS' | 'CROWN' | 'SILVER' | 'BRONZE' | 'RANK'
> = {
  // LA COPA ES SOLO DEL CAMPEÓN. Javier: «haz iconos para el segundo y
  // tercero». Antes los tres llevaban copa y el perfil de quien llegó a semis
  // se leía igual que el de quien ganó.
  TOURNAMENT_WON: 'TROPHY',
  TOURNAMENT_FINAL: 'SILVER',
  TOURNAMENT_SEMIFINAL: 'BRONZE',
  CHALLENGE_WINS: 'RACKETS',
  RANKING_FIRST: 'CROWN',
  // Raquetas, como los retos: los dos hablan de partidos jugados.
  SUPER_TIE_BREAKS: 'RACKETS',
  YEAR_END_RANK: 'CROWN',
  // NO ES CORONA: la corona es de quien va primero, y este logro existe
  // justamente para los demás. Darle la misma haría que un 8.º se leyera como
  // un campeón de un vistazo.
  RANKING_POSITION: 'RANK',
};

/**
 * CUÁNTOS RETOS GANADOS HACEN FALTA PARA QUE APAREZCAN LAS RAQUETAS.
 *
 * Uno solo no es un logro: casi todo el que juega gana alguno, y un icono que
 * tiene todo el mundo no distingue a nadie. Tres es donde empieza a decir
 * algo, y el número que sale al lado dice el resto.
 */
export const CHALLENGE_WINS_FOR_BADGE = 3;

/**
 * CUÁNTOS CIERRES DE AÑO SE MUESTRAN.
 *
 * Javier: «eso se puede paginar a futuro» — o sea que sabe que van a ser
 * muchos con los años y que hoy no hace falta paginarlos. Tres es lo que
 * cuenta una historia sin volverse un archivo: el año pasado, el anterior y
 * el otro. El cuarto ya no dice nada que los tres primeros no digan.
 *
 * NO ES UN LÍMITE DE LO QUE SE GUARDA —no se guarda nada, se calcula— sino
 * de cuántas veces se le pregunta al ranking por una fecha vieja. Subirlo es
 * cambiar este número; paginarlo, el día que haga falta, es cambiar la
 * pantalla, no los datos.
 */
export const YEAR_END_RANKS_SHOWN = 3;

/** Hasta qué ronda cuenta como logro. 4 = semifinal. Ver `ACHIEVEMENT_KINDS`. */
export const ACHIEVEMENT_MIN_ROUND = 4;

/**
 * UN LOGRO YA CONTADO, listo para dibujar.
 *
 * VIENE AGRUPADO Y CON `count`. Tres copas iguales una al lado de la otra son
 * ruido; una copa con un «×3» es un dato. Lo agrupa el servidor y no la
 * pantalla porque el que agrupa tiene que decidir POR QUÉ agrupa —acá, por
 * tipo y por categoría— y esa decisión no puede quedar en dos lugares.
 *
 * SE AGRUPA TAMBIÉN POR CATEGORÍA. Un campeón de C2 y uno de OPEN no son el
 * mismo logro, y sumarlos en un solo «×2» borraría justamente lo que se vino
 * a mirar.
 */
export interface Achievement {
  kind: AchievementKind;
  /** La categoría en la que lo consiguió. Siempre viene: todo logro es de una. */
  level: CategoryLevel;
  /** Cuántas veces, dentro de la ventana. Nunca menor que 1. */
  count: number;
  /**
   * EL MÁS RECIENTE, como día civil «YYYY-MM-DD». Es lo que decide cuándo se
   * vence el grupo entero, y lo que se muestra cuando alguien pregunta «¿de
   * cuándo es esto?».
   */
  lastOn: string;
  /**
   * El día en que este logro deja de mostrarse. `lastOn` + la ventana.
   *
   * `null` en `YEAR_END_RANK`, que es el único que no vence: cómo terminó un
   * año no deja de haber pasado. Ver `ACHIEVEMENT_KINDS`.
   */
  expiresOn: string | null;

  /**
   * EN QUÉ CLUB. Javier: «Poncho sale primero del ranking pero no se sabe de
   * que club».
   *
   * `null` cuando el logro no es de un club: los retos amistosos no tienen
   * ninguno, y una copa de un torneo que ya no existe tampoco. La pantalla
   * muestra el nombre cuando viene y no dibuja nada cuando no.
   *
   * VIENE EL NOMBRE Y NO SOLO EL id, porque es lo que se lee. Viene TAMBIÉN
   * el id, para poder enlazar al club sin una segunda consulta.
   */
  communityId: string | null;
  communityName: string | null;

  /**
   * DE QUÉ AÑO ES. Javier: «si gana torneos ponerle el año».
   *
   * Es el año de `lastOn` —el resultado más nuevo del grupo— y viene ya
   * calculado en vez de dejar que la pantalla corte la cadena: `lastOn` es un
   * día civil en UTC, y sacarle el año con el reloj del navegador le cambia
   * el año a un torneo del 31 de diciembre para quien esté en Asia.
   *
   * En `YEAR_END_RANK` es el año que se cerró, que es de lo que habla.
   */
  year: number;

  /**
   * CUÁNTOS PERDIÓ. Solo en `SUPER_TIE_BREAKS`; `null` en los demás.
   *
   * Javier pidió ver «cuantos tie break pierde y gana», y las dos mitades
   * juntas son el dato: ganar 7 de 9 dice algo muy distinto de ganar 7 de 30.
   * `count` son los ganados; esto, los perdidos.
   *
   * VA ACÁ Y NO COMO OTRO LOGRO porque perder no es un logro: un icono por
   * cada tie break perdido sería un reproche en el perfil. El número al lado
   * del de ganados cuenta la historia completa sin hacer eso.
   */
  lost: number | null;

  /**
   * EL PUESTO. En `YEAR_END_RANK` es con el que cerró el año; en
   * `RANKING_POSITION`, en el que va hoy. `null` en los demás.
   *
   * 1 es primero. No se recorta a los tres primeros: quedar noveno entre
   * cuarenta es un año de tenis, y esconderlo sería decidir por la persona
   * que su año no cuenta.
   *
   * EN `RANKING_POSITION` NUNCA ES 1, y no por casualidad: del primero habla
   * `RANKING_FIRST`, con corona. Ver `achievementsOf`.
   */
  position: number | null;

  /**
   * DE CUÁNTOS. Solo en `RANKING_POSITION`; `null` en los demás.
   *
   * «3.º de 12» y «3.º de 40» son dos cosas distintas, y sin esto el puesto
   * solo se lee mal en las dos direcciones: en una tabla de cuatro, ser
   * tercero no es lo que parece, y en una de cuarenta, sí.
   */
  outOf: number | null;
}

export interface PublicPlayer {
  userId: string;
  displayName: string;
  /** El nombre legal. */
  fullName: string;
  avatarPath: string | null;
  sex: Sex | null;
  /** Provincia y cantón, nunca el distrito ni el punto. */
  locationLabel: string;
  /**
   * A cuántos kilómetros está, redondeado a un decimal.
   *
   * `null` cuando no se puede saber: a alguno de los dos le falta la
   * ubicación. La pantalla lo dice en vez de inventar un cero.
   */
  distanceKm: number | null;
  /** El nivel que declaró en su perfil. No es el de ningún club. */
  declaredLevel: CategoryLevel | null;
  /** Los clubes donde está ACTIVA, con su categoría y sus puntos en cada uno. */
  clubs: PublicPlayerClub[];
  /**
   * En E.164, o `null` SI ESTA PERSONA NO QUIERE QUE LA CONTACTEN.
   *
   * El interruptor es `availableForChallenges`, el mismo que gobierna la
   * pestaña de Retos, y el mismo del que se le dijo a cada persona al
   * registrarse: «podés apagarlo cuando querés y nadie te manda retos».
   * Publicar el teléfono de quien lo apagó sería romper esa frase.
   */
  whatsappPhone: string | null;
  /** Para poder decir POR QUÉ no hay teléfono, en vez de no mostrar nada. */
  availableForChallenges: boolean;

  /**
   * A QUIÉN LLAMAR Y QUÉ SABER SI LE PASA ALGO EN LA CANCHA.
   *
   * SALE EN EL PERFIL QUE VE CUALQUIERA, y Javier lo decidió así a sabiendas:
   * «si alguien ve el perfil los contactos de emergencia deben de aparecer».
   * La razón es la misma por la que el dato existe — «es un deporte de riesgo
   * y las personas pueden actuar inmeditamente»: quien está al lado de
   * alguien tirado en la cancha casi nunca es de su club, y un dato que solo
   * ve el dueño no sirve para nada el día que hace falta.
   *
   * QUÉ SIGNIFICA ESO, ESCRITO PARA QUE NADIE LO DESCUBRA DESPUÉS: el
   * teléfono de la familia de una persona y su tipo de sangre quedan
   * visibles para CUALQUIERA con una cuenta que abra su perfil. No es
   * internet abierto —la pantalla pide sesión— pero sí es todo el padrón.
   * Es un intercambio deliberado: utilidad en una emergencia a cambio de
   * privacidad, y la contraparte es que cargar estos datos es VOLUNTARIO —
   * nada obliga a llenarlos, y quien no quiera compartirlos no los pone.
   *
   * QUIEN SE ESCONDIÓ NO LLEGA HASTA ACÁ: su perfil no se abre para quien no
   * comparte club (ver `hidden`), así que esa protección sigue valiendo.
   */
  emergency: EmergencyInfo;
  /**
   * LOS CLUBES DONDE SE LA PUEDE RETAR POR RANKING, ya filtrados.
   *
   * Mismo club, misma categoría y mismo sexo, los dos activos: la regla
   * completa, aplicada por el servidor. La pantalla dibuja el botón si esto
   * trae algo y no lo dibuja si viene vacío — no vuelve a decidir.
   *
   * ES UNA LISTA porque dos personas pueden compartir dos clubes, y un reto
   * por ranking cuenta en UNO: los puntos son de un club. Con dos, la
   * pantalla pregunta en cuál.
   *
   * Vacía cuando quien mira es la misma persona: nadie se reta a sí mismo.
   */
  rankedChallengeIn: PublicPlayerClub[];
  /**
   * QUÉ HIZO, en iconos. Ya agrupados, contados y ordenados por el servidor, y
   * ya sin los vencidos: la pantalla solo dibuja. Vacío es vacío y no es un
   * error —quien acaba de entrar no tiene ninguno—.
   */
  achievements: Achievement[];
}

/**
 * UNA TABLA EN LA QUE ALGUIEN COMPITE.
 *
 * ES UNA POR RANKING, NO UNA POR CLUB, y ese es el cambio: quien compite en
 * dos categorías del mismo club aparece dos veces, con el mismo
 * `communityId` y distinta `(level, sex)`. Antes era una sola y la segunda
 * tabla no llegaba nunca a la pantalla — Javier lo vio al no poder darle
 * puntos a Silvia en su C masculino.
 *
 * QUIEN LEA ESTO PARA CONTAR CLUBES tiene que agrupar por `communityId`.
 */
export interface PublicPlayerClub {
  communityId: string;
  communityName: string;
  /** El nivel de ESTA tabla. */
  level: CategoryLevel;
  /**
   * EL SEXO DE ESTA TABLA, que no es el de la persona: una mujer puede
   * competir en el ranking masculino. Es lo que distingue sus dos filas.
   */
  sex: CategorySex;
  /** Los puntos que lleva en el ranking de ese club, en la ventana vigente. */
  points: number;
  /**
   * ¿QUIEN MIRA ADMINISTRA ESE CLUB?
   *
   * Decide si se le ofrece corregir los puntos ahí mismo. Lo dice el servidor
   * y no lo adivina la pantalla: un botón que la pantalla se inventa es un 403
   * con forma de botón.
   */
  canManage: boolean;
}

export interface ChangePasswordRequest {
  /** La actual. Sin esto, una pantalla abierta en un café cambia la contraseña de otro. */
  currentPassword: string;
  newPassword: string;
}

// ---------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------

export const COMMUNITY_ERROR_CODES = [
  /** No session, or a token that is no longer valid. */
  'UNAUTHENTICATED',
  /** A valid session that is not allowed to do this. */
  'FORBIDDEN',
  'COMMUNITY_NOT_FOUND',
  /** Se eligió como club por defecto uno del que la persona no es parte. */
  'NOT_A_MEMBER',
  /** Un torneo no puede tener más organizadores que `adminsMax`. */
  'TOO_MANY_ADMINS',
  'COMMUNITY_NAME_TAKEN',
  /** Already approved or rejected: a decision is made once. */
  'COMMUNITY_ALREADY_REVIEWED',
  /** The bytes are not an image we accept, whatever the file was called. */
  'IMAGE_INVALID',
  'IMAGE_TOO_LARGE',
  /** A real image we could not resize — corrupt, or an exotic variant. */
  'IMAGE_UNREADABLE',
  /** Ya está en la comunidad. */
  'ALREADY_MEMBER',
  /** Ya pidió entrar y la solicitud sigue esperando. */
  'JOIN_REQUEST_PENDING',
  'JOIN_REQUEST_NOT_FOUND',
  /** Solo quien creó el club puede repartir permisos de administración. */
  'ONLY_OWNER',
  /**
   * Quiere irse del club, pero lo administra.
   *
   * NO ES UN CAPRICHO: quien administra es quien aprueba pagos, tira cuadros
   * y responde cambios de categoría. Si pudiera irse de un toque, un club se
   * podría quedar sin nadie que lo atienda y con un torneo a medio correr. El
   * camino existe igual —primero se deja el permiso, después se va— y son dos
   * pasos a propósito.
   */
  'ADMIN_CANNOT_LEAVE',
  /**
   * El enlace de la invitación a administrar no sirve: no existe, ya se usó o
   * se venció. LOS TRES CASOS DAN EL MISMO CÓDIGO A PROPÓSITO.
   *
   * Distinguirlos le diría a cualquiera que pruebe enlaces al azar cuáles
   * existieron alguna vez, y eso es justo lo que no tiene por qué saber.
   */
  'ADMIN_INVITE_INVALID',
  /** Abrió el enlace con otra cuenta: la invitación es para una persona. */
  'ADMIN_INVITE_NOT_YOURS',
  /** Ya hay una invitación esperando respuesta para esa persona. */
  'ADMIN_INVITE_PENDING',
  /** Ya administra: no hay nada que invitar. */
  'ALREADY_ADMIN',
  /** Esa cancha no existe, o alguien la borró mientras se editaba. */
  'COURT_NOT_FOUND',
  /** Ya hay una cancha con ese nombre en ese cantón. Ver `saveCourt`. */
  'COURT_DUPLICATED',
  /** Ese aviso del tablón no existe, o su dueño lo borró mientras se miraba. */
  'POST_NOT_FOUND',
  /**
   * Ya tiene el máximo de avisos publicados. Ver `CHALLENGE_POST_LIMITS`.
   *
   * Existe porque los avisos no se vencen solos y el tablón es de toda la
   * aplicación: sin techo, una sola persona lo tapa para todos.
   */
  'TOO_MANY_POSTS',
  /** La fecha del aviso no existe, o está demasiado lejos. */
  'INVALID_DATE',
  /** El lugar del aviso no dice nada. */
  'INVALID_PLACE',
  /** Las categorías del aviso: ninguna, o alguna que no existe. */
  'INVALID_LEVELS',
  /** La nota del aviso es demasiado larga. */
  'INVALID_NOTE',
  /** Esa persona no existe. */
  'USER_NOT_FOUND',
  /** Ese correo ya es de otra cuenta. */
  'EMAIL_TAKEN',
  /**
   * La administracion no se edita a si misma ni entre si desde este panel.
   *
   * Editarse a uno mismo por la puerta de administracion se saltea las
   * comprobaciones que hace la pantalla de perfil; editar a otro admin es el
   * camino corto para que un panel comprometido se lleve puesto al resto.
   */
  'CANNOT_EDIT_ADMIN',
  /** Esa inscripción no existe (o ya se cayó por falta de comprobante). */
  'ENTRY_NOT_FOUND',
  /** Se venció el plazo del comprobante mientras se mandaba. */
  'ENTRY_EXPIRED',
  /** Se mandó un comentario vacío como comprobante. */
  'PROOF_EMPTY',
  /** La fecha o la hora que llegó no se entiende. */
  'SCHEDULE_INVALID',
  /** Ese cuadro ya se tiró: no acepta cambios. */
  'CATEGORY_STARTED',
  /** Ese cuadro todavía no se tiró. */
  'CATEGORY_NOT_STARTED',
  /** La coordenada que llegó no es una coordenada. */
  'COORDINATES_INVALID',
  'MEMBER_NOT_FOUND',
  /** Esa categoría no la corre la comunidad, o no para el sexo de esa persona. */
  'CATEGORY_NOT_OFFERED',
  /** Ya compite en esa tabla: pedirla otra vez no pide nada. */
  'ALREADY_IN_RANKING',
  /** Llegó al máximo de tablas por club. Ver `MÁXIMO_DE_TABLAS`. */
  'TOO_MANY_RANKINGS',
  /**
   * Pidió una tabla que no es de ranking: dobles o mixto. Los rankings son de
   * singles, porque un ranking individual no sabe a quién de la pareja darle
   * los puntos. Ver `esTablaDeRanking`.
   */
  'NOT_A_RANKING',
  /** La comunidad no tiene ninguna categoría configurada todavía. */
  'NO_CATEGORIES',
  /** La contraseña actual no es la que se escribió. */
  'CURRENT_PASSWORD_WRONG',
  /** Le falta un dato del perfil que hace falta para esto — hoy, el sexo. */
  'PROFILE_INCOMPLETE',
  'TOURNAMENT_NOT_FOUND',
  /** Ya se tiró: el cuadro no se toca más. */
  'TOURNAMENT_ALREADY_RUNNING',
  /** Se quiso dar por terminado un torneo que todavía no arrancó ningún cuadro. */
  'TOURNAMENT_NOT_STARTED',
  /** Se quiso reabrir un torneo que no está terminado. */
  'TOURNAMENT_NOT_FINISHED',
  /** Ya está inscrito en esa categoría. */
  'ALREADY_ENTERED',
  /** Se quiso reemplazar a alguien por sí mismo: no es un cambio. */
  'SAME_PLAYER',
  /** Las Finals ya tienen sus ocho cupos tomados o esperando respuesta. */
  'FINALS_FULL',
  /** No queda nadie en el ranking del club a quien invitar. */
  'NOBODY_TO_INVITE',
  /** Ese cuadro no es de Finals: las invitaciones son solo de ese formato. */
  'NOT_FINALS',
  /** Esa invitación ya está contestada. */
  'ALREADY_ANSWERED',
  /** Esa invitación ya se venció. */
  'INVITATION_EXPIRED',
  /** Nadie puede tomar más de dos categorías en el mismo torneo. */
  'TOO_MANY_CATEGORIES',
  /** Se quiso inscribir a un invitado sin nombre, que es lo único que lo identifica. */
  'GUEST_NAME_REQUIRED',
  /** El nombre del invitado no cabe en la columna. */
  'GUEST_NAME_TOO_LONG',
  /** El WhatsApp que llegó no es un número. */
  'PHONE_INVALID',
  /** Se quiso vincular una inscripción que ya tiene cuenta: eso no es vincular, es sustituir. */
  'ENTRY_NOT_GUEST',
  /** La cuenta que se quiso vincular no juega en la categoría de esa inscripción. */
  'SEX_MISMATCH',
  /** El cuadro no se puede armar todavía: falta gente, o sobra. */
  'BRACKET_NOT_POSSIBLE',
  /** Esa categoría no es la suya ni la de justo arriba. */
  'LEVEL_NOT_ELIGIBLE',
  'MATCH_NOT_FOUND',
  /** El marcador no es un resultado posible. El detalle va en `fields`. */
  'SCORE_INVALID',
  /** Todavía no se sabe quiénes juegan ese partido. */
  'MATCH_NOT_READY',
  /** Ya se jugó la ronda siguiente: borrar esto dejaría el cuadro inconsistente. */
  'MATCH_ALREADY_ADVANCED',
  /**
   * SE QUISO BORRAR UN TORNEO QUE YA TIENE PARTIDOS JUGADOS.
   *
   * No lo puede ni la plataforma, y es a propósito: los puntos que ese torneo
   * repartió están en el ranking de gente que no se enteró de la decisión. Lo
   * que sí se puede es borrar un marcador suelto, que se deshace anotando de
   * nuevo. Ver `borrar-marcador.ts` en el servidor.
   */
  'TOURNAMENT_HAS_PLAYED_MATCHES',
  /**
   * El W.O. que llegó no se puede aplicar: quien «no se presentó» no es
   * ninguno de los dos que juegan, o vino junto con un marcador.
   */
  'NO_SHOW_INVALID',
  /**
   * Buscar retos sin tener la ubicación puesta. No es un error del que se
   * escribió: la pantalla lo usa para ofrecer el botón de «usar mi ubicación».
   */
  'LOCATION_REQUIRED',
  /**
   * Falta el club de referencia para lo que se pidió: buscar por ranking, o
   * filtrar por categoría. O no está en ninguno, o está en varios y no eligió
   * cuál es el suyo por defecto.
   */
  'CHALLENGE_NEEDS_CLUB',
  /** Ya hay un reto abierto entre esos dos, de esa clase. */
  /** El motivo del rechazo es más largo de lo que entra. */
  'REVIEW_NOTE_TOO_LONG',
  'CHALLENGE_ALREADY_OPEN',
  /** Se intentó retar a uno mismo. */
  'CHALLENGE_SELF',
  /**
   * Un reto por ranking entre dos que no comparten ranking: distinta
   * categoría, o distinto sexo. Un ranking es NIVEL Y SEXO dentro de un club.
   */
  'CHALLENGE_NEEDS_SAME_RANKING',
  /**
   * Lo frenó un parámetro que el club configuró: los retos apagados, la cuota
   * de la tanda, repetir rival demasiado pronto, o el límite de posiciones.
   *
   * UN SOLO CÓDIGO PARA LOS CINCO FRENOS, y el detalle va en el mensaje. Cinco
   * códigos obligarían a la web a tener cinco textos que dicen lo mismo que ya
   * viene escrito, y a que cada freno nuevo necesite tocar los dos lados.
   */
  'CHALLENGE_NOT_ALLOWED',
  /** Reportar a alguien que no está en ese club, o a uno mismo. */
  /** La pareja no puede jugar ese cuadro. Ver `checkPair`. */
  'PAIR_NOT_ALLOWED',
  'REPORT_NOT_ALLOWED',
  /** Ya tiene demasiados reportes sin atender en ese club. */
  'TOO_MANY_REPORTS',
  /**
   * EL PERFIL ESTÁ EN SILENCIO: no puede publicar, comentar ni reaccionar.
   *
   * Lo pone la administración de la plataforma y tiene fecha de vencimiento.
   * NO impide entrar, ver, jugar torneos ni retar: el problema era lo que
   * escribe, no que juegue.
   *
   * El motivo viaja en `fieldErrors.body` para que la pantalla lo muestre tal
   * cual. Un silencio sin explicación es lo que hace que la persona vuelva a
   * intentarlo igual, y después escriba enojada.
   */
  'MUTED',
  /** Se quiso contestar o cancelar un reto que ya no estaba pendiente. */
  'CHALLENGE_NOT_OPEN',
  /** Se quiso anotar el resultado de un reto que nadie aceptó. */
  'CHALLENGE_NOT_ACCEPTED',
  /** Ese reto ya tiene resultado: alguien lo anotó primero. */
  'CHALLENGE_ALREADY_PLAYED',
  /** Ese amistoso ya lo confirmó el rival: dos toques al mismo botón. */
  'CHALLENGE_ALREADY_CONFIRMED',
  /** Intentaron anotar un partido contra uno mismo. */
  'CHALLENGE_SELF',
  /** El ganador no es uno de los dos que jugaron, o contradice al marcador. */
  'CHALLENGE_WINNER_INVALID',
  /** Se pidió cambiar a la categoría que ya se tiene. */
  'SAME_CATEGORY',
  /** No hay ningún pedido de cambio esperando respuesta. */
  'NO_CATEGORY_REQUEST',
  /** Ese emoji no está en `WALL_REACTIONS`. */
  'REACTION_NOT_ALLOWED',
  /** Ese comentario no existe, o ya lo borraron. */
  'COMMENT_NOT_FOUND',
  /** Ese partido todavía no se jugó, o no es de un club de quien mira. */
  'MATCH_NOT_VISIBLE',
  /** Ese partido existe pero todavía no tiene resultado: no hay qué contar. */
  'MATCH_NOT_PLAYED',
  /**
   * El nivel declarado en el perfil quedaría por debajo del que esa persona
   * tiene en alguno de sus clubes. Ver `requireNotBelowAnyClub`.
   */
  'LEVEL_BELOW_CLUB',
] as const;
export type CommunityErrorCode = (typeof COMMUNITY_ERROR_CODES)[number];

export type ApiErrorCode = AuthErrorCode | CommunityErrorCode;

// =====================================================================
// La campanita
// =====================================================================

/**
 * QUÉ PASÓ.
 *
 * El texto NO viaja armado: viaja el tipo y los datos, y la pantalla lo
 * escribe en el idioma de quien mira. Mandar la frase hecha congelaría el
 * idioma del día en que se creó el aviso, y esta aplicación se lee en nueve.
 */
export const NOTIFICATION_KINDS = [
  /** La inscripción se cayó sola por falta de comprobante. */
  'ENTRY_EXPIRED',
  /** Le aprobaron el ingreso a un club. */
  'JOIN_APPROVED',
  /**
   * Alguien se fue del club por su cuenta. Le llega a QUIEN ADMINISTRA.
   *
   * Irse no pide permiso —es una decisión de la persona— pero el club se
   * tiene que enterar: le cambia la lista, el ranking, y si esa persona
   * estaba inscrita en un torneo, el cuadro. Sin este aviso, irse es una
   * desaparición y el hueco se descubre el sábado en la cancha.
   */
  'MEMBER_LEFT',
  /** Se tiró el torneo en el que está inscrito: ya hay cuadro. */
  'TOURNAMENT_STARTED',
  /** Alguien del club pidió cambiar de categoría. Le llega a quien administra. */
  'CATEGORY_CHANGE_REQUESTED',
  /** Le aprobaron el cambio de categoría. */
  'CATEGORY_CHANGE_APPROVED',
  /** Le dijeron que no al cambio de categoría. */
  'CATEGORY_CHANGE_REJECTED',
  /**
   * Alguien del club pidió entrar a OTRA tabla, además de la suya.
   *
   * SON TRES AVISOS APARTE DE LOS DE CATEGORÍA aunque se parezcan, porque
   * dicen cosas distintas: cambiar de categoría deja a la persona en UNA
   * tabla, y esto la deja en DOS. Reusar los de arriba le diría a quien
   * administra «pidió pasar a B» cuando lo que pidió es «además de mi C,
   * quiero el B», que se responde distinto.
   *
   * `data.sex` es lo que los de categoría no llevan y estos no pueden no
   * llevar: sin él, «pidió el B» no dice cuál de las dos tablas B.
   */
  'RANKING_REQUESTED',
  /** Le aprobaron competir también en esa tabla. */
  'RANKING_APPROVED',
  /** Le dijeron que no a esa tabla. Sigue en las que ya tenía. */
  'RANKING_REJECTED',
  /** Es su cumpleaños y su club —o sus clubes— lo saludan. */
  'BIRTHDAY',
  /** El club le dio por bueno el pago de una inscripción. */
  'ENTRY_APPROVED',
  /**
   * El club le rechazó el pago. `data.note` dice por qué, cuando lo escribió.
   *
   * ES EL AVISO QUE LE DA SENTIDO A RECHAZAR. Un rechazo que nadie ve es peor
   * que no rechazar: la persona sigue creyendo que está adentro y se entera
   * el sábado, en la cancha.
   */
  'ENTRY_REJECTED',
  /**
   * LE BAJARON UN MEME. `data.reason` dice por qué, cuando quien reportó lo
   * escribió.
   *
   * ES EL AVISO QUE LE FALTABA A TODA LA MODERACIÓN. Reportar, bajar y callar
   * pasaban sin que la persona se enterara de nada: su meme simplemente ya no
   * estaba, y si además quedó en silencio lo descubría días después al
   * intentar publicar otro. Eso no es moderar, es hacer desaparecer cosas.
   *
   * NO SE AVISA CUANDO SE BAJA EL PROPIO: apretar «borrar el mío» y recibir
   * un aviso de que te lo borraron es la aplicación contándote lo que acabás
   * de hacer.
   *
   * NO DICE QUIÉN REPORTÓ, y es a propósito: quien reporta está denunciando a
   * alguien de su club, y ponerle el nombre encima convierte un botón de
   * moderación en un motivo de pleito en la cancha.
   */
  'MEME_REMOVED',
  /**
   * Quedó en silencio. `data.days` cuántos y `data.reason` por qué.
   *
   * EL MOTIVO ES LA MITAD DEL ASUNTO. Un silencio sin explicación es lo que
   * hace que la persona vuelva a intentarlo igual —no sabe qué hizo mal— y
   * después escriba enojada al grupo del club.
   */
  'MUTED',
  /** Alguien lo retó a jugar. */
  'CHALLENGE_RECEIVED',
  /**
   * Le aceptaron un reto que mandó, o se lo rechazaron.
   *
   * DOS LLAVES Y NO UNA CON «¿qué contestó?» ADENTRO. La respuesta es lo que
   * cambia la frase entera, y una frase entera es lo que se traduce. Con una
   * sola llave, la palabra «aceptó» viajaría como dato y aparecería en
   * castellano en medio de la frase en japonés.
   */
  'CHALLENGE_ACCEPTED',
  'CHALLENGE_DECLINED',
  /**
   * EL OTRO CANCELÓ UN RETO QUE YA HABÍAN ACEPTADO LOS DOS.
   *
   * Javier: «si ambos aceptan el reto pero no se concreta tener la opción de
   * cancelarlo». Los dos habían quedado en jugar, así que quien no canceló
   * tiene que enterarse: sin aviso, puede ir a la cancha al pedo.
   *
   * SOLO CUANDO YA ESTABA ACEPTADO. Cancelar uno que el otro todavía no
   * contestó es retirar algo que nadie miró, y ahí no se avisa nada.
   */
  'CHALLENGE_CANCELLED',
  /**
   * UN RETO SUYO SE VENCIÓ SOLO.
   *
   * `UNANSWERED`: nadie lo contestó dentro del plazo del club.
   * `UNPLAYED`: se aceptó y no se jugó dentro del plazo.
   *
   * LE LLEGA A LOS DOS, no solo a quien retó. El que no contestó tiene que
   * enterarse de que se le pasó —es su turno de la tanda el que se gastó, y
   * más adelante puede ser su penalidad— y el que retó tiene que saber que ya
   * puede volver a retar a esa persona.
   *
   * Dos llaves y no una con «por qué» adentro, por la misma razón que
   * `CHALLENGE_ACCEPTED` y `CHALLENGE_DECLINED`: son dos frases distintas, y
   * la frase entera es lo que se traduce a los nueve idiomas.
   */
  'CHALLENGE_EXPIRED_UNANSWERED',
  'CHALLENGE_EXPIRED_UNPLAYED',
  /**
   * Ya se sabe cuándo y dónde juega un partido suyo de torneo.
   *
   * VA POR CORREO Y NO SOLO A LA CAMPANITA, a diferencia de casi todo lo
   * demás: es lo único que obliga a estar en un lugar a una hora. Un aviso
   * que se queda esperando a que la persona vuelva a entrar llega tarde por
   * definición.
   */
  'MATCH_SCHEDULED',
  /**
   * Alguien anotó un amistoso contra él y falta que lo confirme.
   *
   * ES EL AVISO QUE SOSTIENE LA REGLA ENTERA. El marcador no suma puntos
   * hasta que el rival lo dé por bueno; sin este aviso, «que el otro
   * confirme» sería un partido esperando para siempre porque nadie se
   * enteró de que existe.
   */
  'FRIENDLY_TO_CONFIRM',
  /** Confirmaron el amistoso que anotó: ya cuenta para el ranking. */
  'FRIENDLY_CONFIRMED',
  /**
   * Lo invitaron a jugar las Finals de su club.
   *
   * `data.days` lleva cuántos días tiene para contestar, y es el dato que
   * hace que conteste: «te invitaron» se lee y se deja para después, «tenés
   * siete días» tiene fecha de vencimiento.
   */
  'FINALS_INVITED',
  /**
   * Se le venció la invitación a las Finals sin contestar.
   *
   * EXISTE PARA QUE NO SE ENTERE POR EL SILENCIO: si no, vería el torneo
   * armado sin él y sin ninguna explicación.
   */
  'FINALS_INVITE_EXPIRED',
  /**
   * Alguien lo puso de pareja en un reto de dobles.
   *
   * NO ES UNA INVITACIÓN: en dobles contesta solo quien recibe el reto, así
   * que su pareja no tiene nada que aceptar. Existe para que nadie se entere
   * de que juega un partido PORQUE LE CAMBIARON LOS PUNTOS, que es lo que
   * pasaría sin esto.
   */
  'DOUBLES_PARTNERED',
  /**
   * Lo invitaron a administrar un club o un torneo, y tiene que aceptarlo.
   *
   * VA ADEMÁS DEL CORREO, no en su lugar. El correo es el que lleva el
   * enlace —es lo que hace que aceptar sea un acto de esa persona y no una
   * decisión de otro—, pero un correo se pierde entre cincuenta, y quien está
   * esperando el permiso entra a la aplicación, no a la bandeja. La campanita
   * lleva a la misma pantalla de aceptar.
   *
   * `data.scope` es 'CLUB' o 'TOURNAMENT'; `data.name`, cómo se llama.
   */
  'ADMIN_INVITED',
  /** Aceptaron la invitación a administrar. Le llega a QUIEN INVITÓ. */
  'ADMIN_INVITE_ACCEPTED',
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export interface Notification {
  id: string;
  kind: NotificationKind;
  /**
   * Los datos para armar el texto: `tournamentName`, `communityName`, `level`…
   *
   * Todo cadenas, incluso lo que es número: lo único que se hace con esto es
   * meterlo en una frase, y un `60` que a veces llega como número y a veces
   * como texto es una diferencia que no le sirve a nadie y rompe a alguien.
   */
  data: Record<string, string>;
  /** A dónde lleva al tocarla. Ruta interna, sin dominio. `null` si no lleva a ningún lado. */
  linkPath: string | null;
  /** ISO 8601, o `null` si no se leyó. */
  readAt: string | null;
  createdAt: string;
}

export interface NotificationsPage {
  items: Notification[];
  /** Cuántas sin leer. Es el número de la campanita. */
  unread: number;
}

/**
 * CUÁNTAS SE GUARDAN Y SE MUESTRAN.
 *
 * Treinta: lo que cabe en una lista que se recorre de un vistazo. Una
 * campanita con trescientos avisos no es un historial, es un lugar donde no
 * se busca nada.
 */
export const NOTIFICATIONS_PAGE_SIZE = 30;

// =====================================================================
// La tabla de posiciones
// =====================================================================

/**
 * Una línea de la tabla.
 *
 * Se guardan sets Y games ganados y perdidos, no solo la diferencia: la
 * diferencia se calcula al ordenar, pero en la tabla se muestran los dos
 * números —«6-2»— porque «+4» no dice si jugó dos partidos o diez.
 */
export interface StandingRow {
  entryId: string;
  displayName: string;
  firstName: string;
  avatarPath: string | null;
  /**
   * LA SIEMBRA, que no es lo mismo que la posición.
   *
   * `position` es en qué lugar VA HOY; `seed` es con qué número ENTRÓ. Las
   * dos juntas son lo que hace legible una tabla: que el sembrado 4 esté
   * primero es la noticia, y sin el número de siembra al lado esa noticia no
   * se ve. Por eso se muestra en el círculo de cada fila.
   */
  seed: number;
  played: number;
  won: number;
  lost: number;
  setsWon: number;
  setsLost: number;
  gamesWon: number;
  gamesLost: number;
  /** La posición, empezando en 1. Sin empates: el desempate siempre decide. */
  position: number;
}

export interface GroupStanding {
  /** 0 es el grupo A, 1 el B. Con una sola tabla, siempre 0. */
  group: number;
  rows: StandingRow[];
}

/**
 * LA TABLA DE POSICIONES, a partir de los partidos jugados.
 *
 * VIVE EN EL CONTRATO, como `checkScore`, y por la misma razón: la usan los
 * dos lados. El servidor, para saber quién pasa a la semifinal; la pantalla,
 * para dibujar la tabla. Dos implementaciones darían dos tablas que un día
 * dicen cosas distintas de la misma persona, y la que se ve no sería la que
 * decide.
 *
 * EL DESEMPATE, en este orden y decidido con Javier:
 *
 *  1. PARTIDOS GANADOS. Es a lo que se juega.
 *  2. EL PARTIDO ENTRE ELLOS. Si Ana le ganó a Beto, Ana va arriba — sin
 *     importar lo que hayan hecho contra los demás. Es lo estándar en tenis
 *     y es lo que la gente espera: «yo te gané».
 *  3. DIFERENCIA DE SETS, y después de GAMES. Solo cuando lo de arriba no
 *     alcanza: tres empatados en un triángulo (A le gana a B, B a C, C a A)
 *     no se resuelven mirándose entre ellos.
 *  4. EL APODO, alfabético. No es deportivo, pero UN ORDEN ESTABLE IMPORTA:
 *     sin este último criterio, dos personas idénticas cambiarían de lugar
 *     entre una carga y otra de la pantalla.
 *
 * El paso 2 se aplica SOLO ENTRE DOS. Con tres o más empatados, el resultado
 * entre ellos no define nada y se pasa derecho a los sets.
 */
/**
 * DESDE DÓNDE NUMERA SUS PARTIDOS CADA TABLA.
 *
 * La identidad de un partido es `(round, position)` —así está el índice único
 * en la base—, así que los de tabla no pueden numerarse desde el uno: la
 * primera semifinal YA ES «ronda 1, posición 1». Cada grupo arranca en su
 * propio millar: el A en 1001, el B en 2001. Mil es mucho más que los 64
 * partidos por jornada del cuadro más grande que existe (128 personas).
 *
 * VIVE EN EL CONTRATO Y NO EN EL SERVIDOR porque las dos puntas lo necesitan:
 * el servidor para numerar al crear, y la pantalla para saber que un partido
 * con posición 1002 es de una tabla y no de un cuadro. Cuando el número lo
 * sabía uno solo, la pantalla no tenía forma de distinguirlos.
 */
export const GROUP_POSITION_BASE = 1000;

/**
 * EN QUÉ GRUPO SE JUEGA ESTE PARTIDO, o `null` si es de definición.
 *
 * Contesta con `group` cuando viene, y SI NO, LO DEDUCE DE LA POSICIÓN. Esa
 * segunda mitad no es un adorno: `group` se agregó después de que ya había
 * torneos armados, así que las filas viejas lo tienen en `null` aunque sean
 * partidos de tabla. Con solo mirar `group`, esos torneos se rompen de una
 * manera muy fea y muy difícil de leer:
 *
 *  · la tabla de posiciones queda VACÍA —ningún partido parece de grupo—, y
 *  · las quince jornadas del todos-contra-todos se cuelan en la definición,
 *    donde se dibujan como si fueran un cuadro: cinco rondas, «Ronda de 32»,
 *    y treinta lugares en blanco que la pantalla escribe como «Bye».
 *
 * La posición no miente: por encima del millar solo hay partidos de tabla, y
 * nunca los hubo de otra cosa. `1002` es del grupo A, `2003` del B.
 */
export function groupOfMatch(match: { group: number | null; position: number }): number | null {
  if (match.group !== null) return match.group;
  if (match.position < GROUP_POSITION_BASE) return null;
  return Math.floor(match.position / GROUP_POSITION_BASE) - 1;
}

export function buildStandings(
  entries: readonly TournamentEntry[],
  matches: readonly Match[],
  format: TournamentFormat = 'ROUND_ROBIN',
): GroupStanding[] {
  const deGrupo = matches.filter((m) => groupOfMatch(m) !== null);
  const grupos = [...new Set(deGrupo.map((m) => groupOfMatch(m) as number))].sort((a, b) => a - b);
  if (grupos.length === 0) return [];

  return grupos.map((group) => {
    const propios = deGrupo.filter((m) => groupOfMatch(m) === group);
    // Quiénes están en este grupo sale de los PARTIDOS y no de una lista
    // aparte: es la única fuente que no se puede desincronizar.
    const ids = new Set<string>();
    for (const match of propios) {
      if (match.home !== null) ids.add(match.home.id);
      if (match.away !== null) ids.add(match.away.id);
    }

    const filas = [...ids].map((entryId) => {
      const suyo = entries.find((e) => e.id === entryId);
      const fila = {
        entryId,
        displayName: suyo?.displayName ?? '—',
        firstName: suyo?.firstName ?? '',
        avatarPath: suyo?.avatarPath ?? null,
        // Sin inscripción no hay siembra que mostrar. `0` y no `1`: uno sería
        // decir «es el primer sembrado», que es una afirmación falsa.
        seed: suyo?.seed ?? 0,
        played: 0,
        won: 0,
        lost: 0,
        setsWon: 0,
        setsLost: 0,
        gamesWon: 0,
        gamesLost: 0,
        position: 0,
      };

      for (const match of propios) {
        if (match.winnerEntryId === null) continue; // sin jugar
        const esLocal = match.home?.id === entryId;
        const esVisita = match.away?.id === entryId;
        if (!esLocal && !esVisita) continue;

        fila.played += 1;
        if (match.winnerEntryId === entryId) fila.won += 1;
        else fila.lost += 1;

        for (const set of match.score) {
          const propios_ = esLocal ? set.home : set.away;
          const ajenos = esLocal ? set.away : set.home;
          fila.gamesWon += propios_;
          fila.gamesLost += ajenos;
          if (propios_ > ajenos) fila.setsWon += 1;
          else if (ajenos > propios_) fila.setsLost += 1;
        }
      }

      return fila;
    });

    const ordenadas =
      format === 'FINALS'
        ? ordenarAtp(filas, propios)
        : [...filas].sort((a, b) => compareStandings(a, b, propios));
    return {
      group,
      rows: ordenadas.map((fila, i) => ({ ...fila, position: i + 1 })),
    };
  });
}

/** El orden de la tabla. Ver `buildStandings` para el porqué de cada paso. */
function compareStandings(
  a: Omit<StandingRow, 'position'>,
  b: Omit<StandingRow, 'position'>,
  matches: readonly Match[],
): number {
  if (a.won !== b.won) return b.won - a.won;

  const entreEllos = headToHead(a.entryId, b.entryId, matches);
  if (entreEllos !== 0) return entreEllos;

  const setsA = a.setsWon - a.setsLost;
  const setsB = b.setsWon - b.setsLost;
  if (setsA !== setsB) return setsB - setsA;

  const gamesA = a.gamesWon - a.gamesLost;
  const gamesB = b.gamesWon - b.gamesLost;
  if (gamesA !== gamesB) return gamesB - gamesA;

  // El último recurso: un orden estable. Ver el punto 4 de `buildStandings`.
  return a.displayName.localeCompare(b.displayName, 'es');
}

/**
 * EL ORDEN DE LA TABLA SEGÚN EL REGLAMENTO DE LA ATP. Solo el formato
 * `FINALS`; el resto de la app usa `compareStandings`, que decidimos con
 * Javier y no se toca.
 *
 * POR QUÉ NO ES UN `sort` CON UN COMPARADOR, que es lo que parece pedir.
 *
 * El reglamento dice «el mano a mano decide SI SOLO DOS siguen empatados». Un
 * comparador ve dos filas y no puede saber si hay una tercera empatada con
 * ellas, así que aplicaría el mano a mano SIEMPRE —y en un triángulo (A le
 * gana a B, B a C, C a A) eso da un orden que depende de en qué orden el
 * `sort` fue comparando, no de los partidos. Con tres empatados a una
 * victoria, Ana con el 40% de los sets terminaba arriba de Cindy con el 60%
 * solo porque le había ganado el partido entre ellas.
 *
 * Entonces se ordena POR TANDAS: primero se separa por lo que sí es
 * individual —victorias y partidos jugados—, y adentro de cada empate se
 * mira cuántos quedaron. Dos: el partido entre ellos. Tres o más: derecho a
 * los porcentajes, como manda el reglamento.
 *
 * El orden completo, del reglamento 2026 (capítulo 4, Nitto ATP Finals):
 *
 *  1. Partidos ganados.
 *  2. Partidos jugados: un 2-1 le gana a un 2-0. Parece al revés hasta que se
 *     ve de dónde sale un 2-0 —alguien se retiró—: ganar dos de tres es más
 *     que ganar dos de dos. Sin esto, retirarse a tiempo mejora la tabla.
 *  3. El mano a mano, SOLO si quedan exactamente dos empatados.
 *  4. Porcentaje de sets, y después de games. Porcentaje y no diferencia,
 *     porque es lo que dice el reglamento —aunque en la práctica casi siempre
 *     coincidan—. Las dos reglas solo se separan cuando los TOTALES difieren
 *     mucho: 12-0 gana por porcentaje (100% contra 61%) y pierde por
 *     diferencia (+12 contra +14) frente a un 40-26. Dentro de un mismo
 *     escalón de la tabla eso es raro —en una rueda todos juegan contra
 *     todos, así que los totales quedan parecidos— y en sets al mejor de tres
 *     es imposible: el total por partido solo puede ser 2 o 3. Se escribe
 *     como manda el reglamento igual, que es más barato que descubrir dentro
 *     de dos años cuál de los dos era.
 *  5. El ranking. La ATP usa el suyo; acá la siembra, que es el ranking del
 *     club congelado al armar el cuadro.
 *  6. El apodo: un orden estable para que dos filas idénticas no se turnen el
 *     puesto entre dos cargas de la pantalla.
 */
function ordenarAtp(
  filas: readonly Omit<StandingRow, 'position'>[],
  matches: readonly Match[],
): Omit<StandingRow, 'position'>[] {
  // Las tandas: primero lo que no depende de con quién se compare.
  const porTanda = [...filas].sort((a, b) => {
    if (a.won !== b.won) return b.won - a.won;
    return b.played - a.played;
  });

  const resultado: Omit<StandingRow, 'position'>[] = [];
  let i = 0;
  while (i < porTanda.length) {
    // Hasta dónde llega este empate.
    let j = i + 1;
    while (
      j < porTanda.length &&
      porTanda[j]!.won === porTanda[i]!.won &&
      porTanda[j]!.played === porTanda[i]!.played
    ) {
      j += 1;
    }

    const empatados = porTanda.slice(i, j);
    if (empatados.length === 2) {
      // EXACTAMENTE DOS: manda el partido entre ellos. `headToHead` devuelve
      // 0 si no jugaron o si se repartieron las victorias, y ahí se sigue por
      // los números como si no hubiera mano a mano.
      const [x, y] = [empatados[0]!, empatados[1]!];
      const entreEllos = headToHead(x.entryId, y.entryId, matches);
      resultado.push(...(entreEllos === 0 ? [x, y].sort(porNumeros) : entreEllos < 0 ? [x, y] : [y, x]));
    } else {
      // UNO SOLO, o TRES O MÁS: sin mano a mano. Ver el comentario de arriba.
      resultado.push(...empatados.sort(porNumeros));
    }
    i = j;
  }
  return resultado;
}

/** Los criterios 4, 5 y 6: porcentajes, siembra, apodo. */
function porNumeros(
  a: Omit<StandingRow, 'position'>,
  b: Omit<StandingRow, 'position'>,
): number {
  const sets = porcentaje(b.setsWon, b.setsLost) - porcentaje(a.setsWon, a.setsLost);
  if (sets !== 0) return sets;

  const games = porcentaje(b.gamesWon, b.gamesLost) - porcentaje(a.gamesWon, a.gamesLost);
  if (games !== 0) return games;

  // El sembrado más alto es el 1, así que va al revés que los demás. Un 0
  // —sin inscripción— se manda al fondo en vez de ganar por ser el más chico.
  const siembraA = a.seed === 0 ? Number.MAX_SAFE_INTEGER : a.seed;
  const siembraB = b.seed === 0 ? Number.MAX_SAFE_INTEGER : b.seed;
  if (siembraA !== siembraB) return siembraA - siembraB;

  return a.displayName.localeCompare(b.displayName, 'es');
}

/**
 * Qué parte se ganó, entre 0 y 1. Sin nada jugado devuelve 0.
 *
 * El cero importa: sin él, quien no jugó ningún set daría `0/0` = `NaN`, y
 * una comparación contra `NaN` es siempre falsa —la fila se quedaría quieta
 * donde estaba y el orden dependería de con quién se la comparó primero—.
 */
function porcentaje(ganados: number, perdidos: number): number {
  const total = ganados + perdidos;
  return total === 0 ? 0 : ganados / total;
}

/**
 * Quién le ganó a quién, si jugaron entre ellos.
 *
 * Devuelve 0 cuando no jugaron, cuando el partido no tiene resultado, o
 * cuando se cruzaron más de una vez con victorias repartidas: en esos casos
 * «el partido entre ellos» no dice nada y hay que seguir con los sets.
 */
function headToHead(a: string, b: string, matches: readonly Match[]): number {
  let ganóA = 0;
  let ganóB = 0;
  for (const match of matches) {
    if (match.winnerEntryId === null) continue;
    const ids = [match.home?.id, match.away?.id];
    if (!ids.includes(a) || !ids.includes(b)) continue;
    if (match.winnerEntryId === a) ganóA += 1;
    if (match.winnerEntryId === b) ganóB += 1;
  }
  if (ganóA === ganóB) return 0;
  return ganóA > ganóB ? -1 : 1;
}

